import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Video, Play, HelpCircle, Settings, ArrowLeft, MessageSquare, Trash2, Mic, MicOff, Users, Copy, Check, X, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import jsQR from 'jsqr';

// ── IndexedDB media store ──────────────────────────────────
const mediaDB = (() => {
  const DB_NAME = '4tz-media';
  const DB_VERSION = 1;
  const STORE_NAME = 'blobs';

  const open = () => new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return {
    async get(key) {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      });
    },
    async put(key, value) {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    async delete(key) {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    async clear() {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
  };
})();

const App = () => {
  // Helper function to safely access localStorage
  const safeLocalStorage = {
    getItem: (key, defaultValue = null) => {
      try {
        const item = localStorage.getItem(key);
        return item !== null ? item : defaultValue;
      } catch (error) {
        console.error(`Error reading ${key} from localStorage:`, error);
        return defaultValue;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (error) {
        console.warn(`Could not save ${key} to localStorage:`, error.message);
        return false;
      }
    }
  };

  // Use localStorage to persist state between sessions
  const [currentScreen, setCurrentScreen] = useState(() => {
    const saved = safeLocalStorage.getItem('currentScreen');
    return saved || 'home';
  });
  const [avatar, setAvatar] = useState(() => {
    const saved = safeLocalStorage.getItem('avatar');
    if (!saved) return null;
    if (saved.startsWith('/api/')) return null; // stale placeholder
    if (saved.startsWith('data:')) return saved; // photo upload
    try { return JSON.parse(saved); } catch { return null; } // custom avatar
  });
  const [avatarName, setAvatarName] = useState(() => {
    const saved = safeLocalStorage.getItem('avatarName');
    return saved || '';
  });
  const [completedMissions, setCompletedMissions] = useState(() => {
    try {
      const saved = safeLocalStorage.getItem('completedMissions');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error parsing completedMissions:', error);
      return [];
    }
  });

  // Accessibility settings
  const [simpleMode, setSimpleMode] = useState(() => {
    const saved = safeLocalStorage.getItem('simpleMode');
    return saved === 'true' || false;
  });
  const [highContrast, setHighContrast] = useState(() => {
    const saved = safeLocalStorage.getItem('highContrast');
    return saved === 'true' || false;
  });
  const [textToSpeech, setTextToSpeech] = useState(() => {
    const saved = safeLocalStorage.getItem('textToSpeech');
    return saved === 'true' || false;
  });
  const [largeText, setLargeText] = useState(() => {
    const saved = safeLocalStorage.getItem('largeText');
    return saved === 'true' || false;
  });
  const [showHelp, setShowHelp] = useState(false);
  const [aquariumItems, setAquariumItems] = useState(() => {
    try { const s = safeLocalStorage.getItem('aquariumItems'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });
  const [sensoryProfile, setSensoryProfile] = useState(() => {
    try {
      const saved = safeLocalStorage.getItem('sensoryProfile');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error parsing sensoryProfile:', error);
      return null;
    }
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [builderStep, setBuilderStep] = useState(1);
  const [builderAvatar, setBuilderAvatar] = useState({ type: 'custom', base: 0, hair: 0, accessory: 0 });
  const [reflectionText, setReflectionText] = useState('');
  const [showReflection, setShowReflection] = useState(false);
  const [selectedCornerPost, setSelectedCornerPost] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);

  // Portfolio state
  const [portfolioEntries, setPortfolioEntries] = useState(() => {
    try {
      const saved = safeLocalStorage.getItem('portfolioEntries');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [portfolioUploadImage, setPortfolioUploadImage] = useState(null);
  const [portfolioUploadTag, setPortfolioUploadTag] = useState(null);
  const [portfolioUploadMediaType, setPortfolioUploadMediaType] = useState('image');
  const [portfolioUploadMediaBlob, setPortfolioUploadMediaBlob] = useState(null);
  const [portfolioCritiqueStep, setPortfolioCritiqueStep] = useState(0);
  const [portfolioCritiqueAnswers, setPortfolioCritiqueAnswers] = useState({ describe: '', analyze: '', interpret: '', evaluate: '' });
  const [portfolioRubricRatings, setPortfolioRubricRatings] = useState({ planning: 0, technique: 0, creativity: 0, cornerPost: 0, principle: 0, selfCritique: 0 });
  const [sensesUsed, setSensesUsed] = useState({ see: false, hear: false, touch: false, smell: false, taste: false, gut: false });
  const [workMode, setWorkMode] = useState(null); // 'dependent' | 'independent' | 'interdependent' | null
  const [workModeBuddy, setWorkModeBuddy] = useState(null); // selected buddy name when interdependent
  const [portfolioFilter, setPortfolioFilter] = useState('all');
  const [portfolioSelectedEntry, setPortfolioSelectedEntry] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({});
  const [blueprintText, setBlueprintText] = useState(() => {
    return safeLocalStorage.getItem('blueprintText') || '';
  });
  const [blueprintSketch, setBlueprintSketch] = useState(() => {
    return safeLocalStorage.getItem('blueprintSketch') || null;
  });
  const [blueprints, setBlueprints] = useState(() => {
    try { const s = safeLocalStorage.getItem('blueprints'); return s ? JSON.parse(s) : {}; }
    catch { return {}; }
  });
  const [blueprintRefOpen, setBlueprintRefOpen] = useState(false);
  const [blueprintGuideStep, setBlueprintGuideStep] = useState(0);
  const [avatarCritiqueMode, setAvatarCritiqueMode] = useState(false);
  const [buddies, setBuddies] = useState(() => {
    try { const s = safeLocalStorage.getItem('buddies'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });
  const buddyScanRef = useRef(null);

  // Teacher mode state
  const [teacherMode, setTeacherMode] = useState(() => {
    return safeLocalStorage.getItem('teacherMode') === 'true';
  });
  const [userRole, setUserRole] = useState(() => {
    return safeLocalStorage.getItem('userRole') || null; // 'student' | 'teacher' | null
  });
  const [teacherStudents, setTeacherStudents] = useState(() => {
    try { const s = safeLocalStorage.getItem('teacherStudents'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });
  const [teacherSelectedStudent, setTeacherSelectedStudent] = useState(null);
  const teacherScanRef = useRef(null);
  const [teacherObservations, setTeacherObservations] = useState(() => {
    try { const s = safeLocalStorage.getItem('teacherObservations'); return s ? JSON.parse(s) : {}; }
    catch { return {}; }
  });
  const [observingCornerPost, setObservingCornerPost] = useState(null); // shortId being observed
  const [observationRatings, setObservationRatings] = useState([0, 0, 0, 0, 0, 0]);
  const [observationNote, setObservationNote] = useState('');

  // ── Live QR Scanner ──
  const liveScanVideoRef = useRef(null);
  const liveScanCanvasRef = useRef(null);
  const liveScanStreamRef = useRef(null);
  const liveScanAnimFrameRef = useRef(null);
  const [liveScanActive, setLiveScanActive] = useState(false);
  const [liveScanMode, setLiveScanMode] = useState(null); // 'buddy' | 'teacher'
  const [scanFeedback, setScanFeedback] = useState(null); // { type, message, data }
  const [scanCount, setScanCount] = useState(0);
  const [showPasteInput, setShowPasteInput] = useState(false);
  const [pasteCodeInput, setPasteCodeInput] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [vocabSearch, setVocabSearch] = useState('');
  const [showTZ, setShowTZ] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [lessonCornerPostPick, setLessonCornerPostPick] = useState(null);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [youtubeSearch, setYoutubeSearch] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [libraryGradeFilter, setLibraryGradeFilter] = useState('all');
  const [libraryKernFilter, setLibraryKernFilter] = useState('all');
  const [libraryStrategyFilter, setLibraryStrategyFilter] = useState('all');
  const [librarySearch, setLibrarySearch] = useState('');
  const [showImportInput, setShowImportInput] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [lessonLibrary, setLessonLibrary] = useState(() => {
    try { const s = safeLocalStorage.getItem('lessonLibrary'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });

  // Custom lesson plans (teacher-created, persisted)
  const [customLessons, setCustomLessons] = useState(() => {
    try { const s = safeLocalStorage.getItem('customLessons'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });
  // Lesson builder state
  const [lessonBuilderStep, setLessonBuilderStep] = useState(0);
  const [lessonBuilderData, setLessonBuilderData] = useState(null); // working draft
  const [editingLessonId, setEditingLessonId] = useState(null); // non-null when editing

  // Lesson plan state (persisted for mid-lesson resume)
  const [selectedLesson, setSelectedLesson] = useState(() => {
    try { const s = safeLocalStorage.getItem('selectedLesson'); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });
  const [lessonStep, setLessonStep] = useState(() => {
    const s = safeLocalStorage.getItem('lessonStep');
    return s !== null ? Number(s) : 0;
  });
  const [lessonQuestionAnswers, setLessonQuestionAnswers] = useState(() => {
    try { const s = safeLocalStorage.getItem('lessonQuestionAnswers'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });

  // Define the corner posts and simple missions
  const cornerPosts = [
    {
      id: 'environmental',
      name: 'Environmental',
      color: highContrast ? 'bg-green-700' : 'bg-green-500',
      pastel: 'bg-green-100',
      accent: 'border-l-green-500',
      icon: '🌱',
      description: 'We choose environments that help ourselves and others',
      missions: [
        {
          id: 'env1',
          name: 'Nature Detectives',
          description: 'Find and draw three living things around you',
          visualSteps: [
            "Find a good spot to look for living things",
            "Point to or take pictures of what you find",
            "Draw or describe what you discovered"
          ],
          sensoryNotes: {
            movement: "You can walk around or stay seated",
            noise: "Can be done quietly or with discussion",
            touch: "Optional touching of safe natural items"
          }
        },
        {
          id: 'env2',
          name: 'Recycling Sorters',
          description: 'Sort household items into recycling categories',
          visualSteps: [
            "Gather clean items that can be recycled",
            "Create sorting areas (paper, plastic, etc.)",
            "Place each item in the right group"
          ],
          sensoryNotes: {
            movement: "Can be done at a table",
            noise: "Quiet activity with minimal noise",
            touch: "Involves handling different materials"
          }
        }
      ]
    },
    {
      id: 'social',
      name: 'Social',
      color: highContrast ? 'bg-blue-700' : 'bg-blue-500',
      pastel: 'bg-blue-100',
      accent: 'border-l-blue-500',
      icon: '👥',
      description: 'We invest in others, sharing time in joy and sorrow',
      missions: [
        {
          id: 'soc1',
          name: 'Story Buddies',
          description: 'Create a story about your avatars one sentence at a time',
          visualSteps: [
            "Sit together in a comfortable spot",
            "One person starts the story with one sentence",
            "Take turns adding one sentence at a time",
            "End the story when you both decide it's done"
          ],
          sensoryNotes: {
            movement: "Sitting activity with optional gestures",
            noise: "Speaking activity at conversational level",
            touch: "No touching required"
          }
        },
        {
          id: 'soc2',
          name: 'Emotion Charades',
          description: 'Act out simple feelings for each other to guess',
          visualSteps: [
            "Choose an emotion to act out",
            "Use your face and body to show the feeling",
            "Other person tries to guess the emotion",
            "Switch turns and try a new emotion"
          ],
          sensoryNotes: {
            movement: "Involves facial expressions and body movements",
            noise: "Can be done with or without sounds",
            touch: "No touching required"
          }
        }
      ]
    },
    {
      id: 'physical',
      name: 'Physical',
      color: highContrast ? 'bg-red-700' : 'bg-red-500',
      pastel: 'bg-red-100',
      accent: 'border-l-red-500',
      icon: '🏃‍♂️',
      description: 'We care for our physical self and body',
      missions: [
        {
          id: 'phy1',
          name: 'Dance Party',
          description: 'Create a special avatar dance move together',
          visualSteps: [
            "Find an open space to move safely",
            "Choose some music or create a beat",
            "Take turns showing a simple dance move",
            "Practice the moves together"
          ],
          sensoryNotes: {
            movement: "Active movement - can be adapted to seated",
            noise: "Music can be loud or soft based on preference",
            touch: "No touching required"
          }
        },
        {
          id: 'phy2',
          name: 'Sensory Explorer',
          description: 'Make a simple obstacle course with different textures',
          visualSteps: [
            "Gather items with different textures (soft, bumpy, etc.)",
            "Arrange items in a path or course",
            "Walk through the course, describing what you feel",
            "Rearrange and try again"
          ],
          sensoryNotes: {
            movement: "Walking activity - can be modified to hand exploration",
            noise: "Quiet activity with talking",
            touch: "Involves touching different textures - alternatives available"
          }
        }
      ]
    },
    {
      id: 'intellectual',
      name: 'Intellectual',
      color: highContrast ? 'bg-purple-900' : 'bg-purple-500',
      pastel: 'bg-purple-100',
      accent: 'border-l-purple-500',
      icon: '🧠',
      description: 'We learn, think, and solve problems',
      missions: [
        {
          id: 'int1',
          name: 'Memory Match',
          description: 'Create and play a matching game with household items',
          visualSteps: [
            "Collect pairs of similar small items",
            "Arrange them face down on a surface",
            "Take turns flipping two items to find matches",
            "Continue until all matches are found"
          ],
          sensoryNotes: {
            movement: "Minimal movement required",
            noise: "Quiet activity",
            touch: "Involves handling small objects"
          }
        },
        {
          id: 'int2',
          name: 'Blueprint Builders',
          description: 'Draw a plan, then build it with blocks or other items',
          visualSteps: [
            "Draw a simple design on paper",
            "Gather building materials (blocks, boxes, etc.)",
            "Follow your plan to build your design",
            "Compare your building to your drawing"
          ],
          sensoryNotes: {
            movement: "Sitting or standing activity with hand movements",
            noise: "Quiet with optional discussion",
            touch: "Involves handling building materials"
          }
        }
      ]
    },
    {
      id: 'emotional',
      name: 'Emotional',
      color: highContrast ? 'bg-yellow-600' : 'bg-yellow-500',
      pastel: 'bg-yellow-100',
      accent: 'border-l-yellow-500',
      icon: '❤️',
      description: 'We understand our feelings and express them appropriately',
      missions: [
        {
          id: 'emo1',
          name: 'Feelings Palette',
          description: 'Mix colors to match different emotions',
          visualSteps: [
            "Gather art supplies (paints, crayons, or markers)",
            "Think of different feelings (happy, calm, excited)",
            "Choose colors that match each feeling",
            "Create a feelings chart with your colors"
          ],
          sensoryNotes: {
            movement: "Sitting activity with hand movements",
            noise: "Quiet activity",
            touch: "Involves art materials - alternatives available"
          }
        },
        {
          id: 'emo2',
          name: 'Calm Corner',
          description: 'Design a special calm-down space for the avatars',
          visualSteps: [
            "Find a comfortable spot in the room",
            "Gather items that help feel calm (pillows, stuffed animals)",
            "Arrange the items to create a cozy space",
            "Practice using the space for 2 minutes of quiet"
          ],
          sensoryNotes: {
            movement: "Gentle movement while arranging items",
            noise: "Very quiet activity",
            touch: "Involves soft, comforting items"
          }
        }
      ]
    },
    {
      id: 'spiritual',
      name: 'Spiritual',
      color: highContrast ? 'bg-cyan-700' : 'bg-cyan-500',
      pastel: 'bg-cyan-100',
      accent: 'border-l-cyan-500',
      icon: '✨',
      description: 'We connect with our inside self',
      missions: [
        {
          id: 'spi1',
          name: 'Gratitude Garden',
          description: 'Draw or talk about things you\'re thankful for',
          visualSteps: [
            "Sit in a peaceful spot together",
            "Take turns naming something you're thankful for",
            "Draw or write each item (optional)",
            "Place all ideas together to create a garden of thanks"
          ],
          sensoryNotes: {
            movement: "Minimal movement, sitting activity",
            noise: "Quiet voices",
            touch: "Optional drawing materials"
          }
        },
        {
          id: 'spi2',
          name: 'Peaceful Moment',
          description: 'Find a quiet spot and share what gives you peace',
          visualSteps: [
            "Find a comfortable, quiet place to sit",
            "Close eyes or look at something calming",
            "Take 3 slow breaths together",
            "Share what helps you feel peaceful"
          ],
          sensoryNotes: {
            movement: "Very still, sitting activity",
            noise: "Very quiet with soft voices",
            touch: "No touching required"
          }
        }
      ]
    }
  ];

  // Corner Post x Skill mapping for teacher dashboard
  const CORNER_POST_SKILLS = {
    env: { name: 'Environmental', skill: 'Problem Solving', icon: '\u{1F331}', color: 'green' },
    soc: { name: 'Social', skill: 'Confidence', icon: '\u{1F465}', color: 'blue' },
    phy: { name: 'Physical', skill: 'Self-Regulation', icon: '\u{1F3C3}', color: 'red' },
    int: { name: 'Intellectual', skill: 'Critical Thinking', icon: '\u{1F9E0}', color: 'purple' },
    emo: { name: 'Emotional', skill: 'Resilience', icon: '\u{2764}\u{FE0F}', color: 'yellow' },
    spi: { name: 'Spiritual', skill: 'Self-Awareness', icon: '\u{2728}', color: 'cyan' },
  };

  // ── Lesson Plans (story-driven assessment) ──────────────────
  const lessonPlans = [
    {
      id: 'emmanuels-dream',
      title: "Emmanuel's Dream",
      author: 'Laurie Ann Thompson',
      coverEmoji: '\u{1F6B2}',
      coverColor: 'from-green-400 to-teal-500',
      hook: 'A boy with one strong leg rides across a whole country!',
      audioUrl: 'https://youtu.be/NjWtLi4a8lg',
      lessonPlanPdf: '/emmanuels-dream-lesson-plan.pdf',
      artActivity: 'Dream Collage \u2014 cut and paste images of YOUR dream for the future',
      essentialQuestions: [
        'Can you picture yourself as a grown-up? What are you doing? Who is with you?',
        'What do you think you do best?',
        'How does doing that make you feel?',
      ],
      vocabulary: [
        { term: 'Collage', def: 'Creating a composition using a collection of images/symbols organized into a visual story' },
        { term: 'Wax Resist', def: 'Using wax (crayon) to repel paint, revealing a stronger line underneath' },
        { term: 'Medium/Media', def: 'Any material used to make art \u2014 paint, crayons, paper, magazines' },
        { term: 'Thumbnail Sketch', def: 'A small, quick drawing that records ideas for a final work of art' },
        { term: 'Revise', def: 'Making an idea or work of art more complete by eliminating what does not work' },
        { term: 'Dialogue', def: 'The words said back and forth in a conversation' },
      ],
      materials: ['Sketchbooks', 'Magazines', 'Glue', 'Scissors'],
      techniqueVideos: [
        { title: 'Collage Demo', url: 'https://youtu.be/-Bv13hN_CdQ' },
      ],
      suggestedCornerPost: 'environmental',
    },
    {
      id: 'the-dot',
      title: 'The Dot',
      author: 'Peter H. Reynolds',
      coverEmoji: '\u{1F3A8}',
      coverColor: 'from-purple-400 to-indigo-500',
      hook: "She said \"I can't draw\" \u2014 then one dot changed everything.",
      audioUrl: 'https://youtu.be/1cOsi-sHPnc',
      lessonPlanPdf: '/the-dot-lesson-plan.pdf',
      artActivity: 'Watercolor Dot Investigation \u2014 experiment with paint and see what happens!',
      essentialQuestions: [
        'What does it mean to have an open mind?',
        'What\'s important about making mistakes?',
        'How do you share what you know with others?',
      ],
      vocabulary: [
        { term: 'Wet-on-Wet', def: 'Putting watercolor paint on paper that is already wet' },
        { term: 'Dry Brush', def: 'Using a nearly dry brush so you can see the paper texture' },
        { term: 'Transparent', def: 'See-through \u2014 like watercolor paint on paper' },
      ],
      materials: ['Watercolor paint', 'Watercolor paper', 'Sketchbooks', 'Crayons', 'Sea salt', 'Small cup of water'],
      techniqueVideos: [
        { title: 'Layering', url: 'https://youtu.be/C3FgueyTxLo' },
        { title: 'Dry Brush', url: 'https://youtu.be/x-qCbm5ZnRo' },
        { title: 'Wet-on-Wet', url: 'https://youtu.be/-IbKwP9arZY' },
      ],
      suggestedCornerPost: 'intellectual',
    },
    {
      id: 'emotion-colors',
      title: 'My Feelings in Color',
      author: '4TZ Original',
      coverEmoji: '\u{2764}\u{FE0F}',
      coverColor: 'from-pink-400 to-rose-500',
      hook: 'What color is happy? What does sad look like? Paint your feelings!',
      audioUrl: null,
      artActivity: 'Emotion Color Wheel \u2014 paint colors that match how different feelings look to you',
      essentialQuestions: [
        'What are feelings and why do we have them?',
        'Can you show a feeling using just colors and shapes?',
        'What helps you when you feel big emotions?',
      ],
      vocabulary: [
        { term: 'Express', def: 'To show what you feel through art or words' },
        { term: 'Color Wheel', def: 'A circle showing how colors relate to each other' },
        { term: 'Warm Colors', def: 'Red, orange, yellow \u2014 colors that feel energetic' },
      ],
      suggestedCornerPost: 'emotional',
    },
  ];

  // Lesson context for each corner post (4TZ framework)
  const lessonData = {
    environmental: {
      essentialQuestions: [
        "How do the places around us affect how we feel and act?",
        "What can we do to make our environment better for everyone?",
        "Why is it important to take care of nature and our spaces?"
      ],
      vocabulary: [
        { term: "Environment", definition: "All the things and places around us, including nature and buildings" },
        { term: "Habitat", definition: "A natural home for plants, animals, or people" },
        { term: "Sustainable", definition: "Using things in a way that doesn't run out or hurt the earth" },
        { term: "Recycle", definition: "To turn old things into new useful things instead of throwing them away" }
      ],
      resources: [
        { title: "Emmanuel's Dream — Audio Reading", url: "https://youtu.be/uBhTOSyXi2o", type: "video" },
        { title: "Collage Demo — How to Answer the Art Problem", url: "https://youtu.be/-Bv13hN_CdQ", type: "video" }
      ],
      guidePrompt: "Think about how the spaces around you make you feel. How can you help make them better?"
    },
    social: {
      essentialQuestions: [
        "How do we show care and kindness to the people around us?",
        "Why is it important to share and work together?",
        "What makes a good friend or helper?"
      ],
      vocabulary: [
        { term: "Empathy", definition: "Understanding and caring about how someone else feels" },
        { term: "Community", definition: "A group of people who share something or live near each other" },
        { term: "Cooperation", definition: "Working together with others to get something done" },
        { term: "Dialogue", definition: "The words said back and forth in a conversation" }
      ],
      resources: [
        { title: "Emmanuel's Dream — Audio Reading", url: "https://youtu.be/uBhTOSyXi2o", type: "video" }
      ],
      guidePrompt: "Think about times when someone helped you. How can you help others feel happy and safe?"
    },
    physical: {
      essentialQuestions: [
        "How does moving our bodies help us feel good and stay healthy?",
        "What are different ways we can be active every day?",
        "Why is it important to take care of our bodies?"
      ],
      vocabulary: [
        { term: "Technique", definition: "The method, skill, or system of working with tools and materials" },
        { term: "Medium", definition: "Any material used in the production of art — visual, music, dance, performance" },
        { term: "Coordination", definition: "Using different parts of your body together smoothly" },
        { term: "Balance", definition: "Staying steady, or making both sides of art feel equal" }
      ],
      resources: [
        { title: "Emmanuel's Dream — Audio Reading", url: "https://youtu.be/uBhTOSyXi2o", type: "video" }
      ],
      guidePrompt: "Notice how your body feels when you move. What kinds of movement make you feel good?"
    },
    intellectual: {
      essentialQuestions: [
        "How do we learn new things and solve problems?",
        "What helps our brains grow stronger?",
        "Why is it okay to make mistakes when we're learning?"
      ],
      vocabulary: [
        { term: "Thumbnail Sketch", definition: "A small, quick drawing that records ideas for a final work of art" },
        { term: "Study", definition: "A small, carefully worked-out detail of a larger drawing" },
        { term: "Revise", definition: "Making an idea or work of art more complete by changing what doesn't work" },
        { term: "Characteristic", definition: "A defining element, important feature, or part" }
      ],
      resources: [
        { title: "Collage Demo — How to Answer the Art Problem", url: "https://youtu.be/-Bv13hN_CdQ", type: "video" }
      ],
      guidePrompt: "What are you curious about? How can you explore and learn more about it?"
    },
    emotional: {
      essentialQuestions: [
        "What are feelings and why do we have them?",
        "How can we understand and express our emotions in healthy ways?",
        "What can we do when we feel big emotions like anger or sadness?"
      ],
      vocabulary: [
        { term: "Express", definition: "To show or tell others what you're feeling or thinking through art or words" },
        { term: "Collage", definition: "Creating a composition using a collection of images and symbols organized into a visual story" },
        { term: "Wax Resist", definition: "A technique using wax (crayon) to repel paint, revealing a stronger line underneath" },
        { term: "Format", definition: "The rules or boundaries that make up the assignment" }
      ],
      resources: [
        { title: "Emmanuel's Dream — Audio Reading", url: "https://youtu.be/uBhTOSyXi2o", type: "video" }
      ],
      guidePrompt: "All feelings are okay to have. What helps you when you have big feelings?"
    },
    spiritual: {
      essentialQuestions: [
        "What does it mean to connect with our inner self?",
        "How do we find peace and calm inside ourselves?",
        "What are things we can be grateful for?"
      ],
      vocabulary: [
        { term: "Mindfulness", definition: "Paying attention to right now and how you feel inside" },
        { term: "Reflection", definition: "Thinking quietly about something important to you" },
        { term: "Gratitude", definition: "Feeling thankful for the good things in your life" },
        { term: "Inner Peace", definition: "Feeling calm and good inside yourself" }
      ],
      resources: [
        { title: "Emmanuel's Dream — Audio Reading", url: "https://youtu.be/uBhTOSyXi2o", type: "video" }
      ],
      guidePrompt: "Take a moment to notice how you feel inside. What makes your heart feel peaceful?"
    }
  };

  // Text-to-speech effect for success screen
  useEffect(() => {
    if (currentScreen === 'success' && textToSpeech) {
      // Check if all corner posts have been explored
      const allCompleted = cornerPosts.every(post =>
        post.missions.some(mission => completedMissions.includes(mission.id))
      );

      setTimeout(() => {
        speak(allCompleted
          ? "You've explored all six corner posts."
          : "Mission complete. Take a moment to reflect.");
      }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, textToSpeech, completedMissions, cornerPosts]);

  useEffect(() => {
    if (currentScreen === 'diveBuddies' && textToSpeech) {
      speak("Find your Dive Buddies! Show your QR code or scan a friend's.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, textToSpeech]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      // Don't persist transient screens — fall back appropriately
      // Lesson screens (except lessonPicker) persist as-is for mid-lesson resume
      const transientScreens = ['portfolioUpload', 'portfolioTag', 'portfolioCritique', 'portfolioGrowth', 'blueprint', 'diveBuddies', 'shareProgress', 'teacherDashboard', 'teacherStudentDetail', 'lessonPicker', 'lessonArtUpload', 'lessonBuilder', 'vocabularyList', 'aquarium', 'lessonLibrary'];
      let screenToSave = currentScreen;
      if (transientScreens.includes(currentScreen)) {
        if (currentScreen === 'lessonPicker' || currentScreen === 'lessonBuilder' || currentScreen === 'vocabularyList') screenToSave = 'home';
        else if (currentScreen === 'blueprint' || currentScreen === 'diveBuddies') screenToSave = 'cornerPosts';
        else if (['shareProgress', 'teacherDashboard', 'teacherStudentDetail'].includes(currentScreen)) screenToSave = 'home';
        else if (currentScreen === 'lessonArtUpload') screenToSave = 'lessonCornerPost';
        else screenToSave = 'portfolio';
      }
      safeLocalStorage.setItem('currentScreen', screenToSave);
      if (avatar) safeLocalStorage.setItem('avatar', typeof avatar === 'object' ? JSON.stringify(avatar) : avatar);
      safeLocalStorage.setItem('avatarName', avatarName);
      safeLocalStorage.setItem('completedMissions', JSON.stringify(completedMissions));
      safeLocalStorage.setItem('simpleMode', simpleMode);
      safeLocalStorage.setItem('highContrast', highContrast);
      safeLocalStorage.setItem('textToSpeech', textToSpeech);
      safeLocalStorage.setItem('largeText', largeText);
      if (userRole) safeLocalStorage.setItem('userRole', userRole);
      if (sensoryProfile) safeLocalStorage.setItem('sensoryProfile', JSON.stringify(sensoryProfile));
      safeLocalStorage.setItem('portfolioEntries', JSON.stringify(portfolioEntries));
      safeLocalStorage.setItem('blueprints', JSON.stringify(blueprints));
      safeLocalStorage.setItem('buddies', JSON.stringify(buddies));
      safeLocalStorage.setItem('teacherMode', teacherMode.toString());
      safeLocalStorage.setItem('teacherStudents', JSON.stringify(teacherStudents));
      safeLocalStorage.setItem('teacherObservations', JSON.stringify(teacherObservations));
      safeLocalStorage.setItem('customLessons', JSON.stringify(customLessons));
      safeLocalStorage.setItem('aquariumItems', JSON.stringify(aquariumItems));
      safeLocalStorage.setItem('lessonLibrary', JSON.stringify(lessonLibrary));
      // Persist lesson state for mid-lesson resume
      safeLocalStorage.setItem('selectedLesson', selectedLesson ? JSON.stringify(selectedLesson) : '');
      safeLocalStorage.setItem('lessonStep', lessonStep.toString());
      safeLocalStorage.setItem('lessonQuestionAnswers', JSON.stringify(lessonQuestionAnswers));
      safeLocalStorage.setItem('blueprintText', blueprintText);
      safeLocalStorage.setItem('blueprintSketch', blueprintSketch || '');
    } catch (error) {
      console.error('Error saving app state:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, avatar, avatarName, completedMissions, simpleMode, highContrast, textToSpeech, largeText, userRole, sensoryProfile, portfolioEntries, blueprints, buddies, teacherMode, teacherStudents, teacherObservations, customLessons, aquariumItems, lessonLibrary, selectedLesson, lessonStep, lessonQuestionAnswers, blueprintText, blueprintSketch]);

  // Apply large text class to root element
  useEffect(() => {
    document.documentElement.classList.toggle('large-text', largeText);
  }, [largeText]);

  // TZ auto-shows tip on screen change, then auto-dismisses
  const tzTimerRef = useRef(null);
  const tzRaf = useRef(null);
  const tzState = useRef({
    x: 80, y: 300, targetX: 200, targetY: 250,
    facingRight: true, kickPhase: 0, armPhase: 0,
    bubbles: [], boosting: false, lastBubble: 0, time: 0,
    fish: [],
  });
  const [, tzRerender] = useState(0);
  useEffect(() => {
    // Clear any existing timer
    if (tzTimerRef.current) clearTimeout(tzTimerRef.current);
    // Hide immediately on navigation
    setShowTZ(false);
    // Auto-show after a delay (feels natural, not instant)
    const showDelay = setTimeout(() => {
      tzTipRef.current = getTZTip();
      setShowTZ(true);
      // Auto-dismiss after a few seconds
      tzTimerRef.current = setTimeout(() => {
        setShowTZ(false);
      }, 6000);
    }, 1500);
    return () => {
      clearTimeout(showDelay);
      if (tzTimerRef.current) clearTimeout(tzTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen]);

  // TZ Diver animation loop
  useEffect(() => {
    const DIVER_W = 100, DIVER_H = 90, SPEED = 0.7;
    const rand = (lo, hi) => lo + Math.random() * (hi - lo);
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    const FISH_COLORS = ['#FF6B6B','#FFD93D','#6BCB77','#4D96FF','#FF922B','#C084FC'];

    // Init fish
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (tzState.current.fish.length === 0) {
      tzState.current.fish = Array.from({ length: 5 }, (_, i) => ({
        x: rand(50, w - 50), y: rand(80, h - 80),
        vx: rand(-0.4, 0.4) || 0.2, vy: rand(-0.1, 0.1),
        scale: rand(0.5, 1.2), color: FISH_COLORS[i % FISH_COLORS.length],
        wobble: rand(0, Math.PI * 2),
      }));
    }

    // Pick random target
    const pickTarget = () => {
      const m = 80;
      tzState.current.targetX = rand(m, window.innerWidth - m - DIVER_W);
      tzState.current.targetY = rand(m, window.innerHeight - m - DIVER_H);
    };
    if (tzState.current.targetX === 200) pickTarget();

    let running = true;
    let lastRender = 0;
    const FRAME_INTERVAL = 33; // ~30fps — smooth and performant
    const tick = (now) => {
      if (!running) return;
      // Throttle rendering
      if (now - lastRender < FRAME_INTERVAL) {
        tzRaf.current = requestAnimationFrame(tick);
        return;
      }
      lastRender = now;
      const s = tzState.current;
      s.time = now;
      const dx = s.targetX - s.x;
      const dy = s.targetY - s.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 10) { pickTarget(); s.boosting = false; }
      const sp = s.boosting ? SPEED * 2.5 : SPEED;
      const angle = Math.atan2(dy, dx);
      s.x += Math.cos(angle) * sp;
      s.y += Math.sin(angle) * sp;
      s.facingRight = dx > 0;
      s.kickPhase += s.boosting ? 0.12 : 0.06;
      s.armPhase += 0.03;

      // Bubbles — less frequent
      const bi = s.boosting ? 400 : 800;
      if (now - s.lastBubble > bi) {
        const bx = s.facingRight ? s.x + 60 : s.x + DIVER_W - 60;
        s.bubbles.push({
          id: now, x: bx + rand(-5, 5), y: s.y + rand(-2, 6),
          r: rand(2, 4), vy: -rand(0.2, 0.5),
          life: 1, wobble: rand(0, 6),
        });
        s.lastBubble = now;
      }
      s.bubbles = s.bubbles.map(b => ({ ...b, y: b.y + b.vy, life: b.life - 0.008, wobble: b.wobble + 0.03 })).filter(b => b.life > 0);

      // Fish — slower
      const W = window.innerWidth;
      const H = window.innerHeight;
      s.fish = s.fish.map(f => {
        let { x, y, vx, vy, wobble, ...rest } = f;
        const fdx = x - (s.x + 50);
        const fdy = y - (s.y + 30);
        const fd = Math.sqrt(fdx * fdx + fdy * fdy);
        if (fd < 120) { vx += (fdx / fd) * 0.04; vy += (fdy / fd) * 0.03; }
        x += vx; y += vy + Math.sin(wobble) * 0.2; wobble += 0.03;
        if (x < -30) x = W + 20;
        if (x > W + 30) x = -20;
        if (y < 20) vy += 0.01;
        if (y > H - 20) vy -= 0.01;
        vx = clamp(vx, -0.8, 0.8);
        vy = clamp(vy, -0.3, 0.3);
        return { x, y, vx, vy, wobble, ...rest };
      });

      tzRerender(v => v + 1);
      tzRaf.current = requestAnimationFrame(tick);
    };
    tzRaf.current = requestAnimationFrame(tick);
    return () => { running = false; if (tzRaf.current) cancelAnimationFrame(tzRaf.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Migrate old entries to IndexedDB (runs once)
  useEffect(() => {
    const MIGRATION_KEY = '4tz-media-migrated';
    if (safeLocalStorage.getItem(MIGRATION_KEY) === 'true') return;

    const migrate = async () => {
      try {
        const entries = [...portfolioEntries];
        let changed = false;
        for (const entry of entries) {
          if (!entry.mediaType) {
            entry.mediaType = 'image';
            changed = true;
          }
          if (entry.imageThumb && entry.imageThumb.startsWith('data:')) {
            try {
              const res = await fetch(entry.imageThumb);
              const blob = await res.blob();
              await mediaDB.put(entry.id, blob);
            } catch (err) {
              console.warn('Migration: could not store media for', entry.id, err);
            }
          }
        }
        if (changed) setPortfolioEntries(entries);
        safeLocalStorage.setItem(MIGRATION_KEY, 'true');
      } catch (err) {
        console.error('Migration error:', err);
      }
    };
    migrate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load full media from IndexedDB for selected entry
  useEffect(() => {
    if (!portfolioSelectedEntry) return;
    const entryId = portfolioSelectedEntry.id;
    if (mediaUrls[entryId]) return;

    let cancelled = false;
    mediaDB.get(entryId).then(blob => {
      if (cancelled || !blob) return;
      const url = URL.createObjectURL(blob);
      setMediaUrls(prev => ({ ...prev, [entryId]: url }));
    }).catch(() => {});

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioSelectedEntry]);

  // Count completed corner posts (at least one mission from each)
  const completedCornerPosts = cornerPosts.filter(post =>
    post.missions.some(mission => completedMissions.includes(mission.id))
  ).length;

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        alert('Please upload an image file (JPEG, PNG, GIF, or WEBP).');
        return;
      }

      if (file.size > maxSize) {
        alert('Image is too large. Please upload an image smaller than 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatar(e.target.result);
      };
      reader.onerror = () => {
        alert('Error reading file. Please try again.');
      };
      reader.readAsDataURL(file);
    }
  };

  // Image compression for portfolio uploads
  const compressImage = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 400;
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h = Math.round((h * MAX) / w); w = MAX; } }
        else { if (h > MAX) { w = Math.round((w * MAX) / h); h = MAX; } }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        let result = canvas.toDataURL('image/jpeg', 0.6);
        if (result.length > 100 * 1024) result = canvas.toDataURL('image/jpeg', 0.4);
        resolve(result);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  };

  const encodeBuddyQR = () => {
    const name = avatarName || 'Diver';
    if (avatar && typeof avatar === 'object' && avatar.type === 'custom') {
      return JSON.stringify({ n: name, a: { t: 'c', b: avatar.base, h: avatar.hair, x: avatar.accessory } });
    }
    return JSON.stringify({ n: name, a: { t: 'p' } });
  };

  const decodeBuddyQR = (jsonStr) => {
    try {
      const d = JSON.parse(jsonStr);
      if (!d || !d.n || !d.a || !d.a.t) return null;
      if (d.a.t === 'c') {
        return { name: d.n, avatar: { type: 'custom', base: d.a.b, hair: d.a.h, accessory: d.a.x } };
      }
      if (d.a.t === 'p') {
        return { name: d.n, avatar: { type: 'photo' } };
      }
      return null;
    } catch { return null; }
  };

  const handleBuddyScan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        if (!code) {
          if (textToSpeech) speak('Could not read QR code. Try again!');
          if (buddyScanRef.current) buddyScanRef.current.value = '';
          return;
        }
        const buddy = decodeBuddyQR(code.data);
        if (!buddy) {
          if (textToSpeech) speak('That QR code is not a Dive Buddy code.');
          if (buddyScanRef.current) buddyScanRef.current.value = '';
          return;
        }
        if (buddy.name === (avatarName || 'Diver')) {
          if (textToSpeech) speak("That's your own QR code! Scan a friend's code instead.");
          if (buddyScanRef.current) buddyScanRef.current.value = '';
          return;
        }
        if (buddies.some(b => b.name === buddy.name)) {
          if (textToSpeech) speak(`${buddy.name} is already your Dive Buddy!`);
          if (buddyScanRef.current) buddyScanRef.current.value = '';
          return;
        }
        setBuddies(prev => [...prev, { ...buddy, addedAt: Date.now() }]);
        if (textToSpeech) speak(`${buddy.name} is now your Dive Buddy!`);
        if (buddyScanRef.current) buddyScanRef.current.value = '';
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Progress QR helpers ─────────────────────────────────────
  const CORNER_POST_SHORT = {
    environmental: 'env', social: 'soc', physical: 'phy',
    intellectual: 'int', emotional: 'emo', spiritual: 'spi',
  };
  const encodeProgressQR = () => {
    const name = (avatarName || 'Diver').slice(0, 20);
    // Avatar encoding
    const a = (avatar && typeof avatar === 'object' && avatar.type === 'custom')
      ? { t: 'c', b: avatar.base, h: avatar.hair, x: avatar.accessory }
      : { t: 'p' };
    // Completed missions
    const m = completedMissions || [];
    // Sensory profile encoding (single letters)
    const s = {};
    if (sensoryProfile) {
      if (sensoryProfile.movement) s.mv = sensoryProfile.movement === 'lots' ? 'L' : sensoryProfile.movement === 'some' ? 'M' : 'N';
      if (sensoryProfile.sound) s.sn = sensoryProfile.sound === 'loud' ? 'L' : sensoryProfile.sound === 'medium' ? 'M' : 'Q';
      if (sensoryProfile.touch) s.tc = sensoryProfile.touch === 'squishy' ? 'S' : sensoryProfile.touch === 'everything' ? 'E' : 'A';
    }
    // Aggregate ratings per corner post
    const r = {};
    const pc = {};
    const grouped = {};
    portfolioEntries.forEach(entry => {
      const shortId = CORNER_POST_SHORT[entry.cornerPostId];
      if (!shortId) return;
      if (!grouped[shortId]) grouped[shortId] = [];
      grouped[shortId].push(entry);
    });
    Object.entries(grouped).forEach(([shortId, entries]) => {
      pc[shortId] = entries.length;
      const cats = ['planning', 'technique', 'creativity', 'cornerPost', 'principle'];
      const avgs = cats.map(cat => {
        const vals = entries.map(e => (e.rubricRatings && e.rubricRatings[cat]) || 0).filter(v => v > 0);
        return vals.length > 0 ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : 0;
      });
      r[shortId] = avgs;
    });
    return JSON.stringify({
      v: 1, t: '4tz', n: name, a, m, s, r, pc,
      ts: Math.floor(Date.now() / 1000),
    });
  };

  const decodeProgressQR = (jsonStr) => {
    try {
      const d = JSON.parse(jsonStr);
      if (!d || d.t !== '4tz' || !d.n) return null;
      return d;
    } catch { return null; }
  };

  const handleTeacherScan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        if (!code) {
          if (textToSpeech) speak('Could not read QR code. Try again!');
          if (teacherScanRef.current) teacherScanRef.current.value = '';
          return;
        }
        const data = decodeProgressQR(code.data);
        if (!data) {
          if (textToSpeech) speak('That is not a student progress code.');
          alert('Not a student progress QR code. Make sure the student used "Share My Progress".');
          if (teacherScanRef.current) teacherScanRef.current.value = '';
          return;
        }
        // Upsert by name
        setTeacherStudents(prev => {
          const existing = prev.findIndex(s => s.n === data.n);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = data;
            return updated;
          }
          return [...prev, data];
        });
        if (textToSpeech) speak(`${data.n}'s progress has been added!`);
        if (teacherScanRef.current) teacherScanRef.current.value = '';
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Live QR Scanner ─────────────────────────────────────────
  const startLiveScan = useCallback((mode) => {
    setLiveScanMode(mode);
    setLiveScanActive(true);
    setScanCount(0);
    setScanFeedback(null);

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
        });
        liveScanStreamRef.current = stream;
        if (liveScanVideoRef.current) {
          liveScanVideoRef.current.srcObject = stream;
        }
      } catch {
        // Camera not available — fall back to file input
        stopLiveScan();
        if (mode === 'buddy' && buddyScanRef.current) buddyScanRef.current.click();
        else if (mode === 'teacher' && teacherScanRef.current) teacherScanRef.current.click();
      }
    };
    initCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanLoop = useCallback(() => {
    const video = liveScanVideoRef.current;
    const canvas = liveScanCanvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      liveScanAnimFrameRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = Math.min(video.videoWidth, 640);
    canvas.height = Math.min(video.videoHeight, 480);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, canvas.width, canvas.height);

    if (code) {
      if (liveScanMode === 'buddy') {
        const buddy = decodeBuddyQR(code.data);
        if (buddy) {
          if (buddy.name === (avatarName || 'Diver')) {
            showScanToast('self', "That's your own code, explorer!");
          } else if (buddies.some(b => b.name === buddy.name)) {
            showScanToast('duplicate', `${buddy.name} is already your Dive Buddy!`);
          } else {
            setBuddies(prev => [...prev, { ...buddy, addedAt: Date.now() }]);
            if (textToSpeech) speak(`${buddy.name} is now your Dive Buddy!`);
            showScanToast('success', `${buddy.name} joined your crew!`, buddy);
            setTimeout(() => stopLiveScan(), 2000);
            return;
          }
        }
      } else if (liveScanMode === 'teacher') {
        const data = decodeProgressQR(code.data);
        if (data) {
          const isUpdate = teacherStudents.some(s => s.n === data.n);
          setTeacherStudents(prev => {
            const existing = prev.findIndex(s => s.n === data.n);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = data;
              return updated;
            }
            return [...prev, data];
          });
          setScanCount(prev => prev + 1);
          if (textToSpeech) speak(`${data.n}'s progress ${isUpdate ? 'updated' : 'received'}!`);
          showScanToast('success', `${data.n}'s expedition log ${isUpdate ? 'updated' : 'received'}!`, data);
          // Continue scanning — don't close for teacher mode
          setTimeout(() => {
            setScanFeedback(null);
            liveScanAnimFrameRef.current = requestAnimationFrame(scanLoop);
          }, 1500);
          return;
        }
      }
    }
    liveScanAnimFrameRef.current = requestAnimationFrame(scanLoop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveScanMode, avatarName, buddies, teacherStudents, textToSpeech]);

  const stopLiveScan = useCallback(() => {
    if (liveScanAnimFrameRef.current) {
      cancelAnimationFrame(liveScanAnimFrameRef.current);
      liveScanAnimFrameRef.current = null;
    }
    if (liveScanStreamRef.current) {
      liveScanStreamRef.current.getTracks().forEach(t => t.stop());
      liveScanStreamRef.current = null;
    }
    setLiveScanActive(false);
    setLiveScanMode(null);
    setScanFeedback(null);
  }, []);

  // Start scan loop when video element is ready
  useEffect(() => {
    if (liveScanActive && liveScanVideoRef.current) {
      const video = liveScanVideoRef.current;
      const onPlay = () => {
        liveScanAnimFrameRef.current = requestAnimationFrame(scanLoop);
      };
      video.addEventListener('playing', onPlay);
      return () => {
        video.removeEventListener('playing', onPlay);
        if (liveScanAnimFrameRef.current) {
          cancelAnimationFrame(liveScanAnimFrameRef.current);
        }
      };
    }
  }, [liveScanActive, scanLoop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (liveScanStreamRef.current) {
        liveScanStreamRef.current.getTracks().forEach(t => t.stop());
      }
      if (liveScanAnimFrameRef.current) {
        cancelAnimationFrame(liveScanAnimFrameRef.current);
      }
    };
  }, []);

  const showScanToast = (type, message, data) => {
    setScanFeedback({ type, message, data });
    if (type !== 'success' || liveScanMode !== 'teacher') {
      setTimeout(() => setScanFeedback(null), 3000);
    }
  };

  // ── Text-based code sharing ────────────────────────────────
  const copyProgressCode = async () => {
    const code = '4TZ:' + btoa(encodeProgressQR());
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Fallback: select text in a hidden input
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const shareProgressViaAPI = async () => {
    const code = '4TZ:' + btoa(encodeProgressQR());
    try {
      await navigator.share({
        title: '4TZ Explorer Log',
        text: `Here's my 4TZ expedition log!\n\n${code}`,
      });
    } catch { /* user cancelled or not supported */ }
  };

  const handlePasteCode = (input) => {
    let decoded;
    if (input.startsWith('4TZ:')) {
      try { decoded = atob(input.slice(4)); } catch { return false; }
    } else if (input.startsWith('4TZ-B:')) {
      try {
        const json = atob(input.slice(6));
        const buddy = decodeBuddyQR(json);
        if (!buddy) return false;
        if (buddies.some(b => b.name === buddy.name)) {
          showScanToast('duplicate', `${buddy.name} is already your Dive Buddy!`);
          return false;
        }
        setBuddies(prev => [...prev, { ...buddy, addedAt: Date.now() }]);
        showScanToast('success', `${buddy.name} joined your crew!`, buddy);
        return true;
      } catch { return false; }
    } else {
      return false;
    }
    const data = decodeProgressQR(decoded);
    if (!data) return false;
    const isUpdate = teacherStudents.some(s => s.n === data.n);
    setTeacherStudents(prev => {
      const existing = prev.findIndex(s => s.n === data.n);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = data;
        return updated;
      }
      return [...prev, data];
    });
    showScanToast('success', `${data.n}'s expedition log ${isUpdate ? 'updated' : 'received'}!`, data);
    return true;
  };

  const copyBuddyCode = async () => {
    const code = '4TZ-B:' + btoa(encodeBuddyQR());
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  // ── Lesson Library — export/import/publish ─────────────────
  const exportLessonCode = (lesson) => {
    const exportData = { ...lesson };
    delete exportData.id;
    exportData._exported = Date.now();
    exportData._author = lesson.author || avatarName || 'Unknown';
    return '4TZ-L:' + btoa(unescape(encodeURIComponent(JSON.stringify(exportData))));
  };

  const importLessonCode = (code) => {
    try {
      if (!code.startsWith('4TZ-L:')) return null;
      const json = decodeURIComponent(escape(atob(code.slice(6))));
      const lesson = JSON.parse(json);
      if (!lesson.title) return null;
      return { ...lesson, id: 'imported_' + Date.now(), _imported: Date.now() };
    } catch { return null; }
  };

  const publishToLibrary = (lesson) => {
    const existing = lessonLibrary.find(l => l.title === lesson.title && l.author === lesson.author);
    if (existing) return false;
    setLessonLibrary(prev => [...prev, {
      ...lesson,
      id: 'lib_' + Date.now(),
      _published: Date.now(),
      _publishedBy: avatarName || 'Teacher',
      downloads: 0,
    }]);
    return true;
  };

  const importFromLibrary = (libLesson) => {
    const exists = customLessons.some(l => l.title === libLesson.title && l.author === libLesson.author);
    if (exists) return false;
    setCustomLessons(prev => [...prev, { ...libLesson, id: 'custom_' + Date.now() }]);
    setLessonLibrary(prev => prev.map(l =>
      l.id === libLesson.id ? { ...l, downloads: (l.downloads || 0) + 1 } : l
    ));
    return true;
  };

  // ── Aquarium Reward System ──────────────────────────────────
  const AQUARIUM_REWARDS = {
    // Lesson completion rewards
    lesson: [
      { emoji: '🐠', name: 'Clownfish', size: 'sm' },
      { emoji: '🐡', name: 'Pufferfish', size: 'sm' },
      { emoji: '🦀', name: 'Crab', size: 'sm' },
      { emoji: '🦐', name: 'Shrimp', size: 'sm' },
      { emoji: '🐚', name: 'Shell', size: 'sm' },
    ],
    // Corner post completion rewards (bigger)
    cornerPost: [
      { emoji: '🐙', name: 'Octopus', size: 'md', postId: 'environmental' },
      { emoji: '🐬', name: 'Dolphin', size: 'md', postId: 'social' },
      { emoji: '🦈', name: 'Shark', size: 'md', postId: 'physical' },
      { emoji: '🐋', name: 'Whale', size: 'md', postId: 'intellectual' },
      { emoji: '🦑', name: 'Squid', size: 'md', postId: 'emotional' },
      { emoji: '🐢', name: 'Sea Turtle', size: 'md', postId: 'spiritual' },
    ],
    // Portfolio milestones
    portfolio: [
      { emoji: '⭐', name: 'Starfish', size: 'sm' },
      { emoji: '🪸', name: 'Coral', size: 'sm' },
      { emoji: '🌊', name: 'Wave Crystal', size: 'sm' },
    ],
    // All corner posts complete — crown jewel
    allComplete: { emoji: '👑', name: 'Ocean Crown', size: 'lg' },
  };

  const awardAquariumItem = useCallback((category, extraId) => {
    const id = `${category}-${extraId || Date.now()}`;
    // Don't award duplicates
    if (aquariumItems.some(item => item.id === id)) return null;

    let reward;
    if (category === 'lesson') {
      const pool = AQUARIUM_REWARDS.lesson;
      reward = pool[aquariumItems.filter(i => i.category === 'lesson').length % pool.length];
    } else if (category === 'cornerPost') {
      reward = AQUARIUM_REWARDS.cornerPost.find(r => r.postId === extraId) || AQUARIUM_REWARDS.cornerPost[0];
    } else if (category === 'portfolio') {
      const pool = AQUARIUM_REWARDS.portfolio;
      reward = pool[aquariumItems.filter(i => i.category === 'portfolio').length % pool.length];
    } else if (category === 'allComplete') {
      reward = AQUARIUM_REWARDS.allComplete;
    }

    if (!reward) return null;

    const newItem = {
      id,
      category,
      ...reward,
      earned: Date.now(),
      placed: false,
      x: Math.random() * 70 + 15, // random position 15-85%
      y: Math.random() * 40 + 40, // random position 40-80%
    };
    setAquariumItems(prev => [...prev, newItem]);
    return newItem;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aquariumItems]);

  // Check for new rewards based on current progress
  useEffect(() => {
    if (!avatar || teacherMode) return;

    // Award for completed lessons (check portfolio entries with lessonId)
    const completedLessonIds = [...new Set(portfolioEntries.filter(e => e.lessonId).map(e => e.lessonId))];
    completedLessonIds.forEach(lessonId => {
      awardAquariumItem('lesson', lessonId);
    });

    // Award for corner posts with completed missions
    cornerPosts.forEach(post => {
      const hasCompletedMission = post.missions.some(m => completedMissions.includes(m.id));
      if (hasCompletedMission) {
        awardAquariumItem('cornerPost', post.id);
      }
    });

    // Award for portfolio milestones (every 3 entries)
    const milestones = [3, 6, 9, 12, 15];
    milestones.forEach(m => {
      if (portfolioEntries.length >= m) {
        awardAquariumItem('portfolio', `milestone-${m}`);
      }
    });

    // Award crown if all 6 corner posts completed
    const allPostsDone = cornerPosts.every(post =>
      post.missions.some(m => completedMissions.includes(m.id))
    );
    if (allPostsDone) {
      awardAquariumItem('allComplete', 'crown');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedMissions, portfolioEntries, avatar, teacherMode]);

  // Demo data for client presentation
  const DEMO_STUDENTS = [
    {
      v: 1, t: '4tz', n: 'Captain McSplash',
      a: { t: 'c', b: 0, h: 2, x: 1 },
      m: ['env1', 'soc1', 'phy1', 'int1', 'emo1'],
      s: { mv: 'L', sn: 'M', tc: 'S' },
      r: {
        env: [4, 3.5, 4, 4.5, 3],
        soc: [3, 4, 3.5, 4, 3.5],
        phy: [4.5, 4, 5, 4, 4],
        int: [3, 3.5, 4, 3, 3.5],
      },
      pc: { env: 3, soc: 2, phy: 2, int: 1 },
      ts: Math.floor(Date.now() / 1000) - 3600,
    },
    {
      v: 1, t: '4tz', n: 'Coral Wavecatcher',
      a: { t: 'c', b: 2, h: 1, x: 3 },
      m: ['env1', 'env2', 'soc1', 'soc2', 'emo1', 'emo2', 'spi1'],
      s: { mv: 'M', sn: 'Q', tc: 'E' },
      r: {
        env: [5, 4.5, 5, 4, 4.5],
        soc: [4, 4, 4.5, 5, 4],
        emo: [5, 4.5, 5, 5, 4.5],
        spi: [4, 4.5, 4, 4.5, 5],
      },
      pc: { env: 4, soc: 3, emo: 3, spi: 2 },
      ts: Math.floor(Date.now() / 1000) - 7200,
    },
    {
      v: 1, t: '4tz', n: 'Bubbles Reefrunner',
      a: { t: 'c', b: 1, h: 0, x: 4 },
      m: ['phy1', 'phy2', 'int1'],
      s: { mv: 'L', sn: 'L', tc: 'S' },
      r: {
        phy: [5, 5, 4.5, 5, 4],
        int: [2.5, 3, 3, 2, 2.5],
      },
      pc: { phy: 4, int: 1 },
      ts: Math.floor(Date.now() / 1000) - 1800,
    },
    {
      v: 1, t: '4tz', n: 'Pearl Stargazer',
      a: { t: 'c', b: 3, h: 4, x: 5 },
      m: ['env1', 'soc1', 'int1', 'int2', 'spi1', 'spi2'],
      s: { mv: 'N', sn: 'Q', tc: 'A' },
      r: {
        env: [3, 2.5, 3.5, 3, 3],
        soc: [2, 2.5, 3, 2.5, 2],
        int: [5, 4.5, 5, 5, 4.5],
        spi: [4.5, 4, 5, 4.5, 5],
      },
      pc: { env: 1, soc: 1, int: 5, spi: 3 },
      ts: Math.floor(Date.now() / 1000) - 5400,
    },
    {
      v: 1, t: '4tz', n: 'Finley Sandcastle',
      a: { t: 'c', b: 4, h: 3, x: 2 },
      m: ['env1', 'soc1', 'phy1', 'emo1'],
      s: { mv: 'M', sn: 'M', tc: 'E' },
      r: {
        env: [3, 3, 3, 3, 3],
        soc: [3, 3, 3.5, 3, 3],
        phy: [3.5, 3, 3, 3.5, 3],
        emo: [3, 3.5, 3, 3, 3.5],
      },
      pc: { env: 2, soc: 2, phy: 2, emo: 2 },
      ts: Math.floor(Date.now() / 1000) - 900,
    },
    {
      v: 1, t: '4tz', n: 'Marina Deepdive',
      a: { t: 'c', b: 5, h: 5, x: 0 },
      m: ['env1', 'env2', 'soc1', 'soc2', 'phy1', 'phy2', 'int1', 'int2', 'emo1', 'emo2', 'spi1', 'spi2'],
      s: { mv: 'L', sn: 'M', tc: 'E' },
      r: {
        env: [4.5, 4, 5, 4, 4.5],
        soc: [4, 4.5, 4, 4.5, 4],
        phy: [4, 3.5, 4.5, 4, 4],
        int: [4.5, 5, 4, 4.5, 4.5],
        emo: [4, 4, 4.5, 4, 3.5],
        spi: [4.5, 4, 5, 5, 4.5],
      },
      pc: { env: 3, soc: 3, phy: 2, int: 4, emo: 2, spi: 2 },
      ts: Math.floor(Date.now() / 1000) - 600,
    },
  ];

  const loadDemoStudents = () => {
    setTeacherStudents(DEMO_STUDENTS);
    // Load sample teacher observations for demo students
    setTeacherObservations({
      'Captain McSplash': {
        env: { ratings: [4, 3, 4, 5, 3, 4], note: 'Great enthusiasm, needs to slow down on planning.', ts: Date.now() - 3600000 },
        soc: { ratings: [3, 4, 3, 4, 4, 3], note: '', ts: Date.now() - 7200000 },
      },
      'Coral Wavecatcher': {
        env: { ratings: [5, 5, 5, 4, 5, 5], note: 'Outstanding across the board.', ts: Date.now() - 1800000 },
        emo: { ratings: [5, 4, 5, 5, 5, 4], note: 'Very emotionally aware.', ts: Date.now() - 3600000 },
      },
      'Bubbles Reefrunner': {
        phy: { ratings: [5, 5, 5, 5, 4, 3], note: 'Incredible physical skill, self-reflection growing.', ts: Date.now() - 900000 },
      },
      'Pearl Stargazer': {
        int: { ratings: [5, 5, 5, 5, 5, 5], note: 'Exceptional critical thinker.', ts: Date.now() - 5400000 },
        spi: { ratings: [4, 4, 5, 5, 5, 4], note: '', ts: Date.now() - 7200000 },
      },
      'Marina Deepdive': {
        env: { ratings: [5, 4, 5, 4, 5, 4], note: '', ts: Date.now() - 600000 },
        int: { ratings: [5, 5, 4, 5, 5, 5], note: 'A natural leader in group work.', ts: Date.now() - 1200000 },
      },
    });
    if (textToSpeech) speak('Sample class loaded with 6 students!');
  };

  const processVideoUpload = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 50 * 1024 * 1024) {
        reject(new Error('Video must be under 50MB.'));
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      const objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;

      video.onloadedmetadata = () => {
        if (video.duration > 60) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Video must be 60 seconds or shorter.'));
          return;
        }
        video.currentTime = Math.min(1, video.duration / 2);
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        const MAX = 400;
        let w = video.videoWidth, h = video.videoHeight;
        if (w > h) { if (w > MAX) { h = Math.round((h * MAX) / w); w = MAX; } }
        else { if (h > MAX) { w = Math.round((w * MAX) / h); h = MAX; } }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.6);
        URL.revokeObjectURL(objectUrl);
        resolve({ blob: file, thumbnail });
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read video file.'));
      };
    });
  };

  const handlePortfolioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image file (JPEG, PNG, GIF, or WEBP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large. Please choose an image smaller than 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      compressImage(ev.target.result).then(compressed => {
        setPortfolioUploadImage(compressed);
        setPortfolioUploadMediaType('image');
        setPortfolioUploadMediaBlob(file);
      }).catch(() => {
        alert('Error processing image. Please try again.');
      });
    };
    reader.onerror = () => alert('Error reading file. Please try again.');
    reader.readAsDataURL(file);
  };

  const handlePortfolioVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mov'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a video file (MP4, MOV, or WebM).');
      return;
    }
    try {
      const result = await processVideoUpload(file);
      setPortfolioUploadImage(result.thumbnail);
      setPortfolioUploadMediaType('video');
      setPortfolioUploadMediaBlob(result.blob);
    } catch (err) {
      alert(err.message || 'Error processing video. Please try again.');
    }
  };

  const critiquePrompts = [
    { key: 'describe', emoji: '\u{1F441}\u{FE0F}', question: 'What do you see?', hint: "Describe your art like you're telling a friend who can't see it. What did you make? What materials did you use?", placeholder: 'I made a... using...' },
    { key: 'analyze', emoji: '\u{1F50D}', question: 'How did you put it together?', hint: 'Look closely! Think about the colors, shapes, lines, and textures you used. Does one part stand out more?', placeholder: 'I used the colors... The shapes are...' },
    { key: 'interpret', emoji: '\u{1F4A1}', question: "What's the story behind it?", hint: 'What gave you the idea? Does it mean something special? Did your idea change while you were making it?', placeholder: 'My idea came from... It means...' },
    { key: 'evaluate', emoji: '\u{2B50}', question: 'How do you feel about it?', hint: "What makes your art work well? Is there anything you'd change? What are you most proud of?", placeholder: "I think it works well because... I'm proud of..." },
  ];

  const rubricCategories = [
    { key: 'planning', emoji: '\u{1F4CB}', label: 'Planning & Effort', question: 'How well did I plan and finish?' },
    { key: 'technique', emoji: '\u{1F3A8}', label: 'Materials & Skill', question: 'How well did I use my materials?' },
    { key: 'creativity', emoji: '\u{1F31F}', label: 'Creativity', question: 'How creative was I?' },
    { key: 'cornerPost', emoji: '\u{1F9ED}', label: 'Corner Post Connection', question: 'How well does this connect to my corner post?' },
    { key: 'principle', emoji: '\u{2696}\u{FE0F}', label: 'Art Principles', question: 'How well did I use art ideas like balance, pattern, or contrast?' },
    { key: 'selfCritique', emoji: '\u{1FA9E}', label: 'Self-Critique', question: 'How well did I think about and reflect on my work?' },
  ];

  const rubricLevels = ['Just Starting', 'Getting There', 'Good Job', 'Great Work', 'Nailed It!'];

  const sensesConfig = [
    { key: 'see',   emoji: '\u{1F441}\u{FE0F}', label: 'See' },
    { key: 'hear',  emoji: '\u{1F442}', label: 'Hear' },
    { key: 'touch', emoji: '\u{1F91A}', label: 'Touch' },
    { key: 'smell', emoji: '\u{1F443}', label: 'Smell' },
    { key: 'taste', emoji: '\u{1F445}', label: 'Taste' },
    { key: 'gut',   emoji: '\u{1FAB7}', label: 'Gut Feeling' },
  ];

  const defaultSenses = { see: false, hear: false, touch: false, smell: false, taste: false, gut: false };

  const renderSensesChecklist = (variant = 'dark') => {
    const isDark = variant === 'dark';
    return (
      <div>
        <p className={`${isDark ? 'text-white/50' : 'text-gray-600'} text-xs font-medium uppercase tracking-wider mb-2`}>Senses I Used</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {sensesConfig.map(s => (
            <button
              key={s.key}
              onClick={() => setSensesUsed(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
              className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-sm transition-all duration-200 ${
                sensesUsed[s.key]
                  ? isDark
                    ? 'glass text-white scale-105 ring-1 ring-white/30'
                    : 'bg-purple-100 text-purple-700 scale-105 ring-1 ring-purple-300'
                  : isDark
                    ? 'bg-white/5 text-white/30 hover:bg-white/10'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              <span className="text-lg">{s.emoji}</span>
              <span className="text-[9px] font-medium">{s.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const workModeConfig = [
    { key: 'dependent',       emoji: '\u{1F91D}', label: 'With Help',    description: 'I had help from a teacher or adult' },
    { key: 'independent',     emoji: '\u{1F4AA}', label: 'On My Own',    description: 'I did it by myself' },
    { key: 'interdependent',  emoji: '\u{1F465}', label: 'With a Buddy', description: 'I worked with a Dive Buddy' },
  ];

  const renderWorkModeSelector = (variant = 'dark') => {
    const isDark = variant === 'dark';
    return (
      <div>
        <p className={`${isDark ? 'text-white/50' : 'text-gray-600'} text-xs font-medium uppercase tracking-wider mb-2`}>How Did You Work?</p>
        <div className="flex gap-2 justify-center">
          {workModeConfig.map(wm => (
            <button
              key={wm.key}
              onClick={() => {
                setWorkMode(prev => prev === wm.key ? null : wm.key);
                if (wm.key !== 'interdependent') setWorkModeBuddy(null);
              }}
              className={`flex-1 flex flex-col items-center gap-1 px-2 py-3 rounded-xl text-sm transition-all duration-200 ${
                workMode === wm.key
                  ? isDark
                    ? 'glass text-white scale-105 ring-1 ring-white/30'
                    : 'bg-purple-100 text-purple-700 scale-105 ring-1 ring-purple-300'
                  : isDark
                    ? 'bg-white/5 text-white/30 hover:bg-white/10'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              <span className="text-2xl">{wm.emoji}</span>
              <span className="text-[10px] font-bold">{wm.label}</span>
              <span className={`text-[8px] leading-tight ${workMode === wm.key ? (isDark ? 'text-white/60' : 'text-purple-500') : (isDark ? 'text-white/20' : 'text-gray-300')}`}>{wm.description}</span>
            </button>
          ))}
        </div>
        {workMode === 'interdependent' && buddies.length > 0 && (
          <div className="mt-2">
            <p className={`${isDark ? 'text-white/40' : 'text-gray-500'} text-[10px] font-medium uppercase tracking-wider mb-1`}>Which Buddy?</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {buddies.map(b => (
                <button
                  key={b.name}
                  onClick={() => setWorkModeBuddy(prev => prev === b.name ? null : b.name)}
                  className={`px-2.5 py-1 rounded-full text-xs transition-all duration-200 ${
                    workModeBuddy === b.name
                      ? isDark
                        ? 'glass text-white ring-1 ring-white/30'
                        : 'bg-purple-100 text-purple-700 ring-1 ring-purple-300'
                      : isDark
                        ? 'bg-white/5 text-white/30 hover:bg-white/10'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  {b.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getCritiqueSummary = (entry) => {
    const c = entry.critique || {};
    const isLegacy = 'didWell' in c || 'wasTricky' in c || 'tryNextTime' in c;

    if (isLegacy) {
      const bestQuote = c.didWell || c.tryNextTime || c.wasTricky || '';
      const quoteEmoji = c.didWell ? '\u{1F31F}' : c.tryNextTime ? '\u{1F680}' : '\u{1F914}';
      return {
        hasText: !!(c.didWell || c.wasTricky || c.tryNextTime),
        bestQuote,
        quoteEmoji,
        previewText: bestQuote || 'No reflection yet',
        isLegacy: true,
        cards: [
          { key: 'didWell', emoji: '\u{1F31F}', label: 'What I did well', text: c.didWell || '' },
          { key: 'wasTricky', emoji: '\u{1F914}', label: 'What was tricky', text: c.wasTricky || '' },
          { key: 'tryNextTime', emoji: '\u{1F680}', label: "What I'd try next time", text: c.tryNextTime || '' },
        ],
      };
    }

    const bestQuote = c.describe || c.evaluate || c.interpret || c.analyze || '';
    const quoteEmoji = c.describe ? '\u{1F441}\u{FE0F}' : c.evaluate ? '\u{2B50}' : c.interpret ? '\u{1F4A1}' : '\u{1F50D}';
    return {
      hasText: !!(c.describe || c.analyze || c.interpret || c.evaluate),
      bestQuote,
      quoteEmoji,
      previewText: bestQuote || 'No reflection yet',
      isLegacy: false,
      cards: [
        { key: 'describe', emoji: '\u{1F441}\u{FE0F}', label: 'What I see', text: c.describe || '' },
        { key: 'analyze', emoji: '\u{1F50D}', label: 'How I put it together', text: c.analyze || '' },
        { key: 'interpret', emoji: '\u{1F4A1}', label: 'The story behind it', text: c.interpret || '' },
        { key: 'evaluate', emoji: '\u{2B50}', label: 'How I feel about it', text: c.evaluate || '' },
      ],
    };
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateLong = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const completeMission = (missionId) => {
    if (!completedMissions.includes(missionId)) {
      setCompletedMissions([...completedMissions, missionId]);
    }
    setCurrentScreen('success');
  };

  // Text-to-speech function
  const speak = (text) => {
    if (textToSpeech && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9; // Slightly slower speech
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Speech-to-text (voice input for critique prompts)
  const speechSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback((onResult) => {
    if (!speechSupported) return;
    stopListening();

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) onResult(transcript);
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [speechSupported, stopListening]);

  // Clean up recognition on unmount or screen change
  useEffect(() => {
    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, [currentScreen]);

  // Reset blueprint guide when entering a blueprint screen
  useEffect(() => {
    if (currentScreen === 'blueprint' || currentScreen === 'lessonBlueprint') {
      setBlueprintRefOpen(false);
      setBlueprintGuideStep(0);
    }
  }, [currentScreen]);

  // Circular progress ring SVG component
  const ProgressRing = ({ progress, size = 100, strokeWidth = 8 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;
    return (
      <svg width={size} height={size} className="block">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="white"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="progress-ring-circle"
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          className="text-lg font-bold"
          style={{ fontSize: '1rem' }}
        >
          {completedCornerPosts}/6
        </text>
      </svg>
    );
  };

  // Help content
  const getHelpContent = () => {
    const t = teacherMode; // shorthand
    switch (currentScreen) {
      case 'home':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">Home Base</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap the avatar to customize the student's diver character</li>
              <li><b>Today's Lessons</b> opens the lesson picker with sequential unlocking</li>
              <li><b>Share My Progress</b> generates a QR code for you to scan from the Teacher Dashboard</li>
              <li>Access <b>Settings</b> (gear icon) to reach the Teacher Dashboard</li>
            </ul>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">Your Dive HQ</h3>
            <p className="mb-2">This is your home base, explorer!</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap your avatar to change how your diver looks</li>
              <li><b>Today's Lessons</b> picks your next book adventure</li>
              <li><b>My Collection</b> shows all the art you've captured</li>
              <li><b>Share My Progress</b> lets your teacher see how you're doing</li>
            </ul>
          </div>
        );
      case 'cornerPosts':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">4TZ Corner Posts</h3>
            <p className="mb-2">The 6 corner posts map to developmental growth areas:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Environmental</b> (green) — awareness of surroundings</li>
              <li><b>Social</b> (blue) — connecting with others</li>
              <li><b>Physical</b> (red) — body and movement</li>
              <li><b>Intellectual</b> (purple) — thinking and problem-solving</li>
              <li><b>Emotional</b> (yellow) — understanding feelings</li>
              <li><b>Spiritual</b> (teal) — inner peace and self-awareness</li>
            </ul>
            <p className="mt-2">Each post contains missions with built-in sensory accommodations.</p>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">Corner Posts</h3>
            <p className="mb-2">Each colored card is a different kind of adventure!</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap any card to see missions for that area</li>
              <li>A checkmark means you've completed a mission there</li>
              <li>Try to explore all six!</li>
            </ul>
          </div>
        );
      case 'missions':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Mission Cards' : 'Choose a Mission'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Each card is a different activity to try</li>
              <li>Tap <b>"Lesson Guide"</b> to see vocabulary and key ideas first</li>
              <li>Tap <b>"Start Mission"</b> to see step-by-step instructions</li>
              {t && <li>Completed missions show a checkmark — students can redo any mission</li>}
            </ul>
          </div>
        );
      case 'missionDetail':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Mission Steps' : 'Your Mission'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Follow the steps in order — take your time</li>
              <li>Sensory tips are included for each step</li>
              <li>When done, tap the green <b>"Mission Complete"</b> button</li>
              {t && <li>Steps adapt based on the student's sensory profile</li>}
            </ul>
          </div>
        );
      case 'lessonPicker':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">Lesson Picker</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Lessons unlock sequentially — The Dot is always first</li>
              <li>Completing a lesson unlocks the next one</li>
              <li>As a teacher, you can access all lessons regardless of locks</li>
              <li>The shopping list shows materials needed for the next 3 lessons</li>
              <li>Custom lessons you create appear at the end</li>
            </ul>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">Pick a Lesson</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Start with <b>The Dot</b> — it's your first adventure!</li>
              <li>Finish a lesson to unlock the next one</li>
              <li>Check the <b>shopping list</b> at the top so you have your materials ready</li>
              <li>You can always go back and redo a lesson you loved</li>
            </ul>
          </div>
        );
      case 'lessonRead':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">Lesson Guide</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap <b>"Listen to the Story"</b> to play the read-aloud video</li>
              <li>Review vocabulary and technique videos with the student</li>
              <li>Tap <b>"Lesson Blueprint"</b> to view the full lesson plan PDF with procedures and standards</li>
              <li>When ready, tap <b>"I Finished"</b> to move to essential questions</li>
            </ul>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">Lesson Guide</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap the play button to <b>hear the story</b> read aloud</li>
              <li>Learn the <b>vocabulary words</b> before you start creating</li>
              <li>Watch the <b>technique videos</b> to learn new skills</li>
              <li>When you're ready to create, tap <b>"I Finished!"</b></li>
            </ul>
          </div>
        );
      case 'lessonQuestions':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Essential Questions' : 'Think About It'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t ? 'These questions align with the lesson objectives' : 'Take a moment to think about each question'}</li>
              <li>Type or speak your answer — there's no wrong answer!</li>
              <li>Tap the arrow to move to the next question</li>
              {t && <li>Answers are saved with the portfolio entry for review</li>}
            </ul>
          </div>
        );
      case 'blueprint':
      case 'lessonBlueprint':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Blueprinting Phase' : 'Plan Your Creation'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t ? 'Students plan before creating — this builds critical thinking' : 'Think about what you want to make before you start'}</li>
              <li>Write or speak your ideas in the text box</li>
              <li>Draw a thumbnail sketch on paper and snap a photo</li>
              {t && <li>Blueprinting connects to the scientific process: observe, hypothesize, predict, test</li>}
            </ul>
          </div>
        );
      case 'lessonArtUpload':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Art Capture' : 'Show Your Art!'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Take a photo of the finished artwork</li>
              <li>Or record a short video showing it off</li>
              <li>You can also pick from your photo library</li>
              {t ? <li>This becomes part of the student's portfolio for assessment</li>
                : <li>Don't worry about it being perfect — it's about the journey!</li>}
            </ul>
          </div>
        );
      case 'success':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Mission Complete!</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your progress is saved automatically</li>
              <li>Choose your <b>work mode</b> — did you work alone, with a buddy, or as a team?</li>
              <li>Check off which senses you used during the activity</li>
              {t && <li>Work mode tracks the student's Kern framework progression (dependent, independent, interdependent)</li>}
            </ul>
          </div>
        );
      case 'portfolio':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">Student Portfolio</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>All captured artwork, ratings, and reflections live here</li>
              <li>Tap any piece to see the full critique and self-assessment</li>
              <li>Use <b>"Share My Progress"</b> from the home screen to export via QR</li>
              <li>The 6-category rubric aligns with the 4TZ Assessment framework</li>
            </ul>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">My Collection</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>This is everything you've created!</li>
              <li>Tap any piece to see your ratings and reflections</li>
              <li>Add new art anytime with the + button</li>
              <li>Watch your collection grow as you complete more adventures</li>
            </ul>
          </div>
        );
      case 'portfolioUpload':
      case 'portfolioTag':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Add to Collection</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Take a photo or video of your work</li>
              <li>Choose which corner post it connects to</li>
              <li>Next you'll rate and reflect on your creation</li>
            </ul>
          </div>
        );
      case 'portfolioCritique':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Feldman Critique Method' : 'Reflect on Your Art'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Step 1: Describe</b> — what do you see? (subject, media, size)</li>
              <li><b>Step 2: Analyze</b> — how were the art elements used? (line, color, shape)</li>
              <li><b>Step 3: Interpret</b> — what does it mean? What inspired you?</li>
              <li><b>Step 4: Evaluate</b> — is it successful? What would you change?</li>
              {t && <li>Students also rate across 6 categories: planning, technique, creativity, corner-post, principle, self-critique</li>}
            </ul>
          </div>
        );
      case 'portfolioDetail':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Portfolio Entry Detail' : 'Your Masterpiece'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>See your photo, ratings, and reflections all in one place</li>
              {t ? <li>Teacher observations appear as teal bars below the student's self-assessment</li>
                : <li>Compare your ratings over time to see how you're growing</li>}
              <li>The work mode badge shows how you worked (solo, buddy, or team)</li>
            </ul>
          </div>
        );
      case 'portfolioGrowth':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Growth Over Time' : 'Your Growth Story'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              {t ? <li>The chart shows the student's average ratings trending over time</li>
                : <li>The chart shows how your skills are growing</li>}
              <li>Read your own words from past reflections</li>
              {t ? <li>Use this data alongside your observations for a complete picture</li>
                : <li>Share this with your teacher, parent, or grandparent!</li>}
            </ul>
          </div>
        );
      case 'accountability':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Accountability Timeline' : 'Your Journey Map'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>See every mission and portfolio entry on a timeline</li>
              <li>Work mode badges show your progression: dependent, independent, interdependent</li>
              {t && <li>This maps to the Denise Kern growth framework — look for the trajectory from dependent to interdependent work</li>}
            </ul>
          </div>
        );
      case 'shareProgress':
        return t ? (
          <div>
            <h3 className="text-xl font-bold mb-2">Share Progress</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>The QR code contains the student's ratings, missions, and sensory profile</li>
              <li>Scan this from the <b>Teacher Dashboard</b> to import their data</li>
              <li><b>Copy Code</b> creates a text version for remote sharing (email, message)</li>
              <li>Photos and writing are NOT included — only summary data</li>
            </ul>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2">Transmit Your Log</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Show the QR code to your teacher so they can scan it</li>
              <li>Or tap <b>Copy Code</b> to send it in a message</li>
              <li>Your photos and writing stay private — only your ratings are shared</li>
            </ul>
          </div>
        );
      case 'diveBuddies':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Dive Buddies</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Show your QR code to a friend to connect</li>
              <li>Tap <b>Scan Buddy</b> to add someone by scanning their code</li>
              <li>Or tap <b>Code</b> to copy a text version you can share</li>
              <li>Have a code from a friend? Tap <b>"Have a buddy code?"</b> to paste it</li>
            </ul>
          </div>
        );
      case 'teacherDashboard':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Teacher Dashboard</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Scan QR</b> opens the camera to scan student progress codes</li>
              <li>The clipboard button lets you <b>paste</b> a student's code (for remote sharing)</li>
              <li>Tap any student to see their detailed progress and add observations</li>
              <li><b>Lesson Plans</b> section lets you create custom lessons or view all lessons</li>
              <li>The class overview chart appears once you have 2+ students scanned</li>
            </ul>
          </div>
        );
      case 'teacherStudentDetail':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Student Detail</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>View the student's self-assessment ratings per corner post</li>
              <li>Add your own <b>teacher observations</b> — rate the same 6 categories and add a note</li>
              <li>Your ratings appear as teal bars below the student's blue bars</li>
              <li>Use this to identify gaps between student self-perception and your assessment</li>
            </ul>
          </div>
        );
      case 'lessonBuilder':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Lesson Builder</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Create custom lessons in 6 steps: basics, questions, vocabulary, activity, media, review</li>
              <li>Add a story read-aloud link, technique videos, and materials list</li>
              <li>Custom lessons appear after the built-in lessons in the lesson picker</li>
              <li>You can edit or delete custom lessons from the Teacher Dashboard</li>
            </ul>
          </div>
        );
      case 'aquarium':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">My Aquarium</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Complete lessons and missions to discover sea creatures!</li>
              <li>Tap new creatures at the bottom to place them in your aquarium</li>
              <li>The sandcastle at the bottom grows as you complete corner posts</li>
              <li>Fill all 6 corner posts to complete your fort!</li>
            </ul>
          </div>
        );
      case 'vocabularyList':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Master Vocabulary' : "Diver's Dictionary"}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t ? 'All vocabulary terms across every lesson, deduplicated' : 'Every word from all your lessons in one place'}</li>
              <li>Use the search bar to find a specific term</li>
              <li>Tap a lesson name to jump to that lesson</li>
              {t && <li>Great for pre-teaching vocabulary before a lesson</li>}
            </ul>
          </div>
        );
      case 'sensoryProfile':
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">{t ? 'Sensory Preferences' : 'About You'}</h3>
            <ul className="list-disc pl-6 space-y-1">
              {t ? <li>These preferences customize activity suggestions and sensory notes for each mission</li>
                : <li>Answer 3 quick questions so we can make activities more comfortable for you</li>}
              <li>There are no wrong answers — just pick what feels right</li>
              <li>You can change these anytime in Settings</li>
            </ul>
          </div>
        );
      default:
        return (
          <div>
            <h3 className="text-xl font-bold mb-2">Need Help?</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tap the back arrow to return to the previous screen</li>
              <li>Your progress is saved automatically</li>
              <li>Open <b>Settings</b> (gear icon) to adjust text size, contrast, or text-to-speech</li>
            </ul>
          </div>
        );
    }
  };

  // ─── Sensory Profile Quiz ─────────────────────────────────────
  const sensoryQuestions = [
    {
      key: 'movement',
      question: 'How do you feel about movement?',
      options: [
        { value: 'loves', label: 'Loves moving around a lot' },
        { value: 'moderate', label: 'Likes some movement, not too much' },
        { value: 'minimal', label: 'Prefers sitting quietly' },
      ],
    },
    {
      key: 'sound',
      question: 'How do you feel about sounds?',
      options: [
        { value: 'loud', label: 'Enjoys music and louder activities' },
        { value: 'moderate', label: 'Comfortable with normal conversation' },
        { value: 'quiet', label: 'Prefers quiet activities' },
      ],
    },
    {
      key: 'touch',
      question: 'How do you feel about touching things?',
      options: [
        { value: 'seeks', label: 'Loves touching different textures' },
        { value: 'selective', label: 'Likes some textures, not others' },
        { value: 'avoids', label: 'Prefers not to touch messy or new textures' },
      ],
    },
  ];

  const renderSensoryProfileQuiz = () => {
    const answeredCount = sensoryProfile
      ? Object.keys(sensoryProfile).filter(k => sensoryQuestions.some(q => q.key === k)).length
      : 0;

    return (
      <div className="glass-surface rounded-3xl p-8 max-w-md mx-auto animate-slide-up">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Quick Sensory Preferences</h2>
        <p className="text-sm text-gray-500 mb-6">These questions help us suggest activities that will be most comfortable.</p>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {sensoryQuestions.map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i < answeredCount ? 'bg-purple-500 scale-110' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {sensoryQuestions.map((q, qi) => (
          <div key={q.key} className="mb-6">
            <h3 className="font-bold text-gray-800 mb-3">{qi + 1}. {q.question}</h3>
            <div className="flex flex-col gap-2">
              {q.options.map(opt => {
                const isSelected = sensoryProfile?.[q.key] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setSensoryProfile({ ...sensoryProfile, [q.key]: opt.value })}
                    className={`p-3 rounded-xl text-left text-sm font-medium transition-all duration-300 ${
                      isSelected
                        ? 'bg-purple-100 border-2 border-purple-500 text-purple-800 ring-2 ring-purple-300'
                        : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <button
          onClick={() => {
            // First-time user (no completed missions): go to lesson picker
            if (completedMissions.length === 0 && portfolioEntries.length === 0) {
              setCurrentScreen('lessonPicker');
              if (textToSpeech) speak("You're all set! Pick your first lesson.");
            } else {
              setCurrentScreen('home');
            }
          }}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold text-lg rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-xl"
        >
          {completedMissions.length === 0 && portfolioEntries.length === 0 ? "Let's Go!" : 'Save Preferences'}
        </button>
      </div>
    );
  };

  // ─── Live QR Scanner Overlay ────────────────────────────────
  const renderLiveScanner = () => {
    if (!liveScanActive) return null;
    const modeLabel = liveScanMode === 'buddy' ? 'Searching for Dive Buddy...' : 'Scanning explorer logs...';
    const modeIcon = liveScanMode === 'buddy' ? '🤿' : '📡';

    return (
      <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center">
        {/* Close button */}
        <button
          onClick={stopLiveScan}
          className="absolute top-6 right-6 z-50 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white transition-all active:scale-95 hover:bg-white/20"
        >
          <X size={20} />
        </button>

        {/* Scan count badge for teacher mode */}
        {liveScanMode === 'teacher' && scanCount > 0 && (
          <div className="absolute top-6 left-6 z-50 px-3 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-sm font-medium animate-scale-in">
            {scanCount} explorer{scanCount !== 1 ? 's' : ''} logged
          </div>
        )}

        {/* Mode label */}
        <div className="text-center mb-6 animate-fade-in">
          <span className="text-3xl">{modeIcon}</span>
          <p className="text-white/80 text-sm font-medium mt-2">{modeLabel}</p>
        </div>

        {/* Camera viewport */}
        <div className="scan-viewport animate-scale-in">
          <video
            ref={liveScanVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {/* Scan line */}
          <div className="scan-line" />
          {/* Corner brackets */}
          <div className="scan-corners absolute inset-0 pointer-events-none">
            <span className="absolute inset-0" />
          </div>
          {/* Sonar rings */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="sonar-ring w-[120%] h-[120%] animate-sonar-1" />
            <div className="sonar-ring absolute w-[120%] h-[120%] animate-sonar-2" />
            <div className="sonar-ring absolute w-[120%] h-[120%] animate-sonar-3" />
          </div>
        </div>

        {/* Hidden canvas for QR processing */}
        <canvas ref={liveScanCanvasRef} className="hidden" />

        {/* Scan feedback overlay */}
        {scanFeedback && (
          <div className={`mt-6 px-6 py-3 rounded-2xl animate-slide-up-toast ${
            scanFeedback.type === 'success' ? 'bg-emerald-500/20 border border-emerald-400/30' :
            scanFeedback.type === 'duplicate' ? 'bg-amber-500/20 border border-amber-400/30' :
            'bg-red-500/20 border border-red-400/30'
          }`}>
            <p className={`text-sm font-medium ${
              scanFeedback.type === 'success' ? 'text-emerald-300' :
              scanFeedback.type === 'duplicate' ? 'text-amber-300' :
              'text-red-300'
            }`}>
              {scanFeedback.type === 'success' ? '✅ ' : scanFeedback.type === 'duplicate' ? '⚠️ ' : '❌ '}
              {scanFeedback.message}
            </p>
          </div>
        )}

        {/* Footer actions */}
        <div className="mt-8 flex flex-col items-center gap-3">
          {liveScanMode === 'teacher' && (
            <button
              onClick={stopLiveScan}
              className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-2xl transition-all active:scale-95"
            >
              Done Scanning
            </button>
          )}
          <button
            onClick={() => {
              stopLiveScan();
              if (liveScanMode === 'buddy' && buddyScanRef.current) buddyScanRef.current.click();
              else if (liveScanMode === 'teacher' && teacherScanRef.current) teacherScanRef.current.click();
            }}
            className="text-white/50 text-xs underline"
          >
            Use photo instead
          </button>
        </div>
      </div>
    );
  };

  // ─── Scan Feedback Toast (global) ─────────────────────────
  const renderScanFeedbackToast = () => {
    if (!scanFeedback || liveScanActive) return null; // Only show outside scanner
    return (
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 animate-slide-up-toast">
        <div className={`px-6 py-3 rounded-2xl shadow-2xl backdrop-blur-xl ${
          scanFeedback.type === 'success' ? 'bg-emerald-900/80 border border-emerald-400/30' :
          scanFeedback.type === 'duplicate' ? 'bg-amber-900/80 border border-amber-400/30' :
          'bg-red-900/80 border border-red-400/30'
        }`}>
          <p className={`text-sm font-medium ${
            scanFeedback.type === 'success' ? 'text-emerald-200' :
            scanFeedback.type === 'duplicate' ? 'text-amber-200' :
            'text-red-200'
          }`}>
            {scanFeedback.type === 'success' ? '✅ ' : scanFeedback.type === 'duplicate' ? '⚠️ ' : '❌ '}
            {scanFeedback.message}
          </p>
        </div>
      </div>
    );
  };

  // ─── Settings Modal ───────────────────────────────────────────
  const renderSettings = () => {
    const toggles = [
      { label: 'Simple Mode', value: simpleMode, setter: () => setSimpleMode(!simpleMode) },
      { label: 'High Contrast', value: highContrast, setter: () => setHighContrast(!highContrast) },
      { label: 'Text-to-Speech', value: textToSpeech, setter: () => setTextToSpeech(!textToSpeech) },
      { label: 'Large Text', value: largeText, setter: () => setLargeText(!largeText) },
    ];

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-surface rounded-3xl p-8 max-w-sm w-full animate-scale-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings</h2>

          <div className="space-y-5">
            {toggles.map(t => (
              <div key={t.label} className="flex items-center justify-between">
                <label className="font-medium text-gray-700">{t.label}</label>
                <button
                  onClick={t.setter}
                  className={`w-14 h-8 rounded-full relative transition-colors duration-300 ${
                    t.value ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-6 h-6 rounded-full bg-white shadow-md absolute top-1 transition-transform duration-300 ${
                      t.value ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Current role indicator */}
          {userRole && (
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 mt-4 mb-2">
              <div>
                <p className="text-gray-500 text-xs font-medium">Signed in as</p>
                <p className="text-gray-800 font-bold text-sm">
                  {userRole === 'teacher' ? '🧭 Guide (Teacher/Parent)' : '🤿 Explorer (Student)'}
                </p>
              </div>
              <button
                onClick={() => {
                  setUserRole(null);
                  safeLocalStorage.removeItem('userRole');
                  setShowSettings(false);
                }}
                className="text-xs text-cyan-600 font-medium hover:text-cyan-700"
              >
                Switch
              </button>
            </div>
          )}

          <div className="h-px bg-gray-200 my-6" />

          <button
            onClick={() => { setShowSettings(false); setCurrentScreen('sensoryProfile'); }}
            className="w-full py-3 bg-purple-50 text-purple-700 font-semibold rounded-xl mb-3 transition-all duration-300 active:scale-95 hover:bg-purple-100"
          >
            Sensory Preferences Quiz
          </button>
          <button
            onClick={() => { setShowSettings(false); setTeacherMode(true); setCurrentScreen('teacherDashboard'); }}
            className="w-full py-3 bg-amber-50 text-amber-700 font-semibold rounded-xl mb-3 transition-all duration-300 active:scale-95 hover:bg-amber-100"
          >
            {'\u{1F393}'} Teacher Dashboard
          </button>
          {portfolioEntries.length > 0 && (
            <button
              onClick={async () => {
                if (window.confirm('This will remove all photos, videos, and reflections from your collection. Are you sure?')) {
                  try { await mediaDB.clear(); } catch {}
                  setPortfolioEntries([]);
                  setMediaUrls({});
                  setBlueprints({});
                  setShowSettings(false);
                }
              }}
              className="w-full py-3 bg-red-50 text-red-600 font-semibold rounded-xl mb-3 transition-all duration-300 active:scale-95 hover:bg-red-100"
            >
              Clear My Collection
            </button>
          )}
          <button
            onClick={() => setShowSettings(false)}
            className="w-full py-3 bg-gray-100 text-gray-600 font-semibold rounded-xl transition-all duration-300 active:scale-95 hover:bg-gray-200"
          >
            Close Settings
          </button>
        </div>
      </div>
    );
  };

  // ─── Help Modal ───────────────────────────────────────────────
  const renderHelpPanel = () => {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-surface rounded-3xl p-8 max-w-sm w-full animate-scale-in">
          <div className="text-gray-700 leading-relaxed">
            {getHelpContent()}
          </div>
          <button
            onClick={() => setShowHelp(false)}
            className="w-full py-3 mt-6 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl transition-all duration-300 active:scale-95"
          >
            Got It!
          </button>
        </div>
      </div>
    );
  };

  // ─── Lesson Progress Bar ──────────────────────────────────────
  const lessonStages = ['Read', 'Questions', 'Blueprint', 'Corner Post', 'Reflect'];
  const renderLessonProgress = (currentStage) => (
    <div className="flex items-center justify-center gap-1 mb-6 animate-fade-in">
      {lessonStages.map((label, i) => (
        <div key={label} className="flex flex-col items-center" style={{ minWidth: 52 }}>
          <div className={`h-2 w-full rounded-full transition-all duration-300 ${
            i === currentStage ? 'bg-purple-400 scale-y-125' :
            i < currentStage ? 'bg-purple-400/50' : 'bg-white/15'
          }`} />
          <span className={`text-[10px] mt-1 font-medium transition-colors duration-300 ${
            i === currentStage ? 'text-white' :
            i < currentStage ? 'text-white/50' : 'text-white/25'
          }`}>{label}</span>
        </div>
      ))}
    </div>
  );

  // ─── Growth Chart (SVG) ─────────────────────────────────────
  const renderGrowthChart = (dataPoints, { gradientId, colorFrom, colorTo, xLabels, showAvgLine }) => {
    const W = 280, H = 140, padL = 24, padR = 12, padT = 12, padB = 22;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const n = dataPoints.length;

    const x = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const y = (v) => padT + plotH - ((v - 1) / 4) * plotH;

    const points = dataPoints.map((d, i) => `${x(i)},${y(d.value)}`);
    const polyline = points.join(' ');
    const areaPath = `M${points[0]} ${points.map(p => `L${p}`).join(' ')} L${x(n - 1)},${y(1)} L${x(0)},${y(1)} Z`;

    const avg = dataPoints.reduce((s, d) => s + d.value, 0) / n;

    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 200 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
          <linearGradient id={`${gradientId}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colorFrom} stopOpacity="0.15" />
            <stop offset="100%" stopColor={colorTo} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines & labels */}
        {[1, 2, 3, 4, 5].map(v => (
          <g key={v}>
            <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
            <text x={padL - 4} y={y(v) + 3} textAnchor="end" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="sans-serif">{v}</text>
          </g>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#${gradientId}-fill)`} />

        {/* Line */}
        <polyline points={polyline} fill="none" stroke={`url(#${gradientId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Average dashed line */}
        {showAvgLine && (
          <g>
            <line x1={padL} y1={y(avg)} x2={W - padR} y2={y(avg)} stroke="rgba(255,255,255,0.25)" strokeWidth="0.7" strokeDasharray="4 3" />
            <text x={W - padR + 1} y={y(avg) - 3} fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="sans-serif">Avg: {avg.toFixed(1)}</text>
          </g>
        )}

        {/* Data points */}
        {dataPoints.map((d, i) => {
          const isLast = i === n - 1;
          return (
            <g key={i}>
              {isLast && <circle cx={x(i)} cy={y(d.value)} r="7" fill={colorFrom} opacity="0.2" />}
              <circle cx={x(i)} cy={y(d.value)} r={isLast ? 4.5 : 3.5} fill="white" stroke={`url(#${gradientId})`} strokeWidth="1.5" />
            </g>
          );
        })}

        {/* X-axis labels */}
        {xLabels.map((lbl, i) => lbl ? (
          <text key={i} x={x(i)} y={H - 4} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="sans-serif">{lbl}</text>
        ) : null)}
      </svg>
    );
  };

  // ─── Floating Action Buttons ──────────────────────────────────
  // ─── TZ Mascot — contextual guide ─────────────────────────
  const tzTipRef = useRef('');
  const getTZTip = () => {
    const t = teacherMode;
    const name = avatarName || 'explorer';
    const missions = (completedMissions || []).length;
    const pieces = portfolioEntries.length;
    const pick = (arr) => { const tip = arr[Math.floor(Math.random() * arr.length)]; tzTipRef.current = tip; return tip; };

    switch (currentScreen) {
      case 'home':
        if (!avatar) return pick([
          "Hey there! I'm TZ, your dive buddy! Tap 'Start Your Adventure' to create your diver!",
          "Welcome to the deep! Let's build your ocean explorer. Tap the big button to start!",
          "Ahoy! Ready to dive in? Create your character and start your art adventure!",
        ]);
        if (missions === 0) return pick([
          `${name}! Your first mission awaits. Tap "Today's Lessons" — The Dot is calling!`,
          `Let's go, ${name}! Hit "Today's Lessons" to start your first book adventure!`,
          `Adventure time, ${name}! Tap "Today's Lessons" and let's make some art!`,
        ]);
        if (pieces === 0) return pick([
          `Nice work on ${missions} mission${missions > 1 ? 's' : ''}, ${name}! Now try "My Collection" to capture your art!`,
          `${name}, you've been busy! Add your artwork to "My Collection" so everyone can see it!`,
        ]);
        return t
          ? pick([
            `${teacherStudents.length} explorer${teacherStudents.length !== 1 ? 's' : ''} logged. Head to Teacher Dashboard for details!`,
            'Ready to observe? Check the Teacher Dashboard to add ratings alongside student self-assessments.',
          ])
          : pick([
            `${pieces} masterpiece${pieces > 1 ? 's' : ''} and counting, ${name}! What will you create next?`,
            `Looking great, ${name}! Keep diving deeper — every adventure teaches you something new!`,
            `Hey ${name}! Did you know you can share your progress with your teacher? Try it!`,
          ]);
      case 'cornerPosts': {
        const done = cornerPosts.filter(p => p.missions.some(m => completedMissions.includes(m.id))).length;
        return pick(done < 6
          ? [`${done} of 6 corner posts explored! Tap a new color to discover something different!`,
             'Each corner post is a whole new world! Try one you haven\'t visited yet.',
             'Pro tip: mixing different corner posts makes you a well-rounded explorer!']
          : ['You\'ve touched all 6 corner posts! That\'s legendary explorer status!',
             'All 6 explored! Try going deeper — there are more missions in each one.']
        );
      }
      case 'missions':
        return pick([
          'Pick a mission that looks fun! Tap "Lesson Guide" first to learn the key words.',
          'Every mission is an adventure. Which one catches your eye?',
          'Not sure where to start? The Lesson Guide has everything you need to know!',
        ]);
      case 'missionDetail':
        return pick([
          'Follow the steps — no rush! Great art takes time.',
          'You got this! Do one step at a time, and remember — there\'s no wrong way.',
          'Take your time with each step. If you get stuck, ask your dive buddy!',
        ]);
      case 'lessonPicker':
        return t
          ? pick([
            'Lessons unlock one at a time. Check the shopping list to prep materials!',
            'Shopping list at the top shows materials for upcoming lessons. Great for planning!',
          ])
          : pick([
            'Start with the first unlocked lesson! Each one you finish opens the next.',
            'Got your art supplies? Check the shopping list at the top before you start!',
            'Tap a lesson to begin your next art adventure!',
          ]);
      case 'lessonRead':
        return pick([
          'Step 1: Listen to the story! Then learn the words and hit "Ready to Create!"',
          'Hear the story first — it\'ll inspire your art! The play button is right there.',
          'Pro explorer tip: listen to the story AND check the vocabulary before creating!',
          'The story is the key to great art! Tap play, then get ready to make something amazing.',
        ]);
      case 'lessonQuestions':
        return pick([
          'No wrong answers! Make a Splash — just say what YOU think! 🌊',
          'Think about it... what comes to mind? Type it out or speak it!',
          'Your answers help tell your story. Be honest — that\'s the explorer way!',
        ]);
      case 'blueprint':
      case 'lessonBlueprint':
        return pick([
          'Great artists plan first! Sketch your idea or write it down.',
          'What will your masterpiece look like? Plan it out before you start!',
          'A blueprint is your secret weapon. Even a quick sketch helps!',
        ]);
      case 'lessonArtUpload':
        return pick([
          'Show off your creation! Take a photo or record a quick video.',
          'Time to capture your masterpiece! Your art tells YOUR story.',
          'Snap a pic of what you made! It goes straight into your portfolio.',
        ]);
      case 'success':
        return pick([
          'Amazing work! How did you do it — solo, with a buddy, or as a team?',
          'You crushed it! Pick your work mode and check off the senses you used.',
          'Another mission complete! You made a splash! 🌊 True explorer status!',
          '💖 You deserve to feel loved, important, and safe. And you just proved you\'re all three!',
        ]);
      case 'portfolio':
        return t
          ? pick([
            `${pieces} entries. Tap any piece to see self-assessments and add observations.`,
            'Review student work here. Add your teacher observations for a complete picture.',
          ])
          : pick([
            `${pieces} piece${pieces > 1 ? 's' : ''} in your collection! Tap any to revisit.`,
            'Your portfolio is growing! Each piece tells part of your story.',
            'Look how far you\'ve come! Every piece shows your growth as an artist.',
          ]);
      case 'portfolioCritique':
        return pick([
          'Describe, Analyze, Interpret, Evaluate — the 4 steps to thinking like an art critic!',
          'Rate your work honestly — that\'s how real artists grow!',
          'The stars show how you feel about your work. There\'s no wrong rating!',
        ]);
      case 'shareProgress':
        return t
          ? pick(['Scan this from the Teacher Dashboard, or use "Copy Code" for remote sharing.'])
          : pick([
            'Show this code to your teacher! They\'ll be impressed with your progress.',
            'Your expedition log is ready to transmit! Show the QR or copy the code.',
          ]);
      case 'diveBuddies':
        return pick([
          'Show your code to a friend to connect! Dive buddies are stronger together.',
          'Scan a friend\'s code or share yours. More buddies = more fun!',
        ]);
      case 'teacherDashboard':
        return pick([
          `${teacherStudents.length} explorer${teacherStudents.length !== 1 ? 's' : ''} logged. Scan more QR codes or paste codes!`,
          'Tap any student to see their detailed progress and add your observations.',
          'The shopping list shows what materials to prep for upcoming lessons.',
        ]);
      case 'teacherStudentDetail':
        return pick([
          'Blue bars = student self-assessment. Add your teal bars to compare!',
          'Look for gaps between student ratings and yours — that\'s where to focus feedback.',
        ]);
      case 'lessonBuilder':
        return pick([
          '6 steps to a great lesson! Students will see it in their lesson picker.',
          'Add a story link, vocab, and materials. Your custom lesson will unlock after the built-ins.',
        ]);
      case 'aquarium': {
        const placed = aquariumItems.filter(i => i.placed).length;
        const unplaced = aquariumItems.filter(i => !i.placed).length;
        return pick(
          unplaced > 0
            ? [`You have ${unplaced} new creature${unplaced > 1 ? 's' : ''} to place! Tap them to add to your aquarium!`,
               'New friends waiting! Tap the creatures at the bottom to drop them in your ocean.']
            : placed > 0
              ? [`${placed} creatures swimming in your aquarium! Complete more adventures to discover new ones.`,
                 'Your ocean is coming alive! Keep exploring to fill it up.']
              : ['Your aquarium is empty — start a lesson to earn your first sea creature!',
                 'Every adventure earns you a new ocean friend. Let\'s go discover some!']
        );
      }
      case 'vocabularyList':
        return pick([
          'Every art term in one place! Use the search bar to find what you need.',
          'Tap a lesson name to jump straight to that lesson. Handy for review!',
        ]);
      case 'accountability':
        return pick([
          'Your journey map! Look for the pattern — are you becoming more independent?',
          'Every step is progress. The timeline shows how far you\'ve come!',
        ]);
      default:
        return pick([
          'Tap around and explore! Remember — Make a Splash! 🌊 There are no wrong moves, only new discoveries!',
          '💖 Buoy Boost: Everyone deserves to feel loved, important, and safe. That includes YOU!',
          '⚓ Remember: Listening + Time + Energy = Trust. Be a good dive buddy!',
          '👑 Leadership tip: Take care of your people. The one doing the work gets to choose!',
        ]);
    }
  };

  const renderTZMascot = () => {
    const s = tzState.current;
    const TZC = { primary: '#00C9FF', secondary: '#6C63FF', accent: '#FFD600', dark: '#0A0E27' };
    const DIVER_W = 100;
    const kick = Math.sin(s.kickPhase) * (s.boosting ? 24 : 16);
    const kick2 = Math.sin(s.kickPhase + 1.2) * (s.boosting ? 24 : 16);
    const armWave = Math.sin(s.armPhase) * 10;
    const bob = Math.sin(s.kickPhase * 0.5) * 2;
    const flipX = s.facingRight ? 1 : -1;

    return (
      <div
        className="fixed inset-0 z-30"
        style={{ pointerEvents: 'none' }}
      >
        {/* SVG overlay with diver, fish, bubbles — NO clickable layer blocking UI */}
        <svg
          width="100%" height="100%"
          className="absolute inset-0"
          style={{ pointerEvents: 'none' }}
        >
          {/* Fish */}
          {s.fish.map((f, i) => {
            const dir = f.vx < 0 ? -1 : 1;
            const tailWag = Math.sin(f.wobble * 3) * 7;
            return (
              <g key={i} transform={`translate(${f.x},${f.y}) scale(${f.scale * dir},${f.scale})`} opacity="0.7">
                <ellipse cx="0" cy="0" rx="11" ry="6" fill={f.color}>
                  <animate attributeName="ry" values="6;5;6" dur="0.6s" repeatCount="indefinite" />
                </ellipse>
                <polygon points={`-11,-4 ${-18 + tailWag},0 -11,4`} fill={f.color} opacity="0.6" />
                <circle cx="5" cy="-1.5" r="1.5" fill="#1a1a2e" />
                <circle cx="5.4" cy="-1.8" r="0.5" fill="white" />
              </g>
            );
          })}

          {/* Bubbles */}
          {s.bubbles.map(b => (
            <circle
              key={b.id}
              cx={b.x + Math.sin(b.wobble) * 6}
              cy={b.y} r={b.r}
              fill="rgba(180,235,255,0.1)"
              stroke="rgba(180,230,255,0.4)"
              strokeWidth={0.5}
              opacity={b.life}
            />
          ))}

          {/* THE DIVER — clickable */}
          <g
            transform={`translate(${s.x},${s.y})`}
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (showTZ) {
                setShowTZ(false);
              } else {
                tzTipRef.current = getTZTip();
                setShowTZ(true);
                if (tzTimerRef.current) clearTimeout(tzTimerRef.current);
                tzTimerRef.current = setTimeout(() => setShowTZ(false), 6000);
              }
            }}
          >
            <g transform={`scale(${flipX * 0.7},0.7) translate(${s.facingRight ? 0 : -DIVER_W},0)`}>
              {/* Fins */}
              <g transform={`translate(10, ${60 + bob})`}>
                <g transform={`rotate(${kick}, 0, 0)`}>
                  <path d="M0,0 Q-30,4 -48,14 Q-30,10 -48,26 Q-20,16 0,12 Z" fill={TZC.primary} opacity="0.85" />
                </g>
              </g>
              <g transform={`translate(10, ${74 + bob})`}>
                <g transform={`rotate(${kick2}, 0, 0)`}>
                  <path d="M0,0 Q-30,4 -48,14 Q-30,10 -48,26 Q-20,16 0,12 Z" fill={TZC.primary} opacity="0.75" />
                </g>
              </g>
              {/* Legs */}
              <g transform={`translate(18, ${50 + bob})`}>
                <line x1="0" y1="0" x2={-4} y2={14 + kick * 0.3} stroke="#1a1f4e" strokeWidth="7" strokeLinecap="round" />
                <line x1="9" y1="0" x2={5} y2={14 + kick2 * 0.3} stroke="#1a1f4e" strokeWidth="7" strokeLinecap="round" />
              </g>
              {/* Body */}
              <ellipse cx="40" cy={35 + bob} rx="20" ry="24" fill="#1a1f4e" />
              <path d={`M22,${24 + bob} Q40,${17 + bob} 54,${24 + bob}`} fill="none" stroke={TZC.primary} strokeWidth="2" opacity="0.5" />
              <text x="33" y={39 + bob} fill={TZC.primary} fontSize="8" fontWeight="900" textAnchor="middle" opacity="0.6">4TZ</text>
              {/* Tank */}
              <rect x="48" y={18 + bob} width="12" height="30" rx="6" fill="#2d2d5e" />
              <rect x="49" y={27 + bob} width="11" height="9" rx="3" fill={TZC.secondary} opacity="0.5" />
              <text x="54.5" y={34 + bob} fill="white" fontSize="6" fontWeight="800" textAnchor="middle">TZ</text>
              {/* Arm */}
              <g transform={`translate(56, ${32 + bob})`}>
                <line x1="0" y1="0" x2={14} y2={armWave} stroke="#1a1f4e" strokeWidth="6" strokeLinecap="round" />
                <circle cx={16} cy={armWave} r="4" fill="#2a2f6e" />
              </g>
              {/* Head */}
              <circle cx="68" cy={23 + bob} r="13" fill="#FFCC80" />
              {/* Mask */}
              <rect x="60" y={14 + bob} width="19" height="12" rx="4" fill="rgba(0,201,255,0.25)" stroke={TZC.dark} strokeWidth="1.5" />
              <circle cx="70" cy={20 + bob} r="2" fill="#1a1a2e" />
              <circle cx="70.4" cy={19.6 + bob} r="0.7" fill="white" />
              <circle cx="74" cy={25 + bob} r="2.5" fill="#FFAB91" opacity="0.3" />
              <path d={`M66,${27 + bob} Q70,${30 + bob} 75,${27 + bob}`} fill="none" stroke="#BF360C" strokeWidth="1.2" strokeLinecap="round" />
              {/* Snorkel */}
              <path d={`M79,${17 + bob} Q86,${9 + bob} 84,${2 + bob}`} fill="none" stroke={TZC.accent} strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="84" cy={1 + bob} r="3" fill={TZC.accent} />
            </g>
          </g>
        </svg>

        {/* Speech bubble */}
        {showTZ && (
          <div
            data-tz-ui="true"
            className="absolute animate-scale-in"
            style={{
              left: Math.min(Math.max(s.x + (s.facingRight ? 80 : -220), 10), window.innerWidth - 260),
              top: Math.max(s.y - 60, 10),
              width: 240,
              pointerEvents: 'auto',
              zIndex: 50,
            }}
          >
            <div
              className="glass-surface rounded-2xl p-3 shadow-2xl cursor-pointer"
              onClick={() => setShowTZ(false)}
            >
              <p className="text-gray-700 text-sm leading-relaxed">{tzTipRef.current}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Rules Panel (Dive Manual) ──────────────────────────
  const renderRulesPanel = () => {
    if (!showRules) return null;
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowRules(false)}>
        <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
          {/* Header with TZ catchphrase */}
          <div className="bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-600 rounded-t-3xl p-6 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="relative z-10">
              <div className="text-5xl mb-2">🤿</div>
              <h2 className="text-2xl font-black text-white tracking-tight">TZ's Dive Manual</h2>
              <div className="mt-3 inline-block px-5 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                <p className="text-white font-black text-lg tracking-wide">
                  "Make a Splash!" 🌊
                </p>
              </div>
              <p className="text-white/70 text-xs mt-2">Mistakes are just splashes on your way to something great.</p>
            </div>
          </div>

          <div className="glass-surface rounded-b-3xl">
            {/* 1. Loved, Important & Safe — The Promise */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-sm">💖</span>
                <h3 className="text-gray-800 font-black text-sm uppercase tracking-wider">Our Promise</h3>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-4 border border-pink-100">
                <p className="text-gray-800 text-sm font-bold text-center">Everyone deserves to feel loved, important, and safe.</p>
                <p className="text-gray-400 text-[10px] mt-2 text-center italic">Ask your grandee or dive buddy!</p>
              </div>
            </div>

            {/* 2. Buoy Boost — Engagement, Connection, Trust */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-sm">⚓</span>
                <h3 className="text-gray-800 font-black text-sm uppercase tracking-wider">Buoy Boost</h3>
              </div>
              <p className="text-gray-400 text-xs mb-3 italic">Engagement, connection, trust</p>

              {/* The formula */}
              <div className="bg-emerald-50/80 rounded-2xl p-3 mb-3 text-center border border-emerald-100">
                <p className="text-emerald-700 text-sm font-bold">Listening + Time + Energy = Trust</p>
                <p className="text-emerald-500 text-[10px] mt-0.5">...or connection, or engagement</p>
              </div>

              <div className="space-y-2.5">
                {[
                  { icon: '👂', rule: 'Listen when your dive buddy speaks' },
                  { icon: '🙋', rule: 'Raise your hand like a periscope' },
                  { icon: '💪', rule: 'Try everything — even the hard stuff' },
                  { icon: '🤝', rule: 'Help your crew when they need it' },
                  { icon: '💡', rule: "Respect everyone's ideas — every idea is a treasure" },
                  { icon: '🧹', rule: 'Leave your workspace shipshape' },
                  { icon: '🌊', rule: "Make a Splash — you can have lots of splashes before your final one!" },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-emerald-50/60 rounded-xl px-3 py-2.5">
                    <span className="text-lg flex-shrink-0 mt-0.5">{r.icon}</span>
                    <div>
                      <p className="text-gray-700 text-sm font-medium">{r.rule}</p>
                      <p className="text-gray-400 text-[10px] mt-0.5 italic">Ask your grandee or dive buddy!</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. Anger Buoy Boost */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-sm">🌡️</span>
                <h3 className="text-gray-800 font-black text-sm uppercase tracking-wider">Anger Buoy Boost</h3>
              </div>
              <div className="bg-amber-50/80 rounded-2xl p-3 mb-3 border border-amber-100">
                <p className="text-amber-700 text-sm font-bold text-center">Everyone is born with angry energy.</p>
                <p className="text-amber-600 text-xs mt-1 text-center">When we get our angry energy out of our bodies <b>without</b>:</p>
                <div className="flex flex-col items-center gap-0.5 mt-1">
                  <span className="text-amber-600 text-xs">hurting ourselves</span>
                  <span className="text-amber-600 text-xs">hurting others with words or actions</span>
                  <span className="text-amber-600 text-xs">hurting property</span>
                </div>
              </div>
              <div className="space-y-2.5">
                {[
                  { icon: '🫧', rule: 'Stop and blow 3 slow bubbles (deep breaths)' },
                  { icon: '🔟', rule: 'Count to 10 like counting fish' },
                  { icon: '🗣️', rule: 'Use your words — say "I feel..."' },
                  { icon: '🏝️', rule: 'Swim to your calm corner' },
                  { icon: '🧭', rule: 'Ask your guide or dive buddy for help' },
                  { icon: '💜', rule: "It's okay to feel stormy — it's what you do next that matters" },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-amber-50/60 rounded-xl px-3 py-2.5">
                    <span className="text-lg flex-shrink-0 mt-0.5">{r.icon}</span>
                    <div>
                      <p className="text-gray-700 text-sm font-medium">{r.rule}</p>
                      <p className="text-gray-400 text-[10px] mt-0.5 italic">Ask your grandee or dive buddy!</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Maggie's Rules of Leadership */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-sm">👑</span>
                <h3 className="text-gray-800 font-black text-sm uppercase tracking-wider">Leadership Buoy Boost</h3>
              </div>
              <p className="text-gray-400 text-xs mb-3 italic">Maggie's Rules — when you are a leader, you must:</p>
              <div className="space-y-2.5">
                {[
                  { num: '#1', rule: 'Take care of your people', note: 'The one doing the work gets to choose.' },
                  { num: '#2', rule: 'Say "yes" — unless there is a compelling reason to say "no"', note: 'Stay open to new ideas.' },
                  { num: '#3', rule: 'Prevent stupid', note: 'Anticipate what can go wrong.' },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-blue-50/60 rounded-xl px-3 py-3">
                    <span className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-black flex-shrink-0">{r.num}</span>
                    <div>
                      <p className="text-gray-700 text-sm font-bold">{r.rule}</p>
                      <p className="text-gray-400 text-[10px] mt-0.5 italic">{r.note}</p>
                      <p className="text-blue-300 text-[10px] mt-0.5 italic">Ask your grandee or dive buddy!</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* TZ footer */}
            <div className="px-5 pb-5">
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-4 text-center border border-cyan-100">
                <p className="text-cyan-700 font-black text-sm">Remember:</p>
                <p className="text-cyan-600 text-sm font-medium mt-1">"Every great explorer made a splash before they made a discovery."</p>
                <p className="text-cyan-400 text-xs mt-1">— TZ 🤿</p>
              </div>
            </div>

            {/* Close button */}
            <div className="px-5 pb-5">
              <button
                onClick={() => setShowRules(false)}
                className="w-full py-3 bg-gray-100 text-gray-600 font-semibold rounded-xl transition-all duration-300 active:scale-95 hover:bg-gray-200"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <>
        {renderTZMascot()}
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
          <button
            onClick={() => setShowRules(true)}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-white flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95"
            aria-label="Dive Manual"
          >
            <span className="text-lg">⚓</span>
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="w-12 h-12 rounded-full glass-surface flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95"
            aria-label="Settings"
          >
            <Settings size={20} className="text-gray-600" />
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-500 text-white flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95"
            aria-label="Help"
          >
            <HelpCircle size={20} />
          </button>
        </div>
        {renderRulesPanel()}
      </>
    );
  };

  // ─── Sensory Profile Screen ───────────────────────────────────
  if (currentScreen === 'sensoryProfile') {
    return (
      <div className="mesh-gradient flex items-center justify-center p-6">
        {renderSensoryProfileQuiz()}
        {renderActionButtons()}
      </div>
    );
  }

  // Floating bubble particles for ocean landing page
  const floatingBubbles = [
    { size: 'w-3 h-3', left: '10%', delay: 'animate-bubble-1', opacity: 'opacity-20' },
    { size: 'w-2 h-2', left: '25%', delay: 'animate-bubble-2', opacity: 'opacity-15' },
    { size: 'w-4 h-4', left: '40%', delay: 'animate-bubble-3', opacity: 'opacity-10' },
    { size: 'w-2 h-2', left: '55%', delay: 'animate-bubble-1', opacity: 'opacity-20' },
    { size: 'w-3 h-3', left: '70%', delay: 'animate-bubble-4', opacity: 'opacity-15' },
    { size: 'w-1.5 h-1.5', left: '85%', delay: 'animate-bubble-2', opacity: 'opacity-20' },
    { size: 'w-2.5 h-2.5', left: '15%', delay: 'animate-bubble-3', opacity: 'opacity-10' },
    { size: 'w-2 h-2', left: '60%', delay: 'animate-bubble-4', opacity: 'opacity-15' },
  ];

  // Animated hero elements — corner post hexagons + popping bubbles + ocean shapes
  const heroCornerPostFloaters = [
    { icon: '🌱', color: 'rgba(34,197,94,0.25)', left: '8%', top: '15%', size: 44, delay: 0 },
    { icon: '🤝', color: 'rgba(59,130,246,0.25)', left: '82%', top: '12%', size: 40, delay: 0.8 },
    { icon: '💪', color: 'rgba(239,68,68,0.25)', left: '5%', top: '60%', size: 38, delay: 1.6 },
    { icon: '🧠', color: 'rgba(168,85,247,0.25)', left: '88%', top: '55%', size: 42, delay: 2.4 },
    { icon: '💜', color: 'rgba(236,72,153,0.25)', left: '12%', top: '82%', size: 36, delay: 3.2 },
    { icon: '✨', color: 'rgba(250,204,21,0.25)', left: '78%', top: '80%', size: 40, delay: 4.0 },
  ];

  const heroBubblePops = [
    { left: '18%', top: '25%', size: 28, delay: 2 },
    { left: '72%', top: '35%', size: 22, delay: 5 },
    { left: '45%', top: '70%', size: 32, delay: 8 },
    { left: '30%', top: '50%', size: 18, delay: 11 },
    { left: '65%', top: '20%', size: 26, delay: 14 },
    { left: '50%', top: '45%', size: 20, delay: 7 },
    { left: '85%', top: '40%', size: 16, delay: 4 },
    { left: '22%', top: '68%', size: 24, delay: 10 },
  ];

  const heroOceanShapes = [
    { type: 'starfish', left: '92%', top: '88%', delay: 1, size: 28 },
    { type: 'shell', left: '3%', top: '42%', delay: 3, size: 24 },
    { type: 'wave', left: '50%', top: '8%', delay: 2, size: 32 },
    { type: 'coral', left: '75%', top: '68%', delay: 0, size: 26 },
  ];

  const heroBuoyBuddies = [
    { label: 'Loved, Important & Safe', emoji: '💖', left: '6%',  top: '22%', delay: 0,   color: '#f472b6', ring: '#ec4899' },
    { label: 'Listen + Time + Energy = Trust', emoji: '⚓', left: '78%', top: '18%', delay: 1.4, color: '#38bdf8', ring: '#0ea5e9' },
    { label: 'Angry energy OUT without hurting', emoji: '🌡️', left: '82%', top: '62%', delay: 2.8, color: '#fb923c', ring: '#f97316' },
    { label: 'Take care of your people', emoji: '👑', left: '4%',  top: '68%', delay: 4.2, color: '#a78bfa', ring: '#8b5cf6' },
  ];

  // ─── Character Builder Data ────────────────────────────────────
  const AVATAR_BASES = [
    { skin: '#FDDCB5', cheek: '#F4A7A7', label: 'Light' },
    { skin: '#F2C18A', cheek: '#E89A8A', label: 'Light Medium' },
    { skin: '#D4944A', cheek: '#C07848', label: 'Medium' },
    { skin: '#A0673A', cheek: '#8B5A30', label: 'Medium Dark' },
    { skin: '#6B4226', cheek: '#5A3720', label: 'Dark' },
  ];

  const AVATAR_HAIRS = [
    { label: 'Short Spiky', color: '#3B2F2F' },
    { label: 'Long Wavy', color: '#6B3A2A' },
    { label: 'Curly Puff', color: '#1A1A1A' },
    { label: 'Pigtails', color: '#D4A043' },
    { label: 'Buzz Cut', color: '#4A3728' },
    { label: 'Captain Hat', color: '#1E3A5F' },
  ];

  const AVATAR_ACCESSORIES = [
    { label: 'None' },
    { label: 'Dive Goggles' },
    { label: 'Snorkel' },
    { label: 'Bubble Wand' },
    { label: 'Star Earrings' },
    { label: 'Coral Crown' },
  ];

  const renderHairSvg = (hairIndex, color) => {
    switch (hairIndex) {
      case 0: // Short Spiky
        return <path d="M28,30 L33,12 L40,25 L50,8 L60,25 L67,12 L72,30" fill={color} />;
      case 1: // Long Wavy
        return (<>
          <ellipse cx="50" cy="25" rx="28" ry="12" fill={color} />
          <path d="M22,28 Q20,55 25,68 Q28,72 30,68 Q32,60 30,50 Q28,40 22,28 Z" fill={color} />
          <path d="M78,28 Q80,55 75,68 Q72,72 70,68 Q68,60 70,50 Q72,40 78,28 Z" fill={color} />
        </>);
      case 2: // Curly Puff
        return (<>
          <circle cx="35" cy="20" r="10" fill={color} />
          <circle cx="50" cy="16" r="11" fill={color} />
          <circle cx="65" cy="20" r="10" fill={color} />
          <circle cx="28" cy="30" r="8" fill={color} />
          <circle cx="72" cy="30" r="8" fill={color} />
          <circle cx="42" cy="14" r="7" fill={color} />
          <circle cx="58" cy="14" r="7" fill={color} />
        </>);
      case 3: // Pigtails
        return (<>
          <ellipse cx="50" cy="24" rx="24" ry="8" fill={color} />
          <circle cx="24" cy="48" r="10" fill={color} />
          <circle cx="76" cy="48" r="10" fill={color} />
          <path d="M28,28 Q22,35 24,42" stroke={color} strokeWidth="4" fill="none" />
          <path d="M72,28 Q78,35 76,42" stroke={color} strokeWidth="4" fill="none" />
        </>);
      case 4: // Buzz Cut
        return <path d="M24,38 Q24,14 50,12 Q76,14 76,38" fill={color} opacity="0.7" />;
      case 5: // Captain Hat
        return (<>
          <path d="M22,32 L78,32 L74,18 L26,18 Z" fill="#1E3A5F" />
          <rect x="22" y="30" width="56" height="6" rx="2" fill="#2D4A6F" />
          <rect x="42" y="20" width="16" height="8" rx="1" fill="#fbbf24" />
        </>);
      default:
        return null;
    }
  };

  const renderAccessorySvg = (accIndex) => {
    switch (accIndex) {
      case 0: return null; // None
      case 1: // Dive Goggles
        return (<>
          <ellipse cx="40" cy="43" rx="10" ry="8" fill="rgba(14,165,233,0.25)" stroke="#0891b2" strokeWidth="2" />
          <ellipse cx="60" cy="43" rx="10" ry="8" fill="rgba(14,165,233,0.25)" stroke="#0891b2" strokeWidth="2" />
          <line x1="50" y1="43" x2="50" y2="43" stroke="#0891b2" strokeWidth="2" />
          <path d="M30,43 Q28,40 26,38" stroke="#64748b" strokeWidth="2" fill="none" />
          <path d="M70,43 Q72,40 74,38" stroke="#64748b" strokeWidth="2" fill="none" />
        </>);
      case 2: // Snorkel
        return (<>
          <circle cx="44" cy="58" r="3" fill="#94a3b8" />
          <path d="M44,55 Q44,40 52,35 Q60,30 60,20 Q60,14 64,10" stroke="#22d3ee" strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx="64" cy="8" r="3" fill="#22d3ee" />
        </>);
      case 3: // Bubble Wand
        return (<>
          <line x1="75" y1="55" x2="82" y2="75" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="75" cy="52" r="5" fill="none" stroke="#a855f7" strokeWidth="1.5" />
          <circle cx="72" cy="42" r="2.5" fill="rgba(255,255,255,0.5)" />
          <circle cx="78" cy="38" r="1.8" fill="rgba(255,255,255,0.4)" />
          <circle cx="70" cy="35" r="1.5" fill="rgba(255,255,255,0.3)" />
        </>);
      case 4: // Star Earrings
        return (<>
          <polygon points="22,52 23.5,48 27,48 24.5,45.5 25.5,42 22,44.5 18.5,42 19.5,45.5 17,48 20.5,48" fill="#fbbf24" />
          <polygon points="78,52 79.5,48 83,48 80.5,45.5 81.5,42 78,44.5 74.5,42 75.5,45.5 73,48 76.5,48" fill="#fbbf24" />
        </>);
      case 5: // Coral Crown
        return (<>
          <path d="M32,22 Q30,14 34,8 Q36,12 38,10 Q40,6 42,10" stroke="#f472b6" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M48,18 Q50,10 52,6 Q54,10 56,8 Q58,12 56,18" stroke="#fb7185" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M62,20 Q64,12 68,10 Q66,14 70,12 Q72,16 68,22" stroke="#f472b6" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>);
      default:
        return null;
    }
  };

  const DiverAvatar = ({ avatarData, size = 80 }) => {
    if (!avatarData || avatarData.type !== 'custom') return null;
    const base = AVATAR_BASES[avatarData.base] || AVATAR_BASES[0];
    const hair = AVATAR_HAIRS[avatarData.hair] || AVATAR_HAIRS[0];
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        {/* Head */}
        <circle cx="50" cy="48" r="32" fill={base.skin} />
        {/* Eyes */}
        <circle cx="40" cy="44" r="3" fill="#1a1a1a" />
        <circle cx="60" cy="44" r="3" fill="#1a1a1a" />
        {/* Eye shine */}
        <circle cx="41.2" cy="43" r="1.2" fill="white" />
        <circle cx="61.2" cy="43" r="1.2" fill="white" />
        {/* Smile */}
        <path d="M40,56 Q50,64 60,56" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
        {/* Cheek blush */}
        <circle cx="34" cy="54" r="4.5" fill={base.cheek} opacity="0.4" />
        <circle cx="66" cy="54" r="4.5" fill={base.cheek} opacity="0.4" />
        {/* Hair */}
        {renderHairSvg(avatarData.hair, hair.color)}
        {/* Accessory */}
        {renderAccessorySvg(avatarData.accessory)}
      </svg>
    );
  };

  const DIVER_FIRST = [
    'Captain', 'Coral', 'Bubbles', 'Finley', 'Marina',
    'Reef', 'Splash', 'Pearl', 'Tidey', 'Sandy',
    'Shelly', 'Wave', 'Neptune', 'Starfish', 'Drift',
    'Oceana', 'Kelpy', 'Aqua', 'Flipper', 'Sunny',
  ];
  const DIVER_LAST = [
    'McSplash', 'Deepdive', 'Seabreeze', 'Wavecatcher', 'Bubblemaker',
    'Coralton', 'Shellsworth', 'Tidewatcher', 'Sandcastle', 'Reefrunner',
    'Pearlhunter', 'Stargazer', 'Seastorm', 'Driftwood', 'Kelpsworth',
    'Oceanwhirl', 'Surfstone', 'Foamrider', 'Depthfinder', 'Splashton',
  ];
  const getRandomDiverName = () => {
    const first = DIVER_FIRST[Math.floor(Math.random() * DIVER_FIRST.length)];
    const last = DIVER_LAST[Math.floor(Math.random() * DIVER_LAST.length)];
    return `${first} ${last}`;
  };

  // ═══════════════════════════════════════════════════════════════
  // HOME SCREEN
  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  // ROLE SELECTION (First-time only — Student or Teacher?)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'home' && !userRole) {
    return (
      <div className="ocean-gradient flex flex-col items-center justify-center text-center px-6 relative overflow-hidden min-h-screen">
        {/* Floating bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingBubbles.map((b, i) => (
            <div
              key={i}
              className={`bubble ${b.size} ${b.delay} ${b.opacity}`}
              style={{ left: b.left, bottom: '-5%' }}
            />
          ))}
        </div>

        {/* Popping bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroBubblePops.slice(0, 4).map((b, i) => (
            <div
              key={`pop-${i}`}
              className="bubble-pop"
              style={{ left: b.left, top: b.top, width: b.size, height: b.size, animationDelay: `${b.delay}s` }}
            />
          ))}
        </div>

        {/* Corner post hexagons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroCornerPostFloaters.map((cp, i) => (
            <div
              key={`hex-${i}`}
              className="hex-floater"
              style={{ left: cp.left, top: cp.top, width: cp.size, height: cp.size, animationDelay: `${cp.delay}s` }}
            >
              <svg viewBox="0 0 100 100" className="hex-shape" style={{ filter: `drop-shadow(0 0 8px ${cp.color})` }}>
                <polygon points="50,2 95,25 95,75 50,98 5,75 5,25" fill={cp.color} stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
              </svg>
              <span className="hex-icon">{cp.icon}</span>
            </div>
          ))}
        </div>

        {/* Buoy Buddies */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroBuoyBuddies.map((b, i) => (
            <div
              key={`buoy-${i}`}
              className="buoy-buddy"
              style={{ left: b.left, top: b.top, animationDelay: `${b.delay}s` }}
            >
              <div className="buoy-buddy-label">{b.label}</div>
              <svg width="52" height="52" viewBox="0 0 52 52">
                {/* Outer ring */}
                <circle cx="26" cy="26" r="22" fill="none" stroke={b.ring} strokeWidth="9" opacity="0.85" />
                {/* Inner ring segments highlight */}
                <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="4"
                  strokeDasharray="14 20" strokeLinecap="round" />
                {/* Center face circle */}
                <circle cx="26" cy="26" r="12" fill={b.color} opacity="0.9" />
                <text x="26" y="30" textAnchor="middle" fontSize="13">{b.emoji}</text>
              </svg>
            </div>
          ))}
        </div>

        {/* Light rays */}
        <div className="light-ray" style={{ top: '-10%', left: '10%', width: '120px', height: '60vh' }} />
        <div className="light-ray" style={{ top: '-10%', right: '15%', width: '80px', height: '50vh' }} />

        <div className="relative z-10 flex flex-col items-center w-full max-w-sm">
          {/* Logo */}
          <h1 className="text-5xl font-black tracking-tight text-white drop-shadow-lg animate-fade-in">
            <span className="logo-4tz relative inline-block">
              <span className="logo-glow" />
              <span className="logo-char">4</span>
              <span className="logo-char">T</span>
              <span className="logo-char">Z</span>
            </span>
            {' '}Adventure Buddies
          </h1>
          <p className="text-xl text-white/80 mt-4 font-medium animate-fade-in-delay">
            A Catch &amp; Build Experience
          </p>

          <p className="text-white/60 text-sm mt-8 mb-4 animate-fade-in-delay-2">
            Who's diving in today?
          </p>

          {/* Role cards */}
          <div className="w-full space-y-4 animate-fade-in-delay-2">
            {/* Student card */}
            <button
              onClick={() => {
                setUserRole('student');
                setTeacherMode(false);
              }}
              className="w-full glass rounded-3xl p-6 flex items-center gap-4 transition-all duration-300 active:scale-95 hover:bg-white/20 hover:-translate-y-1 hover:shadow-2xl text-left"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-3xl">🤿</span>
              </div>
              <div>
                <p className="text-white font-bold text-lg">I'm an Explorer</p>
                <p className="text-white/60 text-sm">Students — create art, complete missions, build your portfolio</p>
              </div>
            </button>

            {/* Teacher/Grandee card */}
            <button
              onClick={() => {
                setUserRole('teacher');
                setTeacherMode(true);
              }}
              className="w-full glass rounded-3xl p-6 flex items-center gap-4 transition-all duration-300 active:scale-95 hover:bg-white/20 hover:-translate-y-1 hover:shadow-2xl text-left"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-3xl">🧭</span>
              </div>
              <div>
                <p className="text-white font-bold text-lg">I'm a Guide</p>
                <p className="text-white/60 text-sm">Teachers, parents &amp; grandparents — track progress, add observations</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'home') {
    // --- Returning user: has avatar ---
    if (avatar) {
      const progressPct = Math.max((completedCornerPosts / 6) * 100, 0);
      return (
        <div className="ocean-gradient flex flex-col items-center justify-center text-center px-6 relative overflow-hidden">
          {/* Floating bubbles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {floatingBubbles.map((b, i) => (
              <div
                key={i}
                className={`bubble ${b.size} ${b.delay} ${b.opacity}`}
                style={{ left: b.left, bottom: '-5%' }}
              />
            ))}
          </div>

          {/* Popping bubbles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {heroBubblePops.map((b, i) => (
              <div
                key={`pop-${i}`}
                className="bubble-pop"
                style={{
                  left: b.left,
                  top: b.top,
                  width: b.size,
                  height: b.size,
                  animationDelay: `${b.delay}s`,
                }}
              />
            ))}
          </div>

          {/* Corner post hexagons */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {heroCornerPostFloaters.map((cp, i) => (
              <div
                key={`hex-${i}`}
                className="hex-floater"
                style={{
                  left: cp.left,
                  top: cp.top,
                  width: cp.size,
                  height: cp.size,
                  animationDelay: `${cp.delay}s`,
                }}
              >
                <svg viewBox="0 0 100 100" className="hex-shape" style={{ filter: `drop-shadow(0 0 8px ${cp.color})` }}>
                  <polygon
                    points="50,2 95,25 95,75 50,98 5,75 5,25"
                    fill={cp.color}
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                  />
                </svg>
                <span className="hex-icon">{cp.icon}</span>
              </div>
            ))}
          </div>

          {/* Ocean shapes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {heroOceanShapes.map((s, i) => (
              <div
                key={`ocean-${i}`}
                className="ocean-shape"
                style={{
                  left: s.left,
                  top: s.top,
                  animationDelay: `${s.delay}s`,
                  fontSize: s.size,
                }}
              >
                {s.type === 'starfish' && '⭐'}
                {s.type === 'shell' && '🐚'}
                {s.type === 'wave' && '🌊'}
                {s.type === 'coral' && '🪸'}
              </div>
            ))}
          </div>

          {/* Buoy Buddies */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {heroBuoyBuddies.map((b, i) => (
              <div
                key={`buoy-${i}`}
                className="buoy-buddy"
                style={{ left: b.left, top: b.top, animationDelay: `${b.delay}s` }}
              >
                <div className="buoy-buddy-label">{b.label}</div>
                <svg width="52" height="52" viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="22" fill="none" stroke={b.ring} strokeWidth="9" opacity="0.85" />
                  <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="4"
                    strokeDasharray="14 20" strokeLinecap="round" />
                  <circle cx="26" cy="26" r="12" fill={b.color} opacity="0.9" />
                  <text x="26" y="30" textAnchor="middle" fontSize="13">{b.emoji}</text>
                </svg>
              </div>
            ))}
          </div>

          {/* Light rays */}
          <div className="light-ray" style={{ top: '-10%', left: '10%', width: '120px', height: '60vh' }} />
          <div className="light-ray" style={{ top: '-10%', right: '15%', width: '80px', height: '50vh' }} />

          <div className="relative z-10 flex flex-col items-center w-full max-w-sm">
            {/* Glass card for returning user */}
            <div className="glass rounded-3xl p-8 w-full animate-slide-up">
              {/* Avatar in diving helmet + progress ring */}
              <div className="flex flex-col items-center mb-6">
                <button
                  className="relative mb-4 group cursor-pointer transition-all duration-300 active:scale-95"
                  onClick={() => {
                    if (typeof avatar === 'object' && avatar.type === 'custom') {
                      setBuilderAvatar({ ...avatar });
                    }
                    setBuilderStep(1);
                    setShowOnboarding(true);
                    setAvatar(null);
                  }}
                  aria-label="Edit avatar"
                >
                  <div className="diving-helmet">
                    <div className="rivet rivet-top" />
                    <div className="rivet rivet-bottom" />
                    <div className="rivet rivet-left" />
                    <div className="rivet rivet-right" />
                    {typeof avatar === 'string' && avatar.startsWith('data:') ? (
                      <img
                        src={avatar}
                        alt="Your Avatar"
                        className="w-24 h-24 rounded-full object-cover shadow-lg animate-scale-in"
                      />
                    ) : typeof avatar === 'object' && avatar.type === 'custom' ? (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-b from-cyan-800/50 to-blue-900/50 flex items-center justify-center shadow-lg animate-scale-in overflow-hidden">
                        <DiverAvatar avatarData={avatar} size={96} />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-5xl shadow-lg animate-scale-in">
                        {avatar}
                      </div>
                    )}
                  </div>
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-white/50 group-hover:text-white/80 transition-colors">tap to edit</span>
                </button>
                <h1 className="text-3xl font-black tracking-tight text-white drop-shadow-lg animate-fade-in">
                  <span className="logo-4tz relative inline-block">
                    <span className="logo-glow" />
                    <span className="logo-char">4</span>
                    <span className="logo-char">T</span>
                    <span className="logo-char">Z</span>
                  </span>
                </h1>
                <p className="text-white/80 text-lg font-medium mt-1 animate-fade-in-delay">
                  Welcome back, {avatarName || 'Explorer'}!
                </p>
              </div>

              {/* Circular progress ring */}
              <div className="flex flex-col items-center mb-6 animate-fade-in-delay">
                <ProgressRing progress={progressPct} size={90} strokeWidth={7} />
                <p className="text-white/80 text-xs uppercase tracking-widest mt-2 font-medium">
                  Corner Posts Explored
                </p>
              </div>

              {/* Today's Lessons card */}
              <button
                onClick={() => {
                  setCurrentScreen('lessonPicker');
                  if (textToSpeech) speak("Pick a lesson to start!");
                }}
                className="w-full mb-3 p-4 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5 text-left animate-fade-in-delay"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{'\u{1F4DA}'}</span>
                  <div>
                    <p className="text-white text-lg font-bold">Today's Lessons</p>
                    <p className="text-white/80 text-xs">{lessonPlans.length + customLessons.length} book adventures waiting</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setCurrentScreen('cornerPosts');
                  if (textToSpeech) speak('Choose a corner post to explore!');
                }}
                className="w-full py-4 bg-gradient-to-r from-cyan-600 via-blue-600 to-teal-500 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5"
              >
                Continue Diving
              </button>

              <button
                onClick={() => setCurrentScreen('portfolio')}
                className="mt-3 w-full py-4 bg-white/10 border border-white/20 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:bg-white/20 hover:-translate-y-0.5 animate-fade-in-delay"
              >
                {'\u{1F4F8}'} My Collection{portfolioEntries.length > 0 && ` (${portfolioEntries.length})`}
              </button>

              {!teacherMode && (
                <button
                  onClick={() => setCurrentScreen('aquarium')}
                  className="mt-3 w-full py-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:bg-cyan-500/30 hover:-translate-y-0.5 animate-fade-in-delay relative"
                >
                  {'\u{1F41F}'} My Aquarium
                  {aquariumItems.filter(i => !i.placed).length > 0 && (
                    <span className="absolute top-3 right-3 w-6 h-6 bg-amber-400 rounded-full text-xs font-black text-amber-900 flex items-center justify-center animate-pulse-soft">
                      {aquariumItems.filter(i => !i.placed).length}
                    </span>
                  )}
                </button>
              )}

              <button
                onClick={() => setCurrentScreen('vocabularyList')}
                className="mt-3 w-full py-4 bg-white/10 border border-white/20 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:bg-white/20 hover:-translate-y-0.5 animate-fade-in-delay"
              >
                {'\u{1F4D6}'} {teacherMode ? 'Master Vocabulary' : "Diver's Dictionary"}
              </button>

              <button
                onClick={() => {
                  setCurrentScreen('shareProgress');
                  if (textToSpeech) speak('Share your progress with your teacher!');
                }}
                className="mt-3 w-full py-4 bg-white/10 border border-white/20 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:bg-white/20 hover:-translate-y-0.5 animate-fade-in-delay"
              >
                {'\u{1F4E4}'} Share My Progress
              </button>

              {completedMissions.length > 0 && (
                <button
                  onClick={() => setCurrentScreen('accountability')}
                  className="mt-4 text-white/70 underline text-sm font-medium animate-fade-in-delay-2 transition-all duration-300 hover:text-white"
                >
                  View Your Journey Map
                </button>
              )}

              {completedMissions.length > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset your adventure? This will clear all your progress.')) {
                      setCompletedMissions([]);
                    }
                  }}
                  className="mt-4 text-white/40 text-xs transition-all duration-300 hover:text-white/70"
                >
                  Reset Adventure
                </button>
              )}
            </div>
          </div>
          {renderActionButtons()}
          {showSettings && renderSettings()}
          {showHelp && renderHelpPanel()}
        </div>
      );
    }

    // --- New user: no avatar ---
    return (
      <div className="ocean-gradient flex flex-col items-center justify-center text-center px-6 relative overflow-hidden">
        {/* Floating bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingBubbles.map((b, i) => (
            <div
              key={i}
              className={`bubble ${b.size} ${b.delay} ${b.opacity}`}
              style={{ left: b.left, bottom: '-5%' }}
            />
          ))}
        </div>

        {/* Popping bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroBubblePops.map((b, i) => (
            <div
              key={`pop-${i}`}
              className="bubble-pop"
              style={{
                left: b.left,
                top: b.top,
                width: b.size,
                height: b.size,
                animationDelay: `${b.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Corner post hexagons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroCornerPostFloaters.map((cp, i) => (
            <div
              key={`hex-${i}`}
              className="hex-floater"
              style={{
                left: cp.left,
                top: cp.top,
                width: cp.size,
                height: cp.size,
                animationDelay: `${cp.delay}s`,
              }}
            >
              <svg viewBox="0 0 100 100" className="hex-shape" style={{ filter: `drop-shadow(0 0 8px ${cp.color})` }}>
                <polygon
                  points="50,2 95,25 95,75 50,98 5,75 5,25"
                  fill={cp.color}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="2"
                />
              </svg>
              <span className="hex-icon">{cp.icon}</span>
            </div>
          ))}
        </div>

        {/* Ocean shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroOceanShapes.map((s, i) => (
            <div
              key={`ocean-${i}`}
              className="ocean-shape"
              style={{
                left: s.left,
                top: s.top,
                animationDelay: `${s.delay}s`,
                fontSize: s.size,
              }}
            >
              {s.type === 'starfish' && '⭐'}
              {s.type === 'shell' && '🐚'}
              {s.type === 'wave' && '🌊'}
              {s.type === 'coral' && '🪸'}
            </div>
          ))}
        </div>

        {/* Buoy Buddies */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {heroBuoyBuddies.map((b, i) => (
            <div
              key={`buoy-${i}`}
              className="buoy-buddy"
              style={{ left: b.left, top: b.top, animationDelay: `${b.delay}s` }}
            >
              <div className="buoy-buddy-label">{b.label}</div>
              <svg width="52" height="52" viewBox="0 0 52 52">
                <circle cx="26" cy="26" r="22" fill="none" stroke={b.ring} strokeWidth="9" opacity="0.85" />
                <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="4"
                  strokeDasharray="14 20" strokeLinecap="round" />
                <circle cx="26" cy="26" r="12" fill={b.color} opacity="0.9" />
                <text x="26" y="30" textAnchor="middle" fontSize="13">{b.emoji}</text>
              </svg>
            </div>
          ))}
        </div>

        {/* Light rays */}
        <div className="light-ray" style={{ top: '-10%', left: '10%', width: '120px', height: '60vh' }} />
        <div className="light-ray" style={{ top: '-10%', right: '15%', width: '80px', height: '50vh' }} />

        <div className="relative z-10 flex flex-col items-center w-full max-w-sm">
          {!showOnboarding ? (
            <>
              {/* Phase 1: Hero */}
              <h1 className="text-5xl font-black tracking-tight text-white drop-shadow-lg animate-fade-in">
                <span className="logo-4tz relative inline-block">
                  <span className="logo-glow" />
                  <span className="logo-char">4</span>
                  <span className="logo-char">T</span>
                  <span className="logo-char">Z</span>
                </span>
                {' '}Adventure Buddies
              </h1>
              <p className="text-xl text-white/80 mt-4 font-medium opacity-start animate-fade-in-delay">
                A Catch &amp; Build Experience
              </p>

              <button
                onClick={() => setShowOnboarding(true)}
                className="mt-10 w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-700 backdrop-blur-xl border border-white/30 text-white text-xl font-bold rounded-2xl shadow-lg animate-fade-in-delay-2 opacity-start transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5"
              >
                Start Your Adventure
              </button>
            </>
          ) : (
            <>
              {/* Live avatar preview in diving helmet */}
              <div className="mb-4 animate-scale-in">
                <div className="diving-helmet">
                  <div className="rivet rivet-top" />
                  <div className="rivet rivet-bottom" />
                  <div className="rivet rivet-left" />
                  <div className="rivet rivet-right" />
                  {typeof avatar === 'string' && avatar.startsWith('data:') ? (
                    <img src={avatar} alt="Selected" className="w-20 h-20 rounded-full object-cover shadow-lg" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-b from-cyan-800/50 to-blue-900/50 flex items-center justify-center shadow-lg overflow-hidden">
                      <DiverAvatar avatarData={builderAvatar} size={80} />
                    </div>
                  )}
                </div>
              </div>

              {/* Character Builder Card */}
              <div className="w-full glass rounded-3xl p-6 animate-slide-up">
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-0 mb-5">
                  {['Character', 'Hair', 'Gear'].map((label, i) => {
                    const stepNum = i + 1;
                    const isActive = builderStep === stepNum;
                    const isDone = builderStep > stepNum;
                    return (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <div className={`w-8 h-1 rounded-full transition-all duration-500 ${isDone ? 'bg-cyan-400' : 'bg-white/20'}`} />
                        )}
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                            isActive ? 'bg-cyan-400 text-white scale-110 shadow-lg shadow-cyan-400/30' :
                            isDone   ? 'bg-cyan-400/60 text-white' :
                                       'bg-white/15 text-white/50'
                          }`}>
                            {isDone ? '\u2713' : stepNum}
                          </div>
                          <span className={`text-xs mt-1 transition-all duration-300 ${isActive ? 'text-white font-bold' : 'text-white/40'}`}>{label}</span>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Step content with animation on change */}
                <div key={builderStep} className="animate-fade-in">
                  {/* Step 1: Skin Tone */}
                  {builderStep === 1 && (
                    <>
                      <h2 className="text-xl font-bold text-white mb-4 text-center">Choose Your Explorer</h2>
                      <div className="flex justify-center gap-3 mb-4">
                        {AVATAR_BASES.map((base, i) => (
                          <button
                            key={i}
                            onClick={() => setBuilderAvatar(prev => ({...prev, base: i}))}
                            className={`w-14 h-14 rounded-full transition-all duration-300 active:scale-90 ${
                              builderAvatar.base === i
                                ? 'ring-[3px] ring-cyan-300 scale-110 shadow-lg shadow-cyan-300/20'
                                : 'hover:scale-105'
                            }`}
                            style={{ background: base.skin }}
                            aria-label={base.label}
                          >
                            <svg viewBox="0 0 100 100" className="w-full h-full">
                              <circle cx="40" cy="42" r="4" fill="#1a1a1a" />
                              <circle cx="60" cy="42" r="4" fill="#1a1a1a" />
                              <path d="M38,58 Q50,68 62,58" fill="none" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Step 2: Hair */}
                  {builderStep === 2 && (
                    <>
                      <h2 className="text-xl font-bold text-white mb-4 text-center">Choose Your Style</h2>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {AVATAR_HAIRS.map((hair, i) => (
                          <button
                            key={i}
                            onClick={() => setBuilderAvatar(prev => ({...prev, hair: i}))}
                            className={`aspect-square rounded-2xl flex flex-col items-center justify-center p-2 transition-all duration-300 active:scale-90 ${
                              builderAvatar.hair === i
                                ? 'bg-white/30 ring-2 ring-cyan-300 shadow-lg scale-105'
                                : 'bg-white/10 hover:bg-white/20 hover:scale-105'
                            }`}
                            aria-label={hair.label}
                          >
                            <svg viewBox="0 0 100 100" className="w-12 h-12">
                              <circle cx="50" cy="48" r="28" fill="#d4d4d8" opacity="0.3" />
                              {renderHairSvg(i, hair.color)}
                            </svg>
                            <span className="text-white/70 text-[10px] mt-1 font-medium leading-tight">{hair.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Step 3: Accessories + Name + Dive In */}
                  {builderStep === 3 && (
                    <>
                      <h2 className="text-xl font-bold text-white mb-4 text-center">Choose Your Gear</h2>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {AVATAR_ACCESSORIES.map((acc, i) => (
                          <button
                            key={i}
                            onClick={() => setBuilderAvatar(prev => ({...prev, accessory: i}))}
                            className={`aspect-square rounded-2xl flex flex-col items-center justify-center p-2 transition-all duration-300 active:scale-90 ${
                              builderAvatar.accessory === i
                                ? 'bg-white/30 ring-2 ring-cyan-300 shadow-lg scale-105'
                                : 'bg-white/10 hover:bg-white/20 hover:scale-105'
                            }`}
                            aria-label={acc.label}
                          >
                            <svg viewBox="0 0 100 100" className="w-12 h-12">
                              <circle cx="50" cy="48" r="28" fill="#d4d4d8" opacity="0.3" />
                              {i === 0 ? (
                                <text x="50" y="52" textAnchor="middle" fill="white" opacity="0.5" style={{ fontSize: '18px' }}>-</text>
                              ) : renderAccessorySvg(i)}
                            </svg>
                            <span className="text-white/70 text-[10px] mt-1 font-medium leading-tight">{acc.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Name input with dice button */}
                      <div className="relative mt-2 mb-2">
                        <input
                          type="text"
                          value={avatarName}
                          onChange={(e) => {
                            const sanitized = e.target.value.slice(0, 50).replace(/<\/?[^>]+(>|$)/g, '');
                            setAvatarName(sanitized);
                          }}
                          className="w-full p-4 pr-14 bg-white/10 border-2 border-white/20 rounded-2xl text-lg text-center text-white placeholder-white/40 focus:border-cyan-300 focus:outline-none transition-all duration-300"
                          placeholder="Name your diver"
                          maxLength={50}
                          aria-label="Avatar name"
                        />
                        <button
                          onClick={() => setAvatarName(getRandomDiverName())}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-cyan-500/30 flex items-center justify-center text-xl transition-all duration-300 active:scale-90 hover:bg-cyan-500/50"
                          aria-label="Generate random diver name"
                        >
                          🎲
                        </button>
                      </div>

                      {/* Dive In button */}
                      <button
                        onClick={() => {
                          const finalAvatar = builderAvatar;
                          const finalName = avatarName || getRandomDiverName();
                          setAvatar(finalAvatar);
                          setAvatarName(finalName);
                          // First-time: go to sensory quiz, then first lesson
                          // Returning edit: go home
                          if (!sensoryProfile) {
                            setCurrentScreen('sensoryProfile');
                          } else {
                            setCurrentScreen('home');
                          }
                        }}
                        className="mt-4 w-full py-4 bg-gradient-to-r from-cyan-600 via-blue-600 to-teal-500 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5"
                      >
                        Dive In!
                      </button>
                    </>
                  )}
                </div>

                {/* Step navigation (steps 1 & 2) */}
                {builderStep < 3 && (
                  <div className="flex gap-3 mt-4">
                    {builderStep > 1 && (
                      <button
                        onClick={() => setBuilderStep(s => s - 1)}
                        className="flex-1 py-3 bg-white/10 text-white font-bold rounded-2xl transition-all duration-300 active:scale-95"
                      >
                        Back
                      </button>
                    )}
                    <button
                      onClick={() => setBuilderStep(s => s + 1)}
                      className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-2xl transition-all duration-300 active:scale-95 shadow-lg"
                    >
                      Next
                    </button>
                  </div>
                )}

                {/* Step 3 back button */}
                {builderStep === 3 && (
                  <button
                    onClick={() => setBuilderStep(s => s - 1)}
                    className="mt-2 w-full py-3 bg-white/10 text-white font-bold rounded-2xl transition-all duration-300 active:scale-95"
                  >
                    Back
                  </button>
                )}

                {/* De-emphasized photo upload */}
                <div className="mt-4 text-center">
                  <label className="cursor-pointer text-white/40 text-xs underline hover:text-white/60 transition-colors">
                    Or upload a photo instead
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        handleAvatarUpload(e);
                        setBuilderStep(3);
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // CORNER POSTS SCREEN — Bento Grid
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'cornerPosts') {
    const staggerClasses = ['animate-stagger-1', 'animate-stagger-2', 'animate-stagger-3', 'animate-stagger-4', 'animate-stagger-5', 'animate-stagger-6'];
    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button — glass circle */}
          <button
            onClick={() => setCurrentScreen('home')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Home"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-6 animate-fade-in`}>
            <span className="logo-4tz relative inline-block mr-2" style={{ fontSize: '0.8em' }}>
              <span className="logo-glow" />
              <span className="logo-char">4</span>
              <span className="logo-char">T</span>
              <span className="logo-char">Z</span>
            </span>
            Corner Posts
          </h1>

          {/* Bento grid — first item spans full width */}
          <div className="grid grid-cols-2 gap-4">
            {cornerPosts.map((post, idx) => {
              const isCompleted = post.missions.some(mission => completedMissions.includes(mission.id));
              const isFirst = idx === 0;
              return (
                <button
                  key={post.id}
                  onClick={() => {
                    setSelectedCornerPost(post);
                    setCurrentScreen('missions');
                    if (textToSpeech) {
                      speak(`${post.name} corner post. ${post.description}`);
                    }
                  }}
                  className={`${isFirst ? 'col-span-2' : ''} glass rounded-3xl p-6 flex flex-col items-center text-center transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 active:scale-95 opacity-start ${staggerClasses[idx] || 'animate-stagger-6'}`}
                >
                  <div className="text-4xl mb-3">{post.icon}</div>
                  <h3 className={`${simpleMode ? 'text-xl' : 'text-lg'} font-bold text-white`}>{post.name}</h3>
                  {!simpleMode && <p className="text-white/60 text-sm mt-1 leading-snug">{post.description}</p>}
                  {isCompleted && (
                    <div className="mt-3 px-3 py-1 rounded-full bg-green-500/20 border border-green-400/30 text-green-300 text-xs font-bold flex items-center gap-1">
                      <span>✓</span> Explored
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Dive Buddies button */}
          <button
            onClick={() => setCurrentScreen('diveBuddies')}
            className="w-full glass rounded-2xl p-4 mt-4 flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 hover:bg-white/20 animate-fade-in"
          >
            <Users size={20} className="text-cyan-300" />
            <span className="text-white font-semibold">Dive Buddies</span>
            {buddies.length > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full bg-cyan-500/30 text-cyan-200 text-xs font-bold">{buddies.length}</span>
            )}
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // DIVE BUDDIES SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'diveBuddies') {
    return (
      <div className="mesh-gradient flex flex-col items-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('cornerPosts')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <div className="text-center mb-6 animate-fade-in">
            <div className="text-4xl mb-2">🤿</div>
            <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-1`}>Dive Buddies</h1>
            <p className="text-white/60 text-sm">Find your crew before diving in!</p>
          </div>

          {/* Your QR Code — with sonar pulse */}
          <div className="glass rounded-3xl p-6 mb-4 animate-fade-in">
            <p className="text-white/80 text-sm font-medium text-center mb-3">Your Dive Signal</p>
            <div className="relative flex justify-center">
              {/* Sonar rings */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="sonar-ring w-48 h-48 animate-sonar-1" />
                <div className="sonar-ring absolute w-48 h-48 animate-sonar-2" />
              </div>
              <div className="bg-white rounded-2xl p-4 relative z-10 shadow-lg" style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.2)' }}>
                <QRCodeSVG value={encodeBuddyQR()} size={150} fgColor="#0c4a6e" />
              </div>
            </div>
            <p className="text-white/50 text-xs text-center mt-3">Show this to a friend to connect!</p>
          </div>

          {/* Scan & share actions */}
          <div className="flex gap-2 mb-4 animate-fade-in">
            <button
              onClick={() => navigator.mediaDevices ? startLiveScan('buddy') : buddyScanRef.current?.click()}
              className="flex-1 glass rounded-2xl p-4 flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 hover:bg-white/20"
            >
              <Camera size={20} className="text-cyan-300" />
              <span className="text-white font-semibold">Scan Buddy</span>
            </button>
            <button
              onClick={copyBuddyCode}
              className={`px-4 rounded-2xl flex items-center justify-center gap-1 transition-all duration-300 active:scale-95 ${
                codeCopied
                  ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-300'
                  : 'glass hover:bg-white/20 text-white'
              }`}
            >
              {codeCopied ? <Check size={16} /> : <Copy size={16} />}
              <span className="text-xs font-medium">{codeCopied ? 'Copied' : 'Code'}</span>
            </button>
          </div>

          {/* Paste buddy code */}
          <button
            onClick={() => { setShowPasteInput(!showPasteInput); setPasteCodeInput(''); }}
            className="w-full text-center text-white/40 text-xs mb-3 transition-all hover:text-white/60"
          >
            {showPasteInput ? 'Cancel' : 'Have a buddy code? Tap here'}
          </button>
          {showPasteInput && (
            <div className="glass rounded-2xl p-4 mb-4 animate-scale-in">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pasteCodeInput}
                  onChange={(e) => setPasteCodeInput(e.target.value)}
                  placeholder="4TZ-B:..."
                  className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400/50"
                />
                <button
                  onClick={() => {
                    if (handlePasteCode(pasteCodeInput.trim())) {
                      setPasteCodeInput('');
                      setShowPasteInput(false);
                    } else {
                      showScanToast('error', 'Invalid buddy code. Try again!');
                    }
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-sm rounded-xl transition-all active:scale-95"
                >
                  Add
                </button>
              </div>
            </div>
          )}
          <input
            ref={buddyScanRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleBuddyScan}
          />

          {/* Buddy List */}
          {buddies.length > 0 && (
            <div className="glass rounded-3xl p-4 mb-4 animate-fade-in">
              <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-3">Your Dive Crew ({buddies.length})</p>
              <div className="space-y-2">
                {buddies.map((buddy, idx) => (
                  <div key={buddy.addedAt || idx} className="flex items-center gap-3 bg-white/5 rounded-2xl p-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      {buddy.avatar.type === 'custom' ? (
                        <DiverAvatar avatarData={buddy.avatar} size={36} />
                      ) : (
                        <Users size={18} className="text-cyan-300" />
                      )}
                    </div>
                    <span className="text-white text-sm font-medium flex-1 truncate">{buddy.name}</span>
                    <button
                      onClick={() => setBuddies(prev => prev.filter((_, i) => i !== idx))}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-white/10 transition-all"
                      aria-label={`Remove ${buddy.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={() => setCurrentScreen('home')}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-lg shadow-lg shadow-cyan-500/25 transition-all duration-300 active:scale-95 hover:shadow-xl hover:shadow-cyan-500/30 animate-fade-in"
          >
            Start Exploring! 🚀
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
        {renderLiveScanner()}
        {renderScanFeedbackToast()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // MISSIONS SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'missions' && selectedCornerPost) {
    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('cornerPosts')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Corner Posts"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <div className="mb-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-3xl">{selectedCornerPost.icon}</span>
              <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
                {selectedCornerPost.name}
              </h1>
            </div>
            {!simpleMode && <p className="text-white/60 text-sm ml-12">{selectedCornerPost.description}</p>}
          </div>

          {/* Lesson Guide — collapsible card */}
          {!simpleMode && lessonData[selectedCornerPost.id] && (
            <details className="glass-surface rounded-2xl mb-6 overflow-hidden animate-fade-in">
              <summary className="p-5 cursor-pointer font-bold text-gray-700 text-sm flex items-center gap-2 select-none hover:bg-white/30 transition-colors">
                <span className="text-purple-500">📚</span> Lesson Guide
              </summary>
              <div className="px-5 pb-5 space-y-4">
                {/* Essential Questions */}
                <div>
                  <h3 className="font-bold text-gray-800 text-sm mb-2">Essential Questions:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    {lessonData[selectedCornerPost.id].essentialQuestions.map((q, i) => (
                      <li key={i} className="leading-snug">{q}</li>
                    ))}
                  </ol>
                </div>

                {/* Guide Prompt */}
                <div className="bg-purple-50 border-l-4 border-purple-400 rounded-r-xl p-3">
                  <p className="text-sm text-gray-700 italic">
                    💡 {lessonData[selectedCornerPost.id].guidePrompt}
                  </p>
                </div>

                {/* Vocabulary */}
                <div>
                  <h3 className="font-bold text-gray-800 text-sm mb-2">Key Words:</h3>
                  <div className="flex flex-wrap gap-2">
                    {lessonData[selectedCornerPost.id].vocabulary.map((vocab, i) => (
                      <details key={i} className="inline-block">
                        <summary className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition-colors select-none">
                          {vocab.term}
                        </summary>
                        <div className="mt-2 p-2 bg-white rounded-lg shadow-sm text-xs text-gray-600 max-w-xs">
                          {vocab.definition}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>

                {/* Resources */}
                {lessonData[selectedCornerPost.id].resources.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm mb-2">Learn More:</h3>
                    <div className="space-y-2">
                      {lessonData[selectedCornerPost.id].resources.map((resource, i) => (
                        <a
                          key={i}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-blue-700 text-sm font-medium"
                        >
                          <Play size={16} />
                          {resource.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="space-y-4">
            {selectedCornerPost.missions.map((mission, idx) => {
              const isCompleted = completedMissions.includes(mission.id);

              // Check if mission matches sensory profile
              let sensoryMatch = true;
              if (sensoryProfile) {
                if (sensoryProfile.movement === 'minimal' && mission.sensoryNotes.movement.includes('Active')) {
                  sensoryMatch = false;
                }
                if (sensoryProfile.sound === 'quiet' && mission.sensoryNotes.noise.includes('loud')) {
                  sensoryMatch = false;
                }
                if (sensoryProfile.touch === 'avoids' && mission.sensoryNotes.touch.includes('touching different textures')) {
                  sensoryMatch = false;
                }
              }

              return (
                <div
                  key={mission.id}
                  className={`glass-surface rounded-2xl p-6 border-l-4 ${selectedCornerPost.accent} transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 opacity-start ${
                    idx === 0 ? 'animate-stagger-1' : 'animate-stagger-2'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className={`${simpleMode ? 'text-xl' : 'text-lg'} font-bold text-gray-900`}>
                      {mission.name}
                    </h3>
                    {isCompleted && (
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                        ✓ Done
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{mission.description}</p>

                  {!sensoryMatch && !isCompleted && (
                    <div className="mb-3 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium inline-block">
                      May need adaptations
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setSelectedMission(mission);
                      setCurrentScreen('missionDetail');
                      if (textToSpeech) {
                        speak(`${mission.name}. ${mission.description}`);
                      }
                    }}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl transition-all duration-300 active:scale-95 hover:shadow-lg"
                  >
                    {isCompleted ? 'Do Again' : 'Start Mission'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // MISSION DETAIL SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'missionDetail' && selectedMission) {
    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('missions')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Missions"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2 animate-fade-in`}>
            {selectedMission.name}
          </h1>
          <p className="text-white/60 text-sm mb-6 animate-fade-in">{selectedMission.description}</p>

          {/* Steps as glass chips */}
          <div className="glass-surface rounded-3xl p-8 mb-6 animate-slide-up">
            <h2 className="text-xs uppercase tracking-widest text-gray-400 mb-4 font-bold">Steps</h2>
            <div className="space-y-3">
              {selectedMission.visualSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-base font-medium leading-snug">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sensory Notes — collapsible glass card */}
          {!simpleMode && (
            <details className="glass-surface rounded-2xl mb-4 overflow-hidden animate-fade-in-delay">
              <summary className="p-5 cursor-pointer font-bold text-gray-700 text-sm flex items-center gap-2 select-none">
                <span className="text-amber-500">⚡</span> Sensory Notes
              </summary>
              <div className="px-5 pb-5 space-y-2 text-sm text-gray-600">
                <p><span className="font-semibold text-gray-700">Movement:</span> {selectedMission.sensoryNotes.movement}</p>
                <p><span className="font-semibold text-gray-700">Sound:</span> {selectedMission.sensoryNotes.noise}</p>
                <p><span className="font-semibold text-gray-700">Touch:</span> {selectedMission.sensoryNotes.touch}</p>
              </div>
            </details>
          )}

          {/* Adaptations info card */}
          <div className="glass rounded-2xl p-5 mb-8 animate-fade-in-delay-2">
            <h3 className="text-white/90 font-bold text-sm mb-2 flex items-center gap-2">
              <span>💡</span> Ways to Adapt
            </h3>
            <ul className="text-white/60 text-sm space-y-1">
              <li>• Take your time — go at your own pace</li>
              <li>• It's okay to change or skip parts</li>
              <li>• Add your own ideas to make it more fun</li>
            </ul>
          </div>

          {/* Complete Mission button */}
          <button
            onClick={() => completeMission(selectedMission.id)}
            className={`w-full py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white ${simpleMode ? 'text-2xl' : 'text-xl'} font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5`}
          >
            Complete Mission
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // BLUEPRINT SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'blueprint' && selectedMission) {
    const handleBlueprintSketch = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload an image file (JPEG, PNG, GIF, or WEBP).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image is too large. Please choose an image smaller than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        compressImage(ev.target.result).then(compressed => {
          setBlueprintSketch(compressed);
        }).catch(() => {
          alert('Error processing image. Please try again.');
        });
      };
      reader.onerror = () => alert('Error reading file. Please try again.');
      reader.readAsDataURL(file);
    };

    const handleBlueprintSubmit = () => {
      const blueprintData = {
        text: blueprintText,
        sketch: blueprintSketch,
        date: Date.now()
      };
      setBlueprints(prev => ({ ...prev, [selectedMission.id]: blueprintData }));
      completeMission(selectedMission.id);
    };

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('missionDetail')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Mission Details"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2 animate-fade-in`}>
            Make Your Blueprint
          </h1>
          <p className="text-white/60 text-sm mb-6 animate-fade-in">
            Plan out your idea before you start creating!
          </p>

          {/* Collapsible Reference Panel */}
          <details
            open={blueprintRefOpen}
            onToggle={(e) => setBlueprintRefOpen(e.target.open)}
            className="glass rounded-2xl mb-4 animate-slide-up"
          >
            <summary className="cursor-pointer p-4 flex items-center gap-2 text-white font-medium text-sm select-none">
              <span className="text-lg">{'\u{1F4DA}'}</span> Tips & Vocab
              <span className="text-white/40 text-xs ml-auto">tap for tips</span>
            </summary>
            <div className="px-4 pb-4 space-y-3">
              {selectedMission.visualSteps && (
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider font-medium mb-2">Steps</p>
                  <ol className="space-y-1.5">
                    {selectedMission.visualSteps.map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                        <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {selectedMission.sensoryNotes && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedMission.sensoryNotes).map(([key, val]) => (
                    <span key={key} className="bg-white/10 text-white/70 text-xs rounded-full px-3 py-1">
                      {key === 'movement' ? '\u{1F3C3}' : key === 'noise' ? '\u{1F50A}' : '\u{270B}'} {val}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </details>

          {/* Grandee Guide Card */}
          {blueprintGuideStep >= 0 && (() => {
            const guideIcon = selectedCornerPost?.icon || '\u{1F9E0}';
            const steps = selectedMission.visualSteps || [];
            const tips = [
              steps[0] ? `What will you need to "${steps[0].toLowerCase()}"?` : 'What will you do first?',
              steps[1] ? `How will you "${steps[1].toLowerCase()}"?` : 'What materials or techniques will you try?',
              'What will make this special to you?',
            ];
            const tip = tips[blueprintGuideStep] || tips[tips.length - 1];
            return (
              <div className="glass rounded-2xl p-4 mb-4 animate-slide-up">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">{guideIcon}</span>
                  <p className={`${simpleMode ? 'text-lg' : 'text-sm'} text-white font-medium leading-relaxed flex-1`}>{tip}</p>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-1.5">
                    {tips.map((_, i) => (
                      <span key={i} className={`w-2 h-2 rounded-full ${i === blueprintGuideStep ? 'bg-white' : 'bg-white/30'}`} />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {blueprintGuideStep < tips.length - 1 && (
                      <button
                        onClick={() => {
                          const next = blueprintGuideStep + 1;
                          setBlueprintGuideStep(next);
                          if (textToSpeech) speak(tips[next]);
                        }}
                        className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium hover:bg-white/30 transition-all"
                      >
                        Next tip {'\u{2192}'}
                      </button>
                    )}
                    <button
                      onClick={() => setBlueprintGuideStep(-1)}
                      className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-medium hover:bg-white/20 transition-all"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Text Plan Section */}
          <div className="glass-surface rounded-3xl p-6 mb-4 animate-slide-up">
            <h2 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold flex items-center gap-2">
              <span className="text-lg">💭</span> What's your plan?
            </h2>
            <textarea
              value={blueprintText}
              onChange={(e) => {
                const sanitized = e.target.value.slice(0, 500).replace(/<\/?[^>]+(>|$)/g, '');
                setBlueprintText(sanitized);
              }}
              className="w-full bg-white/50 text-gray-800 text-base rounded-xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 min-h-[120px] placeholder-gray-400"
              placeholder="Tell us about what you want to create..."
              maxLength={500}
              aria-label="Describe your plan"
            />
            <div className="flex items-center justify-between mt-2">
              {speechSupported ? (
                <button
                  onClick={() => {
                    if (isListening) {
                      stopListening();
                    } else {
                      startListening((transcript) => {
                        setBlueprintText(prev => {
                          const joined = prev ? (prev + ' ' + transcript) : transcript;
                          return joined.slice(0, 500);
                        });
                      });
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                    isListening
                      ? 'bg-red-100 text-red-600 animate-pulse'
                      : 'bg-white/70 text-gray-600 hover:bg-white'
                  }`}
                  aria-label={isListening ? 'Stop recording' : 'Start voice input'}
                >
                  {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                  {isListening ? 'Listening...' : 'Speak'}
                </button>
              ) : (
                <span />
              )}
              <p className="text-gray-400 text-xs">{blueprintText.length}/500</p>
            </div>
          </div>

          {/* Sketch Upload Section */}
          <div className="glass-surface rounded-3xl p-6 mb-6 animate-fade-in-delay">
            <h2 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold flex items-center gap-2">
              <span className="text-lg">✏️</span> Draw or snap your blueprint
            </h2>

            {!blueprintSketch ? (
              <label className="block cursor-pointer bg-white/50 rounded-2xl p-6 text-center transition-all duration-300 hover:bg-white/70 active:scale-95 border-2 border-dashed border-gray-300">
                <Camera size={40} className="text-gray-400 mx-auto mb-2" />
                <span className="text-gray-700 font-medium text-sm block">Tap to add a sketch</span>
                <span className="text-gray-400 text-xs block mt-1">Draw on paper, then take a photo</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleBlueprintSketch}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="space-y-3">
                <img
                  src={blueprintSketch}
                  alt="Your blueprint sketch"
                  className="w-full rounded-2xl object-contain max-h-48 bg-white/50 p-2"
                />
                <button
                  onClick={() => setBlueprintSketch(null)}
                  className="w-full text-center text-gray-500 text-sm underline hover:text-gray-700 transition-colors py-2"
                >
                  Remove sketch
                </button>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleBlueprintSubmit}
            className={`w-full py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white ${simpleMode ? 'text-2xl' : 'text-xl'} font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5`}
          >
            I'm Ready! Start Creating
          </button>

          {/* Skip option */}
          <button
            onClick={() => completeMission(selectedMission.id)}
            className="mt-3 w-full text-center text-white/40 text-sm hover:text-white/60 transition-colors py-2"
          >
            Skip blueprint
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // SUCCESS SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'success') {
    const allCompleted = cornerPosts.every(post =>
      post.missions.some(mission => completedMissions.includes(mission.id))
    );
    const progressPct = Math.max((completedCornerPosts / 6) * 100, 0);

    // Confetti dots config
    const confettiDots = [
      { color: '#7c3aed', left: '15%', anim: 'animate-confetti-1' },
      { color: '#3b82f6', left: '30%', anim: 'animate-confetti-2' },
      { color: '#14b8a6', left: '50%', anim: 'animate-confetti-3' },
      { color: '#f59e0b', left: '65%', anim: 'animate-confetti-4' },
      { color: '#ef4444', left: '80%', anim: 'animate-confetti-5' },
      { color: '#10b981', left: '25%', anim: 'animate-confetti-3' },
      { color: '#8b5cf6', left: '70%', anim: 'animate-confetti-1' },
      { color: '#06b6d4', left: '40%', anim: 'animate-confetti-4' },
    ];

    return (
      <div className="mesh-gradient flex flex-col items-center justify-center text-center px-6 relative overflow-hidden">
        {/* CSS confetti dots */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {confettiDots.map((dot, i) => (
            <div
              key={i}
              className={`confetti-dot ${dot.anim}`}
              style={{ backgroundColor: dot.color, left: dot.left, top: '70%' }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-sm w-full">
          <div className="glass rounded-3xl p-8 animate-slide-up">
            {/* Trophy with scale-in */}
            <div className="text-7xl mb-4 animate-scale-in">🏆</div>

            <h1 className={`${simpleMode ? 'text-4xl' : 'text-3xl'} font-black tracking-tight text-white mb-2`}>
              Mission Complete
            </h1>
            <p className="text-white/70 text-base mb-6">What did you notice?</p>

            {allCompleted ? (
              <div className="bg-yellow-400/20 border border-yellow-400/30 rounded-2xl p-4 mb-6 animate-scale-in">
                <h3 className="text-xl font-bold text-yellow-300">SUPER EXPLORER</h3>
                <p className="text-yellow-200/80 text-sm">You've explored all six corner posts.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center mb-6 animate-fade-in-delay">
                <ProgressRing progress={progressPct} size={80} strokeWidth={6} />
                <p className="text-white/60 text-xs uppercase tracking-widest mt-2">
                  {completedCornerPosts} of 6 explored
                </p>
              </div>
            )}

            {/* Reflection */}
            <div className="mb-6">
              <button
                onClick={() => setShowReflection(!showReflection)}
                className="text-white/60 text-sm font-medium flex items-center justify-center gap-1 mx-auto transition-all duration-300 hover:text-white"
              >
                <MessageSquare size={14} />
                {showReflection ? "Hide Reflection" : "Share Your Thoughts"}
              </button>

              {showReflection && (
                <div className="mt-4 glass-surface rounded-2xl p-5 animate-slide-up text-left">
                  <h3 className="text-sm font-bold text-gray-800 mb-1">True Reflection</h3>
                  <p className="text-xs text-gray-500 mb-3">Share what you learned or enjoyed about this activity.</p>
                  <textarea
                    value={reflectionText}
                    onChange={(e) => {
                      const sanitized = e.target.value.slice(0, 500).replace(/<\/?[^>]+(>|$)/g, "");
                      setReflectionText(sanitized);
                    }}
                    className="w-full p-3 bg-white border-2 border-gray-200 rounded-xl h-24 text-base resize-none focus:border-purple-400 focus:outline-none transition-all duration-300"
                    placeholder="What did you learn? What was your favorite part?"
                    maxLength={500}
                    aria-label="Your reflection"
                  />
                  <p className="text-xs text-gray-400 mt-1">Your reflection stays private.</p>
                </div>
              )}
            </div>

            {/* Senses checklist */}
            <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
              {renderSensesChecklist('dark')}
            </div>

            {/* Work mode selector */}
            <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
              {renderWorkModeSelector('dark')}
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  const activeSenses = Object.fromEntries(Object.entries(sensesUsed).filter(([, v]) => v));
                  try {
                    const refs = JSON.parse(safeLocalStorage.getItem('reflections', '{}'));
                    refs[selectedMission?.id || 'general'] = {
                      text: reflectionText || '',
                      senses: activeSenses,
                      ...(workMode ? { workMode, ...(workModeBuddy ? { workModeBuddy } : {}) } : {}),
                    };
                    safeLocalStorage.setItem('reflections', JSON.stringify(refs));
                  } catch (error) {
                    console.error('Error saving reflection:', error);
                  }
                  setSensesUsed({ ...defaultSenses });
                  setWorkMode(null);
                  setWorkModeBuddy(null);
                  setCurrentScreen('cornerPosts');
                  if (textToSpeech) {
                    speak("Let's continue exploring!");
                  }
                }}
                className="w-full py-4 bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl"
              >
                Continue Exploring
              </button>

              <button
                onClick={() => {
                  const activeSenses = Object.fromEntries(Object.entries(sensesUsed).filter(([, v]) => v));
                  try {
                    const refs = JSON.parse(safeLocalStorage.getItem('reflections', '{}'));
                    refs[selectedMission?.id || 'general'] = {
                      text: reflectionText || '',
                      senses: activeSenses,
                      ...(workMode ? { workMode, ...(workModeBuddy ? { workModeBuddy } : {}) } : {}),
                    };
                    safeLocalStorage.setItem('reflections', JSON.stringify(refs));
                  } catch (error) {
                    console.error('Error saving reflection:', error);
                  }
                  setSensesUsed({ ...defaultSenses });
                  setWorkMode(null);
                  setWorkModeBuddy(null);
                  setCurrentScreen('home');
                }}
                className="w-full py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-2xl transition-all duration-300 active:scale-95 hover:bg-white/20"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // JOURNEY MAP / ACCOUNTABILITY SCREEN — Timeline Layout
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'accountability') {
    const completedMissionDetails = [];
    cornerPosts.forEach(post => {
      post.missions.forEach(mission => {
        if (completedMissions.includes(mission.id)) {
          completedMissionDetails.push({
            ...mission,
            cornerPost: post.name,
            cornerPostColor: post.color,
            cornerPostIcon: post.icon
          });
        }
      });
    });

    let savedReflections = {};
    try {
      savedReflections = JSON.parse(safeLocalStorage.getItem('reflections', '{}'));
    } catch (error) {
      console.error('Error loading reflections:', error);
    }

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('home')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Home"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-1 animate-fade-in`}>
            Your Journey Map
          </h1>
          <p className="text-white/60 text-sm mb-8 animate-fade-in">A record of your adventures and learning</p>

          {completedMissionDetails.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center animate-slide-up">
              <div className="text-5xl mb-4">🗺️</div>
              <h2 className="text-xl font-bold text-white mb-2">Your Journey Awaits</h2>
              <p className="text-white/60 text-sm">Complete missions to see your progress here!</p>
              <button
                onClick={() => setCurrentScreen('cornerPosts')}
                className="mt-6 w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl transition-all duration-300 active:scale-95"
              >
                Start Exploring
              </button>
            </div>
          ) : (
            /* Timeline */
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-white/20" />

              <div className="space-y-6">
                {completedMissionDetails.map((mission, idx) => (
                  <div
                    key={mission.id}
                    className={`relative pl-14 opacity-start ${
                      idx === 0 ? 'animate-stagger-1' : idx === 1 ? 'animate-stagger-2' : idx === 2 ? 'animate-stagger-3' : 'animate-stagger-4'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-3 top-4 w-5 h-5 rounded-full bg-white/30 border-2 border-white/60 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>

                    {/* Glass card */}
                    <div className="glass rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{mission.cornerPostIcon}</span>
                        <div>
                          <h3 className="text-white font-bold text-base">{mission.name}</h3>
                          <p className="text-white/50 text-xs">{mission.cornerPost}</p>
                        </div>
                      </div>
                      {savedReflections[mission.id] && (() => {
                        const ref = savedReflections[mission.id];
                        const refText = typeof ref === 'string' ? ref : (ref?.text || '');
                        const refSenses = typeof ref === 'object' && ref?.senses ? ref.senses : null;
                        return (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            {refText && <p className="text-white/60 text-sm italic">"{refText}"</p>}
                            {refSenses && Object.keys(refSenses).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {sensesConfig.filter(s => refSenses[s.key]).map(s => (
                                  <span key={s.key} className="text-xs bg-white/10 rounded-full px-2 py-0.5 text-white/50">{s.emoji} {s.label}</span>
                                ))}
                              </div>
                            )}
                            {ref.workMode && (() => {
                              const wm = workModeConfig.find(w => w.key === ref.workMode);
                              return wm ? (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className="text-xs bg-white/10 rounded-full px-2 py-0.5 text-white/50">{wm.emoji} {wm.label}</span>
                                  {ref.workModeBuddy && <span className="text-white/30 text-[10px]">with {ref.workModeBuddy}</span>}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Gallery
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolio') {
    const filtered = portfolioFilter === 'all'
      ? portfolioEntries
      : portfolioEntries.filter(e => e.cornerPostId === portfolioFilter);
    const staggerClasses = ['animate-stagger-1', 'animate-stagger-2', 'animate-stagger-3', 'animate-stagger-4', 'animate-stagger-5', 'animate-stagger-6'];

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => setCurrentScreen('home')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Home"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <div className="flex items-center justify-between mb-4 animate-fade-in">
            <div>
              <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>My Collection</h1>
              <p className="text-white/60 text-sm">{portfolioEntries.length} piece{portfolioEntries.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => {
                setPortfolioUploadImage(null);
                setPortfolioUploadMediaType('image');
                setPortfolioUploadMediaBlob(null);
                setPortfolioUploadTag(null);
                setPortfolioCritiqueStep(0);
                setPortfolioCritiqueAnswers({ describe: '', analyze: '', interpret: '', evaluate: '' });
                setPortfolioRubricRatings({ planning: 0, technique: 0, creativity: 0, cornerPost: 0, principle: 0, selfCritique: 0 });
                setCurrentScreen('portfolioUpload');
              }}
              className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-500 text-white flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
              aria-label="Add new"
            >
              <Camera size={20} />
            </button>
          </div>

          {/* Filter pills */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1 animate-fade-in" style={{ scrollbarWidth: 'none' }}>
            <button
              onClick={() => setPortfolioFilter('all')}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                portfolioFilter === 'all' ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white' : 'glass text-white/60'
              }`}
            >
              All
            </button>
            {cornerPosts.map(post => (
              <button
                key={post.id}
                onClick={() => setPortfolioFilter(post.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                  portfolioFilter === post.id ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white' : 'glass text-white/60'
                }`}
              >
                <span>{post.icon}</span> {post.name}
              </button>
            ))}
          </div>

          {/* Growth timeline link */}
          {portfolioEntries.length >= 2 && (
            <button
              onClick={() => setCurrentScreen('portfolioGrowth')}
              className="w-full mb-4 py-3 glass rounded-2xl text-white/80 text-sm font-medium flex items-center justify-center gap-2 transition-all duration-300 hover:bg-white/20 active:scale-95 animate-fade-in"
            >
              {'\u{1F331}'} My Growth Story
            </button>
          )}

          {/* Gallery grid or empty state */}
          {filtered.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center animate-slide-up">
              <div className="text-5xl mb-4">{'\u{1F4F8}'}</div>
              <h2 className="text-xl font-bold text-white mb-2">
                {portfolioFilter !== 'all' ? 'Nothing here yet' : 'Start Your Collection!'}
              </h2>
              <p className="text-white/60 text-sm mb-6">
                {portfolioFilter !== 'all' ? 'Try a different filter or add something new.' : 'Take a picture of something you made and add it here.'}
              </p>
              {portfolioFilter === 'all' && (
                <button
                  onClick={() => {
                    setPortfolioUploadImage(null);
                    setPortfolioUploadMediaType('image');
                    setPortfolioUploadMediaBlob(null);
                    setPortfolioUploadTag(null);
                    setPortfolioCritiqueStep(0);
                    setPortfolioCritiqueAnswers({ describe: '', analyze: '', interpret: '', evaluate: '' });
                    setPortfolioRubricRatings({ planning: 0, technique: 0, creativity: 0, cornerPost: 0, principle: 0, selfCritique: 0 });
                    setCurrentScreen('portfolioUpload');
                  }}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl transition-all duration-300 active:scale-95"
                >
                  Add Your First Piece
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filtered.map((entry, idx) => {
                const post = cornerPosts.find(p => p.id === entry.cornerPostId);
                return (
                  <button
                    key={entry.id}
                    onClick={() => {
                      setPortfolioSelectedEntry(entry);
                      setCurrentScreen('portfolioDetail');
                    }}
                    className={`glass rounded-2xl overflow-hidden text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-95 opacity-start ${staggerClasses[idx % 6]}`}
                  >
                    <div className="relative aspect-square">
                      <img src={entry.imageThumb} alt="Work" className="w-full h-full object-cover" />
                      {entry.mediaType === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                            <Play size={20} className="text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                      {post && (
                        <span className="absolute top-2 right-2 text-lg drop-shadow-lg">{post.icon}</span>
                      )}
                    </div>
                    <div className="p-3">
                      {entry.lessonId && (
                        <p className="text-white/60 text-[10px] font-medium mb-0.5 truncate">{'\u{1F4D6}'} {entry.lessonTitle}</p>
                      )}
                      <p className="text-white/70 text-xs truncate">{getCritiqueSummary(entry).previewText}</p>
                      <p className="text-white/40 text-[10px] mt-1">{formatDate(entry.createdAt)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Upload Photo
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolioUpload') {
    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('portfolio')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2 animate-fade-in`}>
            Add to Your Collection
          </h1>
          <p className="text-white/60 text-sm mb-6 animate-fade-in">Take a picture or record a video of something you made!</p>

          {!portfolioUploadImage ? (
            <div className="animate-slide-up">
              {/* Camera capture */}
              <label className="block cursor-pointer glass rounded-3xl p-8 text-center transition-all duration-300 hover:bg-white/20 active:scale-95 border-2 border-dashed border-white/30 mb-4">
                <Camera size={48} className="text-white/60 mx-auto mb-3" />
                <span className="text-white font-medium text-lg block">Tap to take a photo</span>
                <span className="text-white/40 text-sm block mt-1">Opens your camera</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePortfolioUpload}
                  className="hidden"
                />
              </label>

              {/* Video capture */}
              <label className="block cursor-pointer glass rounded-3xl p-8 text-center transition-all duration-300 hover:bg-white/20 active:scale-95 border-2 border-dashed border-white/30 mb-4">
                <Video size={48} className="text-white/60 mx-auto mb-3" />
                <span className="text-white font-medium text-lg block">Record a video</span>
                <span className="text-white/40 text-sm block mt-1">60 seconds max</span>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/mov"
                  capture="environment"
                  onChange={handlePortfolioVideoUpload}
                  className="hidden"
                />
              </label>

              {/* Gallery picker */}
              <label className="cursor-pointer block text-center text-white/40 text-sm underline hover:text-white/60 transition-colors">
                Or choose from your photos
                <input
                  type="file"
                  accept="image/*,video/mp4,video/quicktime,video/webm,video/mov"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.type.startsWith('video/')) handlePortfolioVideoUpload(e);
                    else handlePortfolioUpload(e);
                  }}
                  className="hidden"
                />
              </label>
            </div>
          ) : (
            <div className="animate-scale-in">
              {/* Preview */}
              <div className="glass rounded-3xl p-4 mb-4">
                <div className="relative">
                  <img src={portfolioUploadImage} alt="Preview" className="w-full rounded-2xl object-contain max-h-64" />
                  {portfolioUploadMediaType === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
                        <Play size={32} className="text-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => setCurrentScreen('portfolioTag')}
                className="w-full py-4 bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl"
              >
                Looks Good!
              </button>
              <button
                onClick={() => {
                  setPortfolioUploadImage(null);
                  setPortfolioUploadMediaType('image');
                  setPortfolioUploadMediaBlob(null);
                }}
                className="mt-3 w-full text-center text-white/40 text-sm underline hover:text-white/60 transition-colors"
              >
                Try again
              </button>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Builder (teacher creates/edits lessons)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonBuilder' && lessonBuilderData) {
    const draft = lessonBuilderData;
    const update = (field, val) => setLessonBuilderData(prev => ({ ...prev, [field]: val }));

    const builderSteps = [
      { label: 'Basics', icon: '\u{1F4D6}' },
      { label: 'Questions', icon: '\u{1F4AC}' },
      { label: 'Vocabulary', icon: '\u{1F4DD}' },
      { label: 'Activity', icon: '\u{1F3A8}' },
      { label: 'Media', icon: '\u{1F3AC}' },
      { label: 'Review', icon: '\u{2705}' },
    ];

    const coverColorOptions = [
      { value: 'from-blue-400 to-indigo-500', label: 'Blue' },
      { value: 'from-green-400 to-teal-500', label: 'Green' },
      { value: 'from-purple-400 to-indigo-500', label: 'Purple' },
      { value: 'from-pink-400 to-rose-500', label: 'Pink' },
      { value: 'from-amber-400 to-orange-500', label: 'Amber' },
      { value: 'from-cyan-400 to-blue-500', label: 'Cyan' },
      { value: 'from-red-400 to-rose-600', label: 'Red' },
      { value: 'from-emerald-400 to-green-600', label: 'Emerald' },
    ];

    const coverEmojiOptions = ['\u{1F4D6}', '\u{1F3A8}', '\u{1F6B2}', '\u{2764}\u{FE0F}', '\u{1F31F}', '\u{1F9E0}', '\u{1F331}', '\u{1F30A}', '\u{1F3B5}', '\u{1F680}', '\u{1F98B}', '\u{2728}', '\u{1F525}', '\u{1F308}', '\u{1FA84}', '\u{1F3AD}'];

    const canSave = draft.title.trim() && draft.essentialQuestions.some(q => q.trim());

    // AI Assist demo — shows what AI-powered lesson building could do
    const handleAiAssist = () => {
      if (!draft.title.trim()) {
        alert('Enter a lesson title first so AI knows what to generate!');
        return;
      }
      setAiAssistLoading(true);
      // Simulate AI generation delay for demo
      setTimeout(() => {
        const title = draft.title.trim();
        if (lessonBuilderStep === 0 && !draft.hook) {
          update('hook', `What happens when you discover the power of ${title.toLowerCase()}? Let's find out!`);
        }
        if (lessonBuilderStep === 1) {
          const qs = draft.essentialQuestions.filter(q => q.trim());
          if (qs.length < 2) {
            update('essentialQuestions', [
              ...draft.essentialQuestions.filter(q => q.trim()),
              `What did you discover about yourself through "${title}"?`,
              `How can you use what you learned in your everyday life?`,
              `What would you share with someone who hasn't experienced this yet?`,
            ].slice(0, 5));
          }
        }
        if (lessonBuilderStep === 2) {
          const existing = draft.vocabulary.filter(v => v.term.trim());
          if (existing.length < 2) {
            update('vocabulary', [
              ...existing,
              { term: 'Medium', def: 'The material or technique used to create art' },
              { term: 'Composition', def: 'How elements are arranged in a work of art' },
              { term: 'Expression', def: 'Showing feelings or ideas through art' },
            ]);
          }
        }
        if (lessonBuilderStep === 3 && !draft.artActivity) {
          update('artActivity', `Create your own ${title.toLowerCase()} inspired artwork using mixed media — experiment with different materials and techniques!`);
        }
        setAiAssistLoading(false);
      }, 1500);
    };

    const renderAiAssistButton = () => (
      <div className="mb-4">
        <button
          onClick={handleAiAssist}
          disabled={aiAssistLoading}
          className={`w-full py-3 rounded-2xl font-bold text-sm transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 ${
            aiAssistLoading
              ? 'bg-purple-400/20 text-purple-300 cursor-wait'
              : 'bg-gradient-to-r from-purple-500 via-indigo-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-purple-500/20 hover:-translate-y-0.5'
          }`}
        >
          {aiAssistLoading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              AI is thinking...
            </>
          ) : (
            <>
              ✨ AI Assist — Auto-Generate
            </>
          )}
        </button>
        {lessonBuilderStep === 0 && !aiAssistOpen && (
          <button
            onClick={() => setAiAssistOpen(true)}
            className="w-full mt-2 text-white/30 text-xs text-center hover:text-white/50 transition-colors"
          >
            What can AI do?
          </button>
        )}
        {aiAssistOpen && lessonBuilderStep === 0 && (
          <div className="glass rounded-2xl p-4 mt-2 animate-scale-in">
            <p className="text-cyan-300 text-xs font-bold uppercase tracking-widest mb-2">Coming Soon — AI Lesson Builder</p>
            <div className="space-y-2 text-white/60 text-xs">
              <p>🧠 <b className="text-white/80">Describe your book</b> and AI generates the full lesson — questions, vocabulary, activities, materials list</p>
              <p>🎬 <b className="text-white/80">YouTube integration</b> — AI finds relevant read-alouds and technique videos automatically</p>
              <p>📋 <b className="text-white/80">Standards alignment</b> — AI maps your lesson to state and national arts standards</p>
              <p>🔄 <b className="text-white/80">Lesson remix</b> — adapt any existing lesson for different grade levels or corner posts</p>
              <p>📚 <b className="text-white/80">Shared library</b> — publish your lessons for other teachers to use and remix</p>
            </div>
            <div className="mt-3 px-3 py-2 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-xl border border-purple-400/20">
              <p className="text-purple-300 text-[10px] font-bold uppercase tracking-wider">Demo Mode Active</p>
              <p className="text-white/50 text-[10px] mt-0.5">Tap "AI Assist" above to see a preview of auto-generation. Full AI integration coming soon.</p>
            </div>
            <button onClick={() => setAiAssistOpen(false)} className="w-full mt-2 text-white/30 text-xs text-center hover:text-white/50">Hide</button>
          </div>
        )}
      </div>
    );

    const handleSaveLesson = () => {
      const lesson = {
        ...draft,
        id: editingLessonId || ('custom_' + Date.now()),
        essentialQuestions: draft.essentialQuestions.filter(q => q.trim()),
        vocabulary: draft.vocabulary.filter(v => v.term.trim()),
        techniqueVideos: (draft.techniqueVideos || []).filter(v => v.title.trim() && v.url.trim()),
        materials: (draft.materials || []).filter(m => m.trim()),
      };
      if (editingLessonId) {
        setCustomLessons(prev => prev.map(l => l.id === editingLessonId ? lesson : l));
      } else {
        setCustomLessons(prev => [...prev, lesson]);
      }
      setLessonBuilderData(null);
      setEditingLessonId(null);
      setCurrentScreen('lessonPicker');
    };

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={() => {
              if (lessonBuilderStep > 0) {
                setLessonBuilderStep(s => s - 1);
              } else {
                setLessonBuilderData(null);
                setEditingLessonId(null);
                setCurrentScreen('lessonPicker');
              }
            }}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          {/* Progress */}
          <div className="text-center mb-4 animate-fade-in">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-2">
              {editingLessonId ? 'Edit Lesson' : 'Create Lesson'} — {builderSteps[lessonBuilderStep].label}
            </p>
            <div className="flex justify-center gap-1.5">
              {builderSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setLessonBuilderStep(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === lessonBuilderStep ? 'w-6 bg-purple-400 scale-110' :
                    i < lessonBuilderStep ? 'w-4 bg-purple-400/60' : 'w-4 bg-white/15'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Step 0: Basics */}
          {lessonBuilderStep === 0 && (
            <div className="animate-fade-in space-y-4">
              {renderAiAssistButton()}
              <div className={`bg-gradient-to-br ${draft.coverColor} rounded-3xl p-6 text-center`}>
                <div className="text-5xl mb-2">{draft.coverEmoji}</div>
                <p className="text-white/80 text-sm font-medium">{draft.title || 'Your Lesson Title'}</p>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Title</label>
                <input
                  value={draft.title}
                  onChange={e => update('title', e.target.value.slice(0, 100))}
                  className="w-full mt-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                  placeholder="e.g. The Dot"
                />
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Author</label>
                <input
                  value={draft.author}
                  onChange={e => update('author', e.target.value.slice(0, 100))}
                  className="w-full mt-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                  placeholder="e.g. Peter H. Reynolds"
                />
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Hook</label>
                <textarea
                  value={draft.hook}
                  onChange={e => update('hook', e.target.value.slice(0, 200))}
                  className="w-full mt-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm h-16 resize-none focus:border-purple-400 focus:outline-none"
                  placeholder="One line that makes students want to start..."
                />
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Cover Emoji</label>
                <div className="flex flex-wrap gap-2">
                  {coverEmojiOptions.map(em => (
                    <button
                      key={em}
                      onClick={() => update('coverEmoji', em)}
                      className={`w-10 h-10 text-2xl rounded-xl transition-all duration-200 ${
                        draft.coverEmoji === em ? 'bg-purple-100 scale-110 ring-2 ring-purple-400' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Cover Color</label>
                <div className="grid grid-cols-4 gap-2">
                  {coverColorOptions.map(c => (
                    <button
                      key={c.value}
                      onClick={() => update('coverColor', c.value)}
                      className={`h-10 rounded-xl bg-gradient-to-br ${c.value} transition-all duration-200 ${
                        draft.coverColor === c.value ? 'scale-110 ring-2 ring-white' : 'opacity-70 hover:opacity-100'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Corner Post</label>
                <div className="grid grid-cols-3 gap-2">
                  {cornerPosts.map(p => (
                    <button
                      key={p.id}
                      onClick={() => update('suggestedCornerPost', p.id)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200 text-center ${
                        draft.suggestedCornerPost === p.id
                          ? 'bg-purple-100 ring-2 ring-purple-400 scale-105'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <span className="text-lg">{p.icon}</span>
                      <span className="text-[9px] font-medium text-gray-700">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Grade Level</label>
                <div className="flex flex-wrap gap-2">
                  {['K-2', '3-5', '6-8', '9-12'].map(g => (
                    <button
                      key={g}
                      onClick={() => update('gradeLevel', g)}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                        draft.gradeLevel === g ? 'bg-purple-100 ring-2 ring-purple-400 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >{g}</button>
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Art Medium</label>
                <div className="flex flex-wrap gap-2">
                  {['Watercolor', 'Collage', 'Drawing', 'Sculpture', 'Mixed Media', 'Digital', 'Photography', 'Other'].map(m => (
                    <button
                      key={m}
                      onClick={() => update('artMedium', m)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                        draft.artMedium === m ? 'bg-cyan-100 ring-2 ring-cyan-400 text-cyan-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >{m}</button>
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1 block">Kern Stage</label>
                <p className="text-gray-400 text-[10px] mb-2">What level of student autonomy?</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'dependent', label: 'Dependent', desc: 'Teacher-led' },
                    { value: 'independent', label: 'Independent', desc: 'Self-directed' },
                    { value: 'interdependent', label: 'Interdependent', desc: 'Collaborative' },
                  ].map(k => (
                    <button
                      key={k.value}
                      onClick={() => update('kernStage', k.value)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200 flex flex-col items-center ${
                        draft.kernStage === k.value ? 'bg-amber-100 ring-2 ring-amber-400 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span>{k.label}</span>
                      <span className="text-[9px] font-normal opacity-60">{k.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1 block">Art Strategy</label>
                <p className="text-gray-400 text-[10px] mb-2">What thinking skill does this develop?</p>
                <div className="flex flex-wrap gap-2">
                  {['Observation', 'Experimentation', 'Expression', 'Storytelling', 'Critique', 'Construction'].map(s => (
                    <button
                      key={s}
                      onClick={() => update('artStrategy', s)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                        draft.artStrategy === s ? 'bg-emerald-100 ring-2 ring-emerald-400 text-emerald-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Essential Questions */}
          {lessonBuilderStep === 1 && (
            <div className="animate-fade-in space-y-4">
              <div className="text-center mb-2">
                <div className="text-4xl mb-2">{'\u{1F4AC}'}</div>
                <h2 className="text-xl font-bold text-white">Essential Questions</h2>
                <p className="text-white/50 text-sm">What will students reflect on?</p>
              </div>
              {renderAiAssistButton()}

              {draft.essentialQuestions.map((q, i) => (
                <div key={i} className="glass-surface rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Question {i + 1}</label>
                    {draft.essentialQuestions.length > 1 && (
                      <button
                        onClick={() => update('essentialQuestions', draft.essentialQuestions.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={q}
                    onChange={e => {
                      const next = [...draft.essentialQuestions];
                      next[i] = e.target.value.slice(0, 200);
                      update('essentialQuestions', next);
                    }}
                    className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm h-16 resize-none focus:border-purple-400 focus:outline-none"
                    placeholder="e.g. What does it mean to have an open mind?"
                  />
                </div>
              ))}

              {draft.essentialQuestions.length < 5 && (
                <button
                  onClick={() => update('essentialQuestions', [...draft.essentialQuestions, ''])}
                  className="w-full py-2 border-2 border-dashed border-white/20 text-white/50 text-sm rounded-xl hover:border-white/40 hover:text-white/70 transition-all"
                >
                  + Add Question
                </button>
              )}
            </div>
          )}

          {/* Step 2: Vocabulary */}
          {lessonBuilderStep === 2 && (
            <div className="animate-fade-in space-y-4">
              <div className="text-center mb-2">
                <div className="text-4xl mb-2">{'\u{1F4DD}'}</div>
                <h2 className="text-xl font-bold text-white">Art Vocabulary</h2>
                <p className="text-white/50 text-sm">Terms students will learn</p>
              </div>
              {renderAiAssistButton()}

              {draft.vocabulary.map((v, i) => (
                <div key={i} className="glass-surface rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Term {i + 1}</label>
                    {draft.vocabulary.length > 1 && (
                      <button
                        onClick={() => update('vocabulary', draft.vocabulary.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <input
                    value={v.term}
                    onChange={e => {
                      const next = [...draft.vocabulary];
                      next[i] = { ...next[i], term: e.target.value.slice(0, 50) };
                      update('vocabulary', next);
                    }}
                    className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none mb-2"
                    placeholder="Term (e.g. Collage)"
                  />
                  <input
                    value={v.def}
                    onChange={e => {
                      const next = [...draft.vocabulary];
                      next[i] = { ...next[i], def: e.target.value.slice(0, 200) };
                      update('vocabulary', next);
                    }}
                    className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                    placeholder="Definition"
                  />
                </div>
              ))}

              {draft.vocabulary.length < 10 && (
                <button
                  onClick={() => update('vocabulary', [...draft.vocabulary, { term: '', def: '' }])}
                  className="w-full py-2 border-2 border-dashed border-white/20 text-white/50 text-sm rounded-xl hover:border-white/40 hover:text-white/70 transition-all"
                >
                  + Add Term
                </button>
              )}
            </div>
          )}

          {/* Step 3: Activity + Materials + Procedures */}
          {lessonBuilderStep === 3 && (
            <div className="animate-fade-in space-y-4">
              <div className="text-center mb-2">
                <div className="text-4xl mb-2">{'\u{1F3A8}'}</div>
                <h2 className="text-xl font-bold text-white">Art Activity</h2>
                <p className="text-white/50 text-sm">What will students create?</p>
              </div>
              {renderAiAssistButton()}

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Activity Description</label>
                <textarea
                  value={draft.artActivity}
                  onChange={e => update('artActivity', e.target.value.slice(0, 300))}
                  className="w-full mt-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm h-20 resize-none focus:border-purple-400 focus:outline-none"
                  placeholder="e.g. Watercolor Dot Investigation — experiment with paint and see what happens!"
                />
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2 block">Materials Needed</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(draft.materials || []).map((m, i) => (
                    <span key={i} className="flex items-center gap-1 bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full text-xs font-medium">
                      {m}
                      <button onClick={() => update('materials', draft.materials.filter((_, j) => j !== i))} className="hover:text-red-500">
                        {'\u{00D7}'}
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    id="material-input"
                    className="flex-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                    placeholder="e.g. Watercolor paint"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        update('materials', [...(draft.materials || []), e.target.value.trim().slice(0, 50)]);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('material-input');
                      if (input?.value.trim()) {
                        update('materials', [...(draft.materials || []), input.value.trim().slice(0, 50)]);
                        input.value = '';
                      }
                    }}
                    className="px-3 py-2 bg-purple-500 text-white text-sm font-bold rounded-xl active:scale-95 transition-all"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Teacher Procedures</label>
                <p className="text-gray-400 text-[10px] mb-1">Only visible to you — Bell Work, Guided Practice, etc.</p>
                <textarea
                  value={draft.procedures || ''}
                  onChange={e => update('procedures', e.target.value.slice(0, 1000))}
                  className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm h-32 resize-none focus:border-purple-400 focus:outline-none"
                  placeholder={"Bell Work: ...\nReview: ...\nGuided Practice: ...\nIndependent Practice: ..."}
                />
              </div>
            </div>
          )}

          {/* Step 4: Media links */}
          {lessonBuilderStep === 4 && (
            <div className="animate-fade-in space-y-4">
              <div className="text-center mb-2">
                <div className="text-4xl mb-2">{'\u{1F3AC}'}</div>
                <h2 className="text-xl font-bold text-white">Audio & Video</h2>
                <p className="text-white/50 text-sm">Links students will use</p>
              </div>

              {/* YouTube search helper */}
              <div className="glass rounded-2xl p-4 border border-red-400/20">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">▶️</span>
                  <p className="text-white font-bold text-sm">Find Videos on YouTube</p>
                </div>
                <div className="flex gap-2 mb-2">
                  <input
                    value={youtubeSearch}
                    onChange={e => setYoutubeSearch(e.target.value)}
                    className="flex-1 p-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm placeholder-white/30 focus:outline-none focus:border-red-400/50"
                    placeholder={draft.title ? `${draft.title} read aloud` : 'Search YouTube...'}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && youtubeSearch.trim()) {
                        window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(youtubeSearch.trim() + ' children read aloud')}`, '_blank');
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const q = youtubeSearch.trim() || (draft.title ? draft.title + ' read aloud' : '');
                      if (q) window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(q + ' children read aloud')}`, '_blank');
                    }}
                    className="px-4 py-2 bg-red-500 text-white text-sm font-bold rounded-xl transition-all active:scale-95"
                  >
                    Search
                  </button>
                </div>
                <p className="text-white/30 text-[10px]">Search YouTube, then copy the video URL and paste it below.</p>
                <div className="mt-2 px-3 py-2 bg-gradient-to-r from-red-500/10 to-purple-500/10 rounded-xl border border-red-400/10">
                  <p className="text-red-300/60 text-[10px] font-bold uppercase tracking-wider">Coming Soon</p>
                  <p className="text-white/40 text-[10px] mt-0.5">YouTube API integration — search, preview, and auto-add videos without leaving the app.</p>
                </div>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Audio Read-Aloud URL</label>
                <input
                  value={draft.audioUrl || ''}
                  onChange={e => update('audioUrl', e.target.value.slice(0, 300))}
                  className="w-full mt-1 p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                  placeholder="https://youtu.be/..."
                />
              </div>

              <div>
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Technique Videos</p>
                {(draft.techniqueVideos || []).map((vid, i) => (
                  <div key={i} className="glass-surface rounded-2xl p-4 mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-500 text-xs uppercase tracking-wider font-medium">Video {i + 1}</label>
                      <button
                        onClick={() => update('techniqueVideos', draft.techniqueVideos.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <input
                      value={vid.title}
                      onChange={e => {
                        const next = [...draft.techniqueVideos];
                        next[i] = { ...next[i], title: e.target.value.slice(0, 100) };
                        update('techniqueVideos', next);
                      }}
                      className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none mb-2"
                      placeholder="Title (e.g. Layering)"
                    />
                    <input
                      value={vid.url}
                      onChange={e => {
                        const next = [...draft.techniqueVideos];
                        next[i] = { ...next[i], url: e.target.value.slice(0, 300) };
                        update('techniqueVideos', next);
                      }}
                      className="w-full p-2 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:border-purple-400 focus:outline-none"
                      placeholder="https://youtu.be/..."
                    />
                  </div>
                ))}

                {(draft.techniqueVideos || []).length < 5 && (
                  <button
                    onClick={() => update('techniqueVideos', [...(draft.techniqueVideos || []), { title: '', url: '' }])}
                    className="w-full py-2 border-2 border-dashed border-white/20 text-white/50 text-sm rounded-xl hover:border-white/40 hover:text-white/70 transition-all"
                  >
                    + Add Video
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {lessonBuilderStep === 5 && (
            <div className="animate-fade-in space-y-4">
              <div className={`bg-gradient-to-br ${draft.coverColor} rounded-3xl p-6 text-center`}>
                <div className="text-5xl mb-2">{draft.coverEmoji}</div>
                <h2 className="text-2xl font-black text-white">{draft.title || 'Untitled'}</h2>
                {draft.author && <p className="text-white/80 text-sm">by {draft.author}</p>}
              </div>

              {draft.hook && (
                <div className="glass-surface rounded-2xl p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Hook</p>
                  <p className="text-gray-700 text-sm">{draft.hook}</p>
                </div>
              )}

              <div className="glass-surface rounded-2xl p-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Corner Post</p>
                <p className="text-gray-700 text-sm">{cornerPosts.find(p => p.id === draft.suggestedCornerPost)?.icon} {cornerPosts.find(p => p.id === draft.suggestedCornerPost)?.name}</p>
              </div>

              <div className="glass-surface rounded-2xl p-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2">Essential Questions ({draft.essentialQuestions.filter(q => q.trim()).length})</p>
                {draft.essentialQuestions.filter(q => q.trim()).map((q, i) => (
                  <p key={i} className="text-gray-700 text-sm mb-1">{i + 1}. {q}</p>
                ))}
                {!draft.essentialQuestions.some(q => q.trim()) && <p className="text-red-400 text-sm italic">At least one question is required</p>}
              </div>

              {draft.vocabulary.some(v => v.term.trim()) && (
                <div className="glass-surface rounded-2xl p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2">Vocabulary ({draft.vocabulary.filter(v => v.term.trim()).length})</p>
                  {draft.vocabulary.filter(v => v.term.trim()).map((v, i) => (
                    <p key={i} className="text-gray-700 text-sm mb-1"><strong>{v.term}</strong> — {v.def}</p>
                  ))}
                </div>
              )}

              {draft.artActivity && (
                <div className="glass-surface rounded-2xl p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Art Activity</p>
                  <p className="text-gray-700 text-sm">{draft.artActivity}</p>
                </div>
              )}

              {(draft.materials || []).length > 0 && (
                <div className="glass-surface rounded-2xl p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2">Materials</p>
                  <div className="flex flex-wrap gap-2">
                    {draft.materials.filter(m => m.trim()).map((m, i) => (
                      <span key={i} className="bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full text-xs">{m}</span>
                    ))}
                  </div>
                </div>
              )}

              {!canSave && (
                <p className="text-red-300 text-sm text-center">Add a title and at least one essential question to save.</p>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="mt-6 space-y-3">
            {lessonBuilderStep < 5 ? (
              <button
                onClick={() => setLessonBuilderStep(s => s + 1)}
                className="w-full py-4 bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95"
              >
                Next
              </button>
            ) : (
              <>
                <button
                  onClick={handleSaveLesson}
                  disabled={!canSave}
                  className={`w-full py-4 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 ${
                    canSave
                      ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:shadow-2xl'
                      : 'bg-gray-500 opacity-50 cursor-not-allowed'
                  }`}
                >
                  {editingLessonId ? 'Save Changes' : 'Create Lesson'}
                </button>
                {canSave && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        handleSaveLesson();
                        const lesson = { ...draft, id: editingLessonId || ('custom_' + Date.now()) };
                        if (publishToLibrary(lesson)) {
                          alert('Lesson published to the 4TZ Library!');
                        } else {
                          alert('This lesson is already in the library.');
                        }
                      }}
                      className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold text-sm rounded-2xl transition-all active:scale-95 hover:shadow-lg"
                    >
                      📚 Save & Publish
                    </button>
                    <button
                      onClick={async () => {
                        const code = exportLessonCode(draft);
                        try {
                          await navigator.clipboard.writeText(code);
                          alert('Lesson code copied! Share it with other teachers.');
                        } catch {
                          prompt('Copy this lesson code:', code);
                        }
                      }}
                      className="py-3 px-4 bg-white/10 border border-white/20 text-white/70 font-bold text-sm rounded-2xl transition-all active:scale-95 hover:bg-white/20"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Picker (card stack)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonPicker') {
    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={() => setCurrentScreen('home')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-1 animate-fade-in`}>
            {'\u{1F4DA}'} Today's Lessons
          </h1>
          <p className="text-white/60 text-sm mb-4 animate-fade-in">Pick a book adventure!</p>

          {/* First-time welcome nudge */}
          {completedMissions.length === 0 && portfolioEntries.length === 0 && (
            <div className="glass rounded-2xl p-4 mb-4 animate-scale-in text-center">
              <p className="text-white font-bold text-sm mb-1">{'\u{1F44B}'} Welcome, {avatarName || 'Explorer'}!</p>
              <p className="text-white/60 text-xs">Start with your first lesson below. Tap "Start" to begin!</p>
            </div>
          )}

          {/* Shopping list — combined materials from up to 3 upcoming lessons */}
          {(() => {
            const dotL = lessonPlans.find(l => l.id === 'the-dot');
            const otherB = lessonPlans.filter(l => l.id !== 'the-dot');
            const ordered = [dotL, ...otherB, ...customLessons].filter(Boolean);
            const upcoming = ordered
              .filter(l => !portfolioEntries.some(e => e.lessonId === l.id))
              .slice(0, 3);
            const materialsMap = {};
            upcoming.forEach(l => {
              (l.materials || []).forEach(m => {
                const key = m.toLowerCase().trim();
                if (!materialsMap[key]) materialsMap[key] = { label: m, lessons: [] };
                materialsMap[key].lessons.push(l.coverEmoji);
              });
            });
            const items = Object.values(materialsMap);
            if (items.length === 0) return null;
            return (
              <div className="glass-surface rounded-2xl p-4 mb-5 animate-fade-in">
                <p className="text-gray-600 text-xs font-medium uppercase tracking-wider mb-2">
                  {'\u{1F6D2}'} Shopping List — Next {upcoming.length} Lesson{upcoming.length !== 1 ? 's' : ''}
                </p>
                <div className="space-y-1.5">
                  {items.map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm">{'\u{25CB}'}</span>
                      <span className="text-gray-800 text-sm flex-1">{item.label}</span>
                      <span className="text-xs text-gray-400">{item.lessons.join(' ')}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-[10px] mt-2">Emojis show which lesson needs each item.</p>
              </div>
            );
          })()}

          {/* Create Lesson button (teacher mode) */}
          {teacherMode && (
            <button
              onClick={() => {
                setEditingLessonId(null);
                setLessonBuilderData({
                  title: '', author: '', coverEmoji: '\u{1F4D6}', coverColor: 'from-blue-400 to-indigo-500',
                  hook: '', audioUrl: '', artActivity: '',
                  essentialQuestions: ['', '', ''],
                  vocabulary: [{ term: '', def: '' }],
                  techniqueVideos: [],
                  materials: [],
                  procedures: '',
                  suggestedCornerPost: 'environmental',
                });
                setLessonBuilderStep(0);
                setCurrentScreen('lessonBuilder');
              }}
              className="w-full mb-4 py-3 border-2 border-dashed border-white/30 text-white/70 font-bold rounded-2xl transition-all duration-300 active:scale-95 hover:border-white/50 hover:text-white animate-fade-in"
            >
              + Create New Lesson
            </button>
          )}

          <div className="space-y-4 animate-slide-up">
            {(() => {
              // Determine unlock order: The Dot first, then sequential.
              // Reorder so The Dot is first among built-ins.
              const dotLesson = lessonPlans.find(l => l.id === 'the-dot');
              const otherBuiltins = lessonPlans.filter(l => l.id !== 'the-dot');
              const ordered = [dotLesson, ...otherBuiltins, ...customLessons].filter(Boolean);

              // Find how many are completed sequentially to determine unlock frontier
              let unlockedCount = 1; // first lesson always unlocked
              for (let i = 0; i < ordered.length; i++) {
                if (portfolioEntries.some(e => e.lessonId === ordered[i].id)) {
                  unlockedCount = Math.max(unlockedCount, i + 2); // completing lesson i unlocks i+1
                }
              }

              return ordered.map((lesson, idx) => {
              const completed = portfolioEntries.some(e => e.lessonId === lesson.id);
              const isCustom = lesson.id?.startsWith('custom_');
              const locked = !teacherMode && !completed && idx >= unlockedCount;

              return (
                <div
                  key={lesson.id}
                  className={`rounded-3xl overflow-hidden shadow-xl transition-all duration-300 ${locked ? 'opacity-50 grayscale' : ''}`}
                >
                  <div className={`bg-gradient-to-br ${lesson.coverColor} p-6 relative`}>
                    {completed && (
                      <span className="absolute top-3 right-3 bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1">
                        {'\u{2705}'} Done
                      </span>
                    )}
                    {locked && (
                      <span className="absolute top-3 right-3 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1">
                        {'\u{1F512}'} Locked
                      </span>
                    )}
                    {isCustom && teacherMode && (
                      <div className="absolute top-3 left-3 flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingLessonId(lesson.id);
                            setLessonBuilderData({ ...lesson });
                            setLessonBuilderStep(0);
                            setCurrentScreen('lessonBuilder');
                          }}
                          className="bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white hover:bg-white/50 transition-all"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${lesson.title}"?`)) {
                              setCustomLessons(prev => prev.filter(l => l.id !== lesson.id));
                            }
                          }}
                          className="bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold text-white hover:bg-red-400/50 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                    <div className="text-6xl mb-3">{lesson.coverEmoji}</div>
                    <h2 className="text-2xl font-black text-white">{lesson.title}</h2>
                    <p className="text-white/80 text-sm font-medium">by {lesson.author}</p>
                  </div>
                  <div className="glass-surface p-5">
                    <p className="text-gray-700 text-sm mb-4">{lesson.hook}</p>
                    {locked ? (
                      <div className="w-full py-3 bg-gray-300 text-gray-500 font-bold rounded-xl text-center">
                        {'\u{1F512}'} Complete the previous lesson to unlock
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedLesson(lesson);
                          setLessonStep(0);
                          setLessonQuestionAnswers([]);
                          setBlueprintText('');
                          setBlueprintSketch(null);
                          ['lessonStep', 'lessonQuestionAnswers', 'blueprintText', 'blueprintSketch'].forEach(k => safeLocalStorage.setItem(k, ''));
                          setCurrentScreen('lessonRead');
                          if (textToSpeech) speak(lesson.title + '. ' + lesson.hook);
                        }}
                        className={`w-full py-3 text-white font-bold rounded-xl transition-all duration-300 active:scale-95 ${
                          completed
                            ? 'bg-gradient-to-r from-gray-400 to-gray-500'
                            : 'bg-gradient-to-r from-purple-600 to-blue-500'
                        }`}
                      >
                        {completed ? 'Do Again' : 'Start'}
                      </button>
                    )}
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // Safety net: if on a lesson screen but selectedLesson is null, redirect home
  if (['lessonRead', 'lessonQuestions', 'lessonBlueprint', 'lessonCornerPost', 'lessonArtUpload'].includes(currentScreen) && !selectedLesson) {
    setCurrentScreen('home');
    return null;
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Read / Listen screen
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonRead' && selectedLesson) {
    const lesson = selectedLesson;

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={() => setCurrentScreen('lessonPicker')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {renderLessonProgress(0)}

          {/* Book hero with mission briefing feel */}
          <div className={`bg-gradient-to-br ${lesson.coverColor} rounded-3xl p-8 text-center mb-4 animate-fade-in relative overflow-hidden`}>
            {/* Subtle animated shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="relative z-10">
              <div className="text-7xl mb-3 animate-float">{lesson.coverEmoji}</div>
              <h1 className="text-2xl font-black text-white">{lesson.title}</h1>
              <p className="text-white/80 text-sm">by {lesson.author}</p>
            </div>
          </div>

          {/* Hook / Mission teaser — the exciting part */}
          <div className="glass rounded-2xl p-5 mb-4 animate-scale-in border border-white/10">
            <p className="text-cyan-300 text-xs font-bold uppercase tracking-widest mb-2">Mission Briefing</p>
            <p className="text-white text-lg font-medium italic leading-relaxed">"{lesson.hook}"</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xl">{'\u{1F3A8}'}</span>
              <p className="text-white/70 text-sm"><b>Your challenge:</b> {lesson.artActivity}</p>
            </div>
          </div>

          {/* Step 1: Listen to the story — big, inviting */}
          {lesson.audioUrl ? (
            <a
              href={lesson.audioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-4 animate-slide-up"
            >
              <div className="bg-gradient-to-r from-rose-500 to-red-500 rounded-2xl p-5 shadow-lg shadow-rose-500/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl active:scale-95">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 animate-pulse-soft">
                    <Play size={28} className="text-white ml-1" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Step 1: Hear the Story!</p>
                    <p className="text-white/70 text-sm">Tap to listen to the read-aloud</p>
                  </div>
                </div>
              </div>
            </a>
          ) : (
            <div className="glass-surface rounded-2xl p-4 mb-4 animate-slide-up">
              <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-1">Today's Activity</p>
              <p className="text-gray-800 font-medium text-sm">{lesson.artActivity}</p>
            </div>
          )}

          {/* Vocabulary — collapsible to reduce overwhelm */}
          <details className="glass-surface rounded-2xl mb-4 animate-slide-up group">
            <summary className="p-4 cursor-pointer flex items-center justify-between list-none">
              <span className="text-gray-500 text-xs uppercase tracking-wider font-medium">{'\u{1F4D6}'} Step 2: Learn the Words ({lesson.vocabulary.length})</span>
              <span className="text-gray-400 text-xs group-open:rotate-180 transition-transform duration-300">{'\u{25BC}'}</span>
            </summary>
            <div className="px-4 pb-4 space-y-2">
              {lesson.vocabulary.map(v => (
                <div key={v.term} className="bg-white/60 rounded-xl p-3">
                  <span className="font-bold text-gray-800 text-sm">{v.term}</span>
                  <span className="text-gray-600 text-sm"> — {v.def}</span>
                </div>
              ))}
            </div>
          </details>

          {/* Materials list */}
          {lesson.materials && lesson.materials.length > 0 && (
            <div className="glass-surface rounded-2xl p-4 mb-4 animate-slide-up">
              <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-3">{'\u{1F9F0}'} Materials</p>
              <div className="flex flex-wrap gap-2">
                {lesson.materials.map((m, i) => (
                  <span key={i} className="bg-white/60 rounded-full px-3 py-1 text-gray-700 text-sm">{m}</span>
                ))}
              </div>
            </div>
          )}

          {/* Procedures (teacher-only) */}
          {teacherMode && lesson.procedures && (
            <div className="glass-surface rounded-2xl p-4 mb-4 animate-slide-up border border-amber-200">
              <p className="text-amber-600 text-xs uppercase tracking-wider font-medium mb-2">{'\u{1F4CB}'} Teacher Procedures</p>
              <p className="text-gray-700 text-sm whitespace-pre-line">{lesson.procedures}</p>
            </div>
          )}

          {/* Technique videos */}
          {lesson.techniqueVideos && (
            <div className="glass-surface rounded-2xl p-4 mb-4 animate-slide-up">
              <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-3">{'\u{1F3AC}'} Step 3: Watch &amp; Learn</p>
              <div className="space-y-2">
                {lesson.techniqueVideos.map(vid => (
                  <a
                    key={vid.title}
                    href={vid.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white/60 rounded-xl p-3 transition-all duration-300 hover:bg-white/80 active:scale-95"
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Play size={14} className="text-purple-600 ml-0.5" />
                    </div>
                    <span className="text-gray-800 text-sm font-medium">{vid.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Full lesson plan PDF */}
          {teacherMode && lesson.lessonPlanPdf && (
            <a
              href={lesson.lessonPlanPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 glass-surface rounded-2xl p-4 mb-4 transition-all duration-300 hover:-translate-y-0.5 active:scale-95 animate-slide-up"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">📄</span>
              </div>
              <div>
                <p className="text-gray-800 font-bold text-sm">{teacherMode ? 'Full Lesson Plan (PDF)' : 'Lesson Blueprint'}</p>
                <p className="text-gray-500 text-xs">{teacherMode ? 'Procedures, standards, assessments' : 'See the full lesson details'}</p>
              </div>
            </a>
          )}

          {/* Ready to Create — action button */}
          <div className="mt-4 animate-slide-up">
            <button
              onClick={() => {
                setLessonCornerPostPick(null);
                setCurrentScreen('lessonCornerPostPick');
              }}
              className="w-full py-5 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white text-xl font-black rounded-2xl shadow-lg shadow-emerald-500/30 transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-1 animate-pulse-soft"
            >
              {'\u{1F680}'} Ready to Create!
            </button>
            <p className="text-center text-white/40 text-xs mt-2">Heard the story? Learned the words? Let's go!</p>
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Corner Post Check-in (before questions)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonCornerPostPick' && selectedLesson) {
    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden min-h-screen">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('lessonRead')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          <div className="text-center mb-6 animate-fade-in">
            <div className="text-4xl mb-3">🧭</div>
            <h1 className="text-2xl font-black text-white">Before you create...</h1>
            <p className="text-white/60 text-sm mt-2">Which part of you feels most ready for this adventure?</p>
          </div>

          <div className="grid grid-cols-2 gap-3 animate-slide-up">
            {cornerPosts.map((post) => (
              <button
                key={post.id}
                onClick={() => setLessonCornerPostPick(post.id)}
                className={`rounded-2xl p-4 text-center transition-all duration-300 active:scale-95 ${
                  lessonCornerPostPick === post.id
                    ? `${post.color} ring-2 ring-white shadow-xl scale-105`
                    : 'glass hover:bg-white/15'
                }`}
              >
                <span className="text-2xl">{post.icon}</span>
                <p className={`text-sm font-bold mt-1 ${lessonCornerPostPick === post.id ? 'text-white' : 'text-white/80'}`}>{post.name}</p>
              </button>
            ))}
          </div>

          {lessonCornerPostPick && (
            <div className="mt-4 animate-scale-in">
              <p className="text-center text-cyan-300/80 text-sm mb-4">
                {cornerPosts.find(p => p.id === lessonCornerPostPick)?.icon} {cornerPosts.find(p => p.id === lessonCornerPostPick)?.description}
              </p>
              <button
                onClick={() => {
                  setLessonStep(0);
                  setLessonQuestionAnswers([]);
                  setCurrentScreen('lessonQuestions');
                  if (textToSpeech) speak(selectedLesson.essentialQuestions[0]);
                }}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl"
              >
                Let's Go! 🌊
              </button>
            </div>
          )}

          <p className="text-center text-white/30 text-xs mt-4">
            You can have lots of splashes before your final one!
          </p>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Essential Questions (one at a time)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonQuestions' && selectedLesson) {
    const lesson = selectedLesson;
    const totalQs = lesson.essentialQuestions.length;
    const currentQ = lesson.essentialQuestions[lessonStep] || '';
    const currentAnswer = lessonQuestionAnswers[lessonStep] || '';
    const isLast = lessonStep === totalQs - 1;

    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => {
              if (lessonStep > 0) {
                setLessonStep(s => s - 1);
              } else {
                setCurrentScreen('lessonRead');
              }
            }}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {renderLessonProgress(1)}

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {Array.from({ length: totalQs }).map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === lessonStep ? 'w-8 bg-purple-400' :
                  i < lessonStep ? 'w-4 bg-purple-400/60' : 'w-4 bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Question */}
          <div key={lessonStep} className="animate-fade-in">
            <div className="text-5xl text-center mb-4">{'\u{1F4AC}'}</div>
            <h2 className={`${simpleMode ? 'text-2xl' : 'text-xl'} font-bold text-white text-center mb-6`}>
              {currentQ}
            </h2>

            {/* Answer input */}
            <div className="glass-surface rounded-2xl p-4 mb-4">
              <textarea
                value={currentAnswer}
                onChange={(e) => {
                  const sanitized = e.target.value.slice(0, 300).replace(/<\/?[^>]+(>|$)/g, '');
                  setLessonQuestionAnswers(prev => {
                    const next = [...prev];
                    next[lessonStep] = sanitized;
                    return next;
                  });
                }}
                className="w-full bg-transparent text-gray-800 text-base resize-none focus:outline-none min-h-[80px] placeholder-gray-400"
                placeholder="Type or speak your answer..."
                maxLength={300}
                aria-label={currentQ}
              />
              <div className="flex items-center justify-between mt-1">
                {speechSupported ? (
                  <button
                    onClick={() => {
                      if (isListening) {
                        stopListening();
                      } else {
                        startListening((transcript) => {
                          setLessonQuestionAnswers(prev => {
                            const next = [...prev];
                            const current = next[lessonStep] || '';
                            next[lessonStep] = (current ? current + ' ' + transcript : transcript).slice(0, 300);
                            return next;
                          });
                        });
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                      isListening
                        ? 'bg-red-100 text-red-600 animate-pulse'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    aria-label={isListening ? 'Stop recording' : 'Start voice input'}
                  >
                    {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                    {isListening ? 'Listening...' : 'Speak'}
                  </button>
                ) : (
                  <span />
                )}
                <p className="text-gray-400 text-xs">{currentAnswer.length}/300</p>
              </div>
            </div>

            {/* Next / Continue */}
            <button
              onClick={() => {
                if (!isLast) {
                  setLessonStep(s => s + 1);
                  if (textToSpeech) speak(lesson.essentialQuestions[lessonStep + 1]);
                } else {
                  setBlueprintText('');
                  setBlueprintSketch(null);
                  setCurrentScreen('lessonBlueprint');
                  if (textToSpeech) speak('Plan your art!');
                }
              }}
              className={`w-full py-4 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl ${
                isLast
                  ? 'bg-gradient-to-r from-green-500 to-teal-500'
                  : 'bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400'
              }`}
            >
              {isLast ? 'Plan Your Art' : 'Next'}
            </button>

            <button
              onClick={() => {
                if (!isLast) {
                  setLessonStep(s => s + 1);
                  if (textToSpeech && lesson.essentialQuestions[lessonStep + 1]) speak(lesson.essentialQuestions[lessonStep + 1]);
                } else {
                  setBlueprintText('');
                  setBlueprintSketch(null);
                  setCurrentScreen('lessonBlueprint');
                  if (textToSpeech) speak('Plan your art!');
                }
              }}
              className="mt-3 w-full text-center text-white/30 text-xs hover:text-white/50 transition-colors"
            >
              Skip this one
            </button>
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Blueprint (Plan Your Art)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonBlueprint' && selectedLesson) {
    const lesson = selectedLesson;

    const handleLessonBlueprintSketch = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload an image file (JPEG, PNG, GIF, or WEBP).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image is too large. Please choose an image smaller than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        compressImage(ev.target.result).then(compressed => {
          setBlueprintSketch(compressed);
        }).catch(() => {
          alert('Error processing image. Please try again.');
        });
      };
      reader.onerror = () => alert('Error reading file. Please try again.');
      reader.readAsDataURL(file);
    };

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('lessonQuestions')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Questions"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {renderLessonProgress(2)}

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2 animate-fade-in`}>
            {'\u{270F}\u{FE0F}'} Plan Your Art
          </h1>
          <p className="text-white/60 text-sm mb-2 animate-fade-in">
            {lesson.artActivity}
          </p>

          {/* First draft encouragement */}
          <div className="glass rounded-2xl p-4 mb-6 animate-fade-in-delay border border-cyan-400/20">
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">🌊</span>
              <div>
                <p className="text-white font-bold text-sm">This is your first splash!</p>
                <p className="text-white/60 text-xs mt-1">
                  This is just a first draft — it doesn't have to be perfect! Sketch out your ideas,
                  try things, and remember: you'll get to revise and improve. The best art comes from
                  making a splash and seeing what happens. <span className="text-cyan-300 font-bold">Make a Splash!</span> 🤿
                </p>
              </div>
            </div>
          </div>

          {/* Collapsible Reference Panel */}
          <details
            open={blueprintRefOpen}
            onToggle={(e) => setBlueprintRefOpen(e.target.open)}
            className="glass rounded-2xl mb-4 animate-slide-up"
          >
            <summary className="cursor-pointer p-4 flex items-center gap-2 text-white font-medium text-sm select-none">
              <span className="text-lg">{'\u{1F4DA}'}</span> Tips & Vocab
              <span className="text-white/40 text-xs ml-auto">tap for tips & vocab</span>
            </summary>
            <div className="px-4 pb-4 space-y-3">
              {/* Vocabulary */}
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider font-medium mb-2">{'\u{1F4D6}'} Vocabulary</p>
                <div className="space-y-1.5">
                  {lesson.vocabulary.map(v => (
                    <div key={v.term} className="bg-white/10 rounded-xl p-2.5">
                      <span className="font-bold text-white text-sm">{v.term}</span>
                      <span className="text-white/70 text-sm"> — {v.def}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Technique videos */}
              {lesson.techniqueVideos && (
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider font-medium mb-2">{'\u{1F3AC}'} Technique Videos</p>
                  <div className="flex flex-wrap gap-2">
                    {lesson.techniqueVideos.map(vid => (
                      <a
                        key={vid.title}
                        href={vid.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white/10 text-white text-xs rounded-full px-3 py-1.5 hover:bg-white/20 transition-all"
                      >
                        {'\u{25B6}\u{FE0F}'} {vid.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {/* Art activity reminder */}
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider font-medium mb-1">{'\u{1F3A8}'} Activity</p>
                <p className="text-white/80 text-sm">{lesson.artActivity}</p>
              </div>
            </div>
          </details>

          {/* Grandee Guide Card */}
          {blueprintGuideStep >= 0 && (() => {
            const guideIcon = cornerPosts.find(cp => cp.id === lesson.suggestedCornerPost)?.icon || '\u{1F9E0}';
            const tips = [
              lesson.essentialQuestions?.[0] || 'What inspires you about this activity?',
              'What materials or techniques will you try?',
              'How will you know your art turned out the way you wanted?',
            ];
            const tip = tips[blueprintGuideStep] || tips[tips.length - 1];
            return (
              <div className="glass rounded-2xl p-4 mb-4 animate-slide-up">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">{guideIcon}</span>
                  <p className={`${simpleMode ? 'text-lg' : 'text-sm'} text-white font-medium leading-relaxed flex-1`}>{tip}</p>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-1.5">
                    {tips.map((_, i) => (
                      <span key={i} className={`w-2 h-2 rounded-full ${i === blueprintGuideStep ? 'bg-white' : 'bg-white/30'}`} />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {blueprintGuideStep < tips.length - 1 && (
                      <button
                        onClick={() => {
                          const next = blueprintGuideStep + 1;
                          setBlueprintGuideStep(next);
                          if (textToSpeech) speak(tips[next]);
                        }}
                        className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium hover:bg-white/30 transition-all"
                      >
                        Next tip {'\u{2192}'}
                      </button>
                    )}
                    <button
                      onClick={() => setBlueprintGuideStep(-1)}
                      className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-medium hover:bg-white/20 transition-all"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Text Plan Section */}
          <div className="glass-surface rounded-3xl p-6 mb-4 animate-slide-up">
            <h2 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold flex items-center gap-2">
              <span className="text-lg">{'\u{1F4AD}'}</span> What's your plan?
            </h2>
            <textarea
              value={blueprintText}
              onChange={(e) => {
                const sanitized = e.target.value.slice(0, 500).replace(/<\/?[^>]+(>|$)/g, '');
                setBlueprintText(sanitized);
              }}
              className="w-full bg-white/50 text-gray-800 text-base rounded-xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 min-h-[120px] placeholder-gray-400"
              placeholder="Tell us about what you want to create..."
              maxLength={500}
              aria-label="Describe your plan"
            />
            <div className="flex items-center justify-between mt-2">
              {speechSupported ? (
                <button
                  onClick={() => {
                    if (isListening) {
                      stopListening();
                    } else {
                      startListening((transcript) => {
                        setBlueprintText(prev => {
                          const joined = prev ? (prev + ' ' + transcript) : transcript;
                          return joined.slice(0, 500);
                        });
                      });
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                    isListening
                      ? 'bg-red-100 text-red-600 animate-pulse'
                      : 'bg-white/70 text-gray-600 hover:bg-white'
                  }`}
                  aria-label={isListening ? 'Stop recording' : 'Start voice input'}
                >
                  {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                  {isListening ? 'Listening...' : 'Speak'}
                </button>
              ) : (
                <span />
              )}
              <p className="text-gray-400 text-xs">{blueprintText.length}/500</p>
            </div>
          </div>

          {/* Sketch Upload Section */}
          <div className="glass-surface rounded-3xl p-6 mb-6 animate-fade-in-delay">
            <h2 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold flex items-center gap-2">
              <span className="text-lg">{'\u{270F}\u{FE0F}'}</span> Draw or snap your blueprint
            </h2>

            {!blueprintSketch ? (
              <label className="block cursor-pointer bg-white/50 rounded-2xl p-6 text-center transition-all duration-300 hover:bg-white/70 active:scale-95 border-2 border-dashed border-gray-300">
                <Camera size={40} className="text-gray-400 mx-auto mb-2" />
                <span className="text-gray-700 font-medium text-sm block">Tap to add a sketch</span>
                <span className="text-gray-400 text-xs block mt-1">Draw on paper, then take a photo</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleLessonBlueprintSketch}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="space-y-3">
                <img
                  src={blueprintSketch}
                  alt="Your blueprint sketch"
                  className="w-full rounded-2xl object-contain max-h-48 bg-white/50 p-2"
                />
                <button
                  onClick={() => setBlueprintSketch(null)}
                  className="w-full text-center text-gray-500 text-sm underline hover:text-gray-700 transition-colors py-2"
                >
                  Remove sketch
                </button>
              </div>
            )}
          </div>

          {/* Next Button */}
          <button
            onClick={() => {
              setCurrentScreen('lessonCornerPost');
              if (textToSpeech) speak('What does this story connect to?');
            }}
            className={`w-full py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white ${simpleMode ? 'text-2xl' : 'text-xl'} font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5`}
          >
            Next
          </button>

          {/* Skip option */}
          <button
            onClick={() => {
              setCurrentScreen('lessonCornerPost');
              if (textToSpeech) speak('What does this story connect to?');
            }}
            className="mt-3 w-full text-center text-white/40 text-sm hover:text-white/60 transition-colors py-2"
          >
            Skip blueprint
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Corner Post Picker
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonCornerPost' && selectedLesson) {
    const lesson = selectedLesson;

    const handleCornerPostPick = (postId) => {
      setPortfolioUploadTag(postId);
      // Clear any previous upload so the art upload screen starts fresh
      setPortfolioUploadImage(null);
      setPortfolioUploadMediaType('image');
      setPortfolioUploadMediaBlob(null);
      setCurrentScreen('lessonArtUpload');
      if (textToSpeech) speak('Capture your art!');
    };

    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('lessonBlueprint')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {renderLessonProgress(3)}

          <div className="text-5xl text-center mb-3 animate-fade-in">{'\u{1F9ED}'}</div>
          <h1 className={`${simpleMode ? 'text-2xl' : 'text-xl'} font-bold text-white text-center mb-2 animate-fade-in`}>
            What does this story connect to?
          </h1>
          <p className="text-white/60 text-sm text-center mb-6 animate-fade-in">Pick the corner post that fits best</p>

          <div className="grid grid-cols-2 gap-3 mb-6 animate-slide-up">
            {cornerPosts.map(post => {
              const isSuggested = post.id === lesson.suggestedCornerPost;
              return (
                <button
                  key={post.id}
                  onClick={() => handleCornerPostPick(post.id)}
                  className={`glass rounded-2xl p-4 flex flex-col items-center text-center transition-all duration-300 active:scale-95 hover:bg-white/15 hover:scale-105 relative`}
                >
                  {isSuggested && (
                    <span className="absolute top-2 right-2 text-sm" title="Suggested">{'\u{2B50}'}</span>
                  )}
                  <span className="text-3xl mb-2">{post.icon}</span>
                  <span className="text-white text-sm font-bold">{post.name}</span>
                  <span className="text-white/50 text-[10px] mt-0.5">
                    {CORNER_POST_SKILLS[post.id.slice(0, 3)]?.skill || ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON — Art Upload (capture finished artwork)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonArtUpload' && selectedLesson) {
    const lesson = selectedLesson;

    const proceedToCritique = () => {
      setPortfolioCritiqueStep(0);
      setPortfolioCritiqueAnswers({ describe: '', analyze: '', interpret: '', evaluate: '' });
      setPortfolioRubricRatings({ planning: 0, technique: 0, creativity: 0, cornerPost: 0, principle: 0, selfCritique: 0 });
      setCurrentScreen('portfolioCritique');
      if (textToSpeech) speak('Rate your work!');
    };

    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('lessonCornerPost')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {renderLessonProgress(4)}

          <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2 animate-fade-in`}>
            {'\u{1F4F8}'} Capture Your Art
          </h1>
          <p className="text-white/60 text-sm mb-6 animate-fade-in">Take a photo or video of what you made!</p>

          {!portfolioUploadImage ? (
            <div className="animate-slide-up">
              {/* Camera capture */}
              <label className="block cursor-pointer glass rounded-3xl p-8 text-center transition-all duration-300 hover:bg-white/20 active:scale-95 border-2 border-dashed border-white/30 mb-4">
                <Camera size={48} className="text-white/60 mx-auto mb-3" />
                <span className="text-white font-medium text-lg block">Tap to take a photo</span>
                <span className="text-white/40 text-sm block mt-1">Opens your camera</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePortfolioUpload}
                  className="hidden"
                />
              </label>

              {/* Video capture */}
              <label className="block cursor-pointer glass rounded-3xl p-8 text-center transition-all duration-300 hover:bg-white/20 active:scale-95 border-2 border-dashed border-white/30 mb-4">
                <Video size={48} className="text-white/60 mx-auto mb-3" />
                <span className="text-white font-medium text-lg block">Record a video</span>
                <span className="text-white/40 text-sm block mt-1">60 seconds max</span>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/mov"
                  capture="environment"
                  onChange={handlePortfolioVideoUpload}
                  className="hidden"
                />
              </label>

              {/* Gallery picker */}
              <label className="cursor-pointer block text-center text-white/40 text-sm underline hover:text-white/60 transition-colors mb-4">
                Or choose from your photos
                <input
                  type="file"
                  accept="image/*,video/mp4,video/quicktime,video/webm,video/mov"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.type.startsWith('video/')) handlePortfolioVideoUpload(e);
                    else handlePortfolioUpload(e);
                  }}
                  className="hidden"
                />
              </label>

              {/* Skip option */}
              <button
                onClick={() => {
                  // Use generated lesson thumbnail as fallback
                  const generateLessonThumb = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 200;
                    canvas.height = 200;
                    const ctx = canvas.getContext('2d');
                    const grad = ctx.createLinearGradient(0, 0, 200, 200);
                    const colorMap = {
                      'from-green-400 to-teal-500': ['#4ade80', '#14b8a6'],
                      'from-purple-400 to-indigo-500': ['#c084fc', '#6366f1'],
                      'from-pink-400 to-rose-500': ['#f472b6', '#f43f5e'],
                    };
                    const [c1, c2] = colorMap[lesson.coverColor] || ['#8b5cf6', '#3b82f6'];
                    grad.addColorStop(0, c1);
                    grad.addColorStop(1, c2);
                    ctx.fillStyle = grad;
                    ctx.fillRect(0, 0, 200, 200);
                    ctx.font = '72px serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(lesson.coverEmoji, 100, 90);
                    ctx.font = 'bold 16px sans-serif';
                    ctx.fillStyle = 'white';
                    ctx.fillText(lesson.title, 100, 160);
                    return canvas.toDataURL('image/jpeg', 0.8);
                  };
                  setPortfolioUploadImage(generateLessonThumb());
                  setPortfolioUploadMediaBlob(null);
                  proceedToCritique();
                }}
                className="block w-full text-center text-white/40 text-sm underline hover:text-white/60 transition-colors"
              >
                Skip — I don't have my art handy
              </button>
            </div>
          ) : (
            <div className="animate-scale-in">
              {/* Preview */}
              <div className="glass rounded-3xl p-4 mb-4">
                <div className="relative">
                  <img src={portfolioUploadImage} alt="Preview" className="w-full rounded-2xl object-contain max-h-64" />
                  {portfolioUploadMediaType === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
                        <Play size={32} className="text-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={proceedToCritique}
                className="w-full py-4 bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl"
              >
                Looks Good!
              </button>
              <button
                onClick={() => {
                  setPortfolioUploadImage(null);
                  setPortfolioUploadMediaType('image');
                  setPortfolioUploadMediaBlob(null);
                }}
                className="mt-3 w-full text-center text-white/40 text-sm underline hover:text-white/60 transition-colors"
              >
                Try again
              </button>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Tag to Corner Post
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolioTag') {
    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => setCurrentScreen('portfolioUpload')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          {/* Small image preview */}
          <div className="flex justify-center mb-4 animate-fade-in">
            <img src={portfolioUploadImage} alt="Preview" className="w-16 h-16 rounded-xl object-cover shadow-lg" />
          </div>

          <h1 className={`${simpleMode ? 'text-2xl' : 'text-xl'} font-bold text-white mb-1 text-center animate-fade-in`}>
            What kind of work is this?
          </h1>
          <p className="text-white/60 text-sm mb-6 text-center animate-fade-in">Pick the area it fits best</p>

          <div className="grid grid-cols-2 gap-3 mb-6 animate-slide-up">
            {cornerPosts.map(post => (
              <button
                key={post.id}
                onClick={() => setPortfolioUploadTag(post.id)}
                className={`glass rounded-2xl p-4 flex flex-col items-center text-center transition-all duration-300 active:scale-95 ${
                  portfolioUploadTag === post.id
                    ? 'ring-2 ring-white scale-105 bg-white/20 shadow-lg'
                    : 'hover:bg-white/15 hover:scale-105'
                }`}
              >
                <span className="text-2xl mb-1">{post.icon}</span>
                <span className={`text-white ${simpleMode ? 'text-base' : 'text-sm'} font-medium`}>{post.name}</span>
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              if (portfolioUploadTag) {
                setPortfolioCritiqueStep(0);
                setPortfolioCritiqueAnswers({ describe: '', analyze: '', interpret: '', evaluate: '' });
                setPortfolioRubricRatings({ planning: 0, technique: 0, creativity: 0, cornerPost: 0, principle: 0, selfCritique: 0 });
                setCurrentScreen('portfolioCritique');
              }
            }}
            disabled={!portfolioUploadTag}
            className={`w-full py-4 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 ${
              portfolioUploadTag
                ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400 hover:shadow-2xl'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Assessment & Self-Critique (5-step: ratings + Feldman)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolioCritique') {
    const tagPost = cornerPosts.find(p => p.id === portfolioUploadTag);
    const isRatingsStep = portfolioCritiqueStep === 0;
    const critiqueIndex = portfolioCritiqueStep - 1; // 0-3 for Feldman steps
    const prompt = !isRatingsStep ? critiquePrompts[critiqueIndex] : null;
    const currentAnswer = prompt ? (portfolioCritiqueAnswers[prompt.key] || '') : '';
    const totalSteps = 5; // 1 ratings + 4 Feldman
    const isLastStep = portfolioCritiqueStep === totalSteps - 1;

    const handleCritiqueNext = async () => {
      if (!isLastStep) {
        setPortfolioCritiqueStep(s => s + 1);
      } else {
        const entry = {
          id: 'port_' + Date.now(),
          createdAt: Date.now(),
          cornerPostId: portfolioUploadTag,
          mediaType: portfolioUploadMediaType,
          imageThumb: portfolioUploadImage,
          critique: { ...portfolioCritiqueAnswers },
          ratings: { ...portfolioRubricRatings },
          sensesUsed: { ...sensesUsed },
          ...(workMode ? { workMode, ...(workModeBuddy ? { workModeBuddy } : {}) } : {}),
          ...(selectedLesson ? {
            lessonId: selectedLesson.id,
            lessonTitle: selectedLesson.title,
            blueprintText: blueprintText || null,
            blueprintSketch: blueprintSketch || null,
          } : {}),
        };
        // Store full media blob in IndexedDB
        if (portfolioUploadMediaBlob) {
          try { await mediaDB.put(entry.id, portfolioUploadMediaBlob); }
          catch (err) { console.warn('Could not save media to IndexedDB:', err); }
        }
        setPortfolioEntries(prev => [entry, ...prev]);
        setPortfolioUploadMediaBlob(null);
        setPortfolioUploadMediaType('image');
        setSelectedLesson(null);
        setBlueprintText('');
        setBlueprintSketch(null);
        setLessonStep(0);
        setLessonQuestionAnswers([]);
        setSensesUsed({ ...defaultSenses });
        setWorkMode(null);
        setWorkModeBuddy(null);
        // Clear persisted lesson state
        ['selectedLesson', 'lessonStep', 'lessonQuestionAnswers', 'blueprintText', 'blueprintSketch'].forEach(k => safeLocalStorage.setItem(k, ''));
        if (avatarCritiqueMode) {
          setAvatarCritiqueMode(false);
          setCurrentScreen('diveBuddies');
          if (textToSpeech) speak('Your avatar is saved. Find your Dive Buddies!');
        } else {
          setPortfolioSelectedEntry(entry);
          setCurrentScreen('portfolioDetail');
          if (textToSpeech) speak('Your work has been added to your collection.');
        }
      }
    };

    return (
      <div className="mesh-gradient flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="max-w-sm w-full">
          <button
            onClick={() => {
              if (portfolioCritiqueStep > 0) setPortfolioCritiqueStep(s => s - 1);
              else if (avatarCritiqueMode) {
                setAvatarCritiqueMode(false);
                setAvatar(null);
                setCurrentScreen('home');
              } else if (selectedLesson) {
                setCurrentScreen('lessonArtUpload');
              } else setCurrentScreen('portfolioTag');
            }}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-4 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          {selectedLesson && renderLessonProgress(4)}

          {/* Phase label + progress */}
          <div className="text-center mb-3 animate-fade-in">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-2">
              {isRatingsStep ? 'Rate Your Work' : 'Reflect on Your Art'}
            </p>
            <div className="flex justify-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === 0 ? 'w-6' : 'w-4'
                  } ${
                    i === portfolioCritiqueStep ? 'bg-purple-400 scale-110' :
                    i < portfolioCritiqueStep ? 'bg-purple-400/60' : 'bg-white/15'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Image preview + tag badge */}
          <div className="flex items-center justify-center gap-3 mb-4 animate-fade-in">
            <img src={portfolioUploadImage} alt="Work" className="w-12 h-12 rounded-xl object-cover shadow-lg" />
            {tagPost && (
              <span className="px-3 py-1 rounded-full glass text-white/80 text-xs font-medium flex items-center gap-1">
                {tagPost.icon} {tagPost.name}
              </span>
            )}
          </div>

          {/* Step 0: Rubric self-ratings */}
          {isRatingsStep && (
            <div className="animate-fade-in">
              <h2 className={`${simpleMode ? 'text-2xl' : 'text-xl'} font-bold text-white text-center mb-1`}>
                How did you do?
              </h2>
              <p className="text-white/50 text-sm text-center mb-5">Tap the stars for each one!</p>

              <div className="space-y-3 mb-5">
                {rubricCategories.map((cat) => {
                  const rating = portfolioRubricRatings[cat.key];
                  return (
                    <div key={cat.key} className="glass-surface rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{cat.emoji}</span>
                        <p className="text-gray-800 text-sm font-medium">{cat.question}</p>
                      </div>
                      <div className="flex items-center gap-1 justify-center">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            onClick={() => setPortfolioRubricRatings(prev => ({ ...prev, [cat.key]: star }))}
                            className={`w-11 h-11 text-2xl transition-all duration-200 rounded-full ${
                              star <= rating
                                ? 'text-yellow-400 scale-110'
                                : 'text-gray-400/80 bg-gray-100 hover:text-yellow-300'
                            }`}
                            aria-label={`Rate ${cat.label} ${star} out of 5`}
                          >
                            {star <= rating ? '\u{2B50}' : '\u{2606}'}
                          </button>
                        ))}
                      </div>
                      {rating > 0 && (
                        <p className="text-center text-xs text-gray-500 mt-1 animate-fade-in">
                          {rubricLevels[rating - 1]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Senses checklist */}
              <div className="glass-surface rounded-2xl p-4 mb-4">
                {renderSensesChecklist('light')}
              </div>

              {/* Work mode selector */}
              <div className="glass-surface rounded-2xl p-4 mb-4">
                {renderWorkModeSelector('light')}
              </div>

              <button
                onClick={handleCritiqueNext}
                className="w-full py-4 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400"
              >
                Next
              </button>

              <button
                onClick={handleCritiqueNext}
                className="mt-3 w-full text-center text-white/30 text-xs hover:text-white/50 transition-colors"
              >
                Skip ratings
              </button>
            </div>
          )}

          {/* Steps 1-4: Feldman critique prompts */}
          {!isRatingsStep && prompt && (
            <div key={portfolioCritiqueStep} className="animate-fade-in">
              <div className="text-5xl text-center mb-3">{prompt.emoji}</div>
              <h2 className={`${simpleMode ? 'text-2xl' : 'text-xl'} font-bold text-white text-center mb-1`}>
                {prompt.question}
              </h2>
              <p className="text-white/50 text-sm text-center mb-5">{prompt.hint}</p>

              <div className="glass-surface rounded-2xl p-4 mb-4">
                <textarea
                  value={currentAnswer}
                  onChange={(e) => {
                    const sanitized = e.target.value.slice(0, 300).replace(/<\/?[^>]+(>|$)/g, '');
                    setPortfolioCritiqueAnswers(prev => ({ ...prev, [prompt.key]: sanitized }));
                  }}
                  className="w-full bg-transparent text-gray-800 text-base resize-none focus:outline-none min-h-[100px] placeholder-gray-400"
                  placeholder={prompt.placeholder}
                  maxLength={300}
                  aria-label={prompt.question}
                />
                <div className="flex items-center justify-between mt-1">
                  {speechSupported ? (
                    <button
                      onClick={() => {
                        if (isListening) {
                          stopListening();
                        } else {
                          startListening((transcript) => {
                            setPortfolioCritiqueAnswers(prev => {
                              const current = prev[prompt.key] || '';
                              const joined = current ? (current + ' ' + transcript) : transcript;
                              return { ...prev, [prompt.key]: joined.slice(0, 300) };
                            });
                          });
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                        isListening
                          ? 'bg-red-100 text-red-600 animate-pulse'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      aria-label={isListening ? 'Stop recording' : 'Start voice input'}
                    >
                      {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                      {isListening ? 'Listening...' : 'Speak'}
                    </button>
                  ) : (
                    <span />
                  )}
                  <p className="text-gray-400 text-xs">{currentAnswer.length}/300</p>
                </div>
              </div>

              <button
                onClick={handleCritiqueNext}
                className={`w-full py-4 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl ${
                  isLastStep
                    ? 'bg-gradient-to-r from-green-500 to-teal-500'
                    : 'bg-gradient-to-r from-purple-600 via-blue-500 to-teal-400'
                }`}
              >
                {isLastStep ? 'Save to My Collection' : 'Next'}
              </button>

              <button
                onClick={handleCritiqueNext}
                className="mt-3 w-full text-center text-white/30 text-xs hover:text-white/50 transition-colors"
              >
                Skip this one
              </button>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Entry Detail View
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolioDetail' && portfolioSelectedEntry) {
    const entry = portfolioSelectedEntry;
    const post = cornerPosts.find(p => p.id === entry.cornerPostId);
    const summary = getCritiqueSummary(entry);
    const staggerClasses = ['animate-stagger-1', 'animate-stagger-2', 'animate-stagger-3', 'animate-stagger-4'];

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => setCurrentScreen('portfolio')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Collection"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          {/* Large media display */}
          <div className="glass rounded-3xl p-3 mb-4 animate-fade-in">
            {entry.mediaType === 'video' ? (
              <video
                src={mediaUrls[entry.id]}
                poster={entry.imageThumb}
                controls
                playsInline
                className="w-full rounded-2xl max-h-[50vh]"
              >
                Your browser does not support video playback.
              </video>
            ) : (
              <img src={mediaUrls[entry.id] || entry.imageThumb} alt="Work" className="w-full rounded-2xl object-contain max-h-[50vh]" />
            )}
          </div>

          {/* Badge + date */}
          <div className="flex items-center justify-between mb-2 animate-fade-in">
            {post && (
              <span className="px-3 py-1.5 rounded-full glass text-white/80 text-sm font-medium flex items-center gap-1.5">
                {post.icon} {post.name}
              </span>
            )}
            <span className="text-white/40 text-sm">{formatDateLong(entry.createdAt)}</span>
          </div>
          {entry.lessonId && (
            <div className="mb-6 animate-fade-in">
              <span className="px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-200 text-xs font-medium inline-flex items-center gap-1.5">
                {'\u{1F4D6}'} {entry.lessonTitle}
              </span>
            </div>
          )}
          {!entry.lessonId && <div className="mb-4" />}

          {/* Blueprint card */}
          {(entry.blueprintText || entry.blueprintSketch) && (
            <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
              <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">{'\u{270F}\u{FE0F}'} My Blueprint</h3>
              {entry.blueprintText && (
                <p className="text-white/80 text-sm mb-3 whitespace-pre-wrap">{entry.blueprintText}</p>
              )}
              {entry.blueprintSketch && (
                <img
                  src={entry.blueprintSketch}
                  alt="Blueprint sketch"
                  className="w-full rounded-xl object-contain max-h-48 bg-white/10 p-2"
                />
              )}
            </div>
          )}

          {/* Rubric ratings (new entries only) */}
          {entry.ratings && (
            <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
              <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">My Self-Rating</h3>
              <div className="space-y-2.5">
                {rubricCategories.map(cat => {
                  const rating = entry.ratings[cat.key] || 0;
                  return (
                    <div key={cat.key} className="flex items-center gap-2">
                      <span className="text-sm">{cat.emoji}</span>
                      <span className="text-white/70 text-xs flex-1 min-w-0 truncate">{cat.label}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <span key={s} className={`text-sm ${s <= rating ? 'text-yellow-400' : 'text-white/15'}`}>
                            {s <= rating ? '\u{2B50}' : '\u{2606}'}
                          </span>
                        ))}
                      </div>
                      {rating > 0 && (
                        <span className="text-white/40 text-[10px] w-20 text-right">{rubricLevels[rating - 1]}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Senses engaged */}
          {entry.sensesUsed && Object.values(entry.sensesUsed).some(v => v) && (
            <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
              <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Senses Engaged</h3>
              <div className="flex flex-wrap gap-2">
                {sensesConfig.filter(s => entry.sensesUsed[s.key]).map(s => (
                  <span key={s.key} className="flex items-center gap-1 px-2.5 py-1 rounded-full glass text-white/80 text-xs">
                    {s.emoji} {s.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Work mode badge */}
          {entry.workMode && (() => {
            const wm = workModeConfig.find(w => w.key === entry.workMode);
            return wm ? (
              <div className="glass rounded-2xl p-4 mb-4 animate-fade-in">
                <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">How I Worked</h3>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-white/80 text-sm">
                    {wm.emoji} {wm.label}
                  </span>
                  {entry.workModeBuddy && (
                    <span className="text-white/50 text-xs">with {entry.workModeBuddy}</span>
                  )}
                </div>
              </div>
            ) : null;
          })()}

          {/* Critique cards (works for old and new entries) */}
          <div className="space-y-3 mb-6">
            {summary.cards.map((card, idx) => (
              <div
                key={card.key}
                className={`glass-surface rounded-2xl p-5 opacity-start ${staggerClasses[idx % 4]}`}
              >
                <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <span>{card.emoji}</span> {card.label}
                </h3>
                <p className={`text-base ${card.text ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                  {card.text || 'No answer yet'}
                </p>
              </div>
            ))}
          </div>

          {/* Delete */}
          <button
            onClick={async () => {
              if (window.confirm('Remove this piece from your collection?')) {
                try { await mediaDB.delete(entry.id); } catch {}
                setPortfolioEntries(prev => prev.filter(e => e.id !== entry.id));
                setCurrentScreen('portfolio');
              }
            }}
            className="flex items-center justify-center gap-2 mx-auto text-red-300/60 text-xs hover:text-red-300 transition-colors"
          >
            <Trash2 size={12} /> Remove from collection
          </button>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PORTFOLIO — Growth Timeline
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'portfolioGrowth') {
    // Group entries by month (newest first)
    const monthGroups = {};
    const sortedEntries = [...portfolioEntries].sort((a, b) => b.createdAt - a.createdAt);
    sortedEntries.forEach(entry => {
      const d = new Date(entry.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!monthGroups[key]) monthGroups[key] = { label, entries: [] };
      monthGroups[key].entries.push(entry);
    });
    const months = Object.keys(monthGroups).sort().reverse();

    // Collect all critique quotes for the summary
    const allQuotes = sortedEntries
      .filter(e => getCritiqueSummary(e).hasText)
      .slice(0, 3);

    // Count which corner posts appear
    const postCounts = {};
    portfolioEntries.forEach(e => { postCounts[e.cornerPostId] = (postCounts[e.cornerPostId] || 0) + 1; });

    return (
      <div className="mesh-gradient p-6 relative overflow-hidden">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => setCurrentScreen('portfolio')}
            className="w-10 h-10 rounded-full glass flex items-center justify-center mb-6 transition-all duration-300 active:scale-95 hover:bg-white/20"
            aria-label="Back to Collection"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>

          {/* Header */}
          <div className="mb-6 animate-fade-in">
            <h1 className={`${simpleMode ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-1`}>{'\u{1F331}'} My Growth Story</h1>
            <p className="text-white/60 text-sm">Look how far you've come!</p>
          </div>

          {/* Corner post dots — which areas they've explored */}
          <div className="glass rounded-2xl p-4 mb-6 animate-fade-in">
            <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Areas You've Explored</p>
            <div className="flex flex-wrap gap-2">
              {cornerPosts.map(post => {
                const count = postCounts[post.id] || 0;
                return (
                  <span
                    key={post.id}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      count > 0
                        ? 'glass text-white'
                        : 'bg-white/5 text-white/20'
                    }`}
                  >
                    <span>{post.icon}</span>
                    {post.name}
                    {count > 0 && <span className="text-white/40">({count})</span>}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Growth chart */}
          {(() => {
            const chronoEntries = [...portfolioEntries]
              .filter(e => e.ratings && Object.keys(e.ratings).length > 0)
              .sort((a, b) => a.createdAt - b.createdAt);
            const chartData = chronoEntries.map(e => {
              const vals = Object.values(e.ratings).filter(v => v > 0);
              return { value: vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0, ts: e.createdAt };
            }).filter(d => d.value > 0);

            if (chartData.length < 2) {
              return chartData.length === 1 ? (
                <div className="glass rounded-2xl p-5 mb-6 animate-fade-in text-center">
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Your Growth</p>
                  <p className="text-white/40 text-sm">Add more work to see your growth!</p>
                </div>
              ) : null;
            }

            let prevMonth = '';
            const xLabels = chartData.map((d, i) => {
              const mo = new Date(d.ts).toLocaleDateString('en-US', { month: 'short' });
              if (i === 0 || i === chartData.length - 1 || mo !== prevMonth) { prevMonth = mo; return mo; }
              prevMonth = mo;
              return '';
            });

            return (
              <div className="glass rounded-2xl p-5 mb-6 animate-fade-in">
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Your Growth</p>
                {renderGrowthChart(chartData, {
                  gradientId: 'studentGrowth',
                  colorFrom: '#c084fc',
                  colorTo: '#60a5fa',
                  xLabels,
                  showAvgLine: false
                })}
              </div>
            );
          })()}

          {/* Recent highlights — student's own words */}
          {allQuotes.length > 0 && (
            <div className="glass rounded-2xl p-5 mb-6 animate-fade-in-delay">
              <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">In Your Own Words</p>
              <div className="space-y-3">
                {allQuotes.map((entry, idx) => {
                  const qs = getCritiqueSummary(entry);
                  const bestQuote = qs.bestQuote;
                  const quoteLabel = qs.quoteEmoji;
                  return (
                    <div key={entry.id} className={`opacity-start ${idx === 0 ? 'animate-stagger-1' : idx === 1 ? 'animate-stagger-2' : 'animate-stagger-3'}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-lg flex-shrink-0 mt-0.5">{quoteLabel}</span>
                        <div className="min-w-0">
                          <p className="text-white text-sm italic leading-relaxed">"{bestQuote}"</p>
                          <p className="text-white/30 text-[10px] mt-1">{formatDate(entry.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timeline by month */}
          <div className="relative animate-fade-in-delay-2">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-white/10" />

            {months.map((monthKey) => {
              const group = monthGroups[monthKey];
              return (
                <div key={monthKey} className="mb-8 last:mb-0">
                  {/* Month label */}
                  <div className="flex items-center gap-3 mb-4 relative">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 z-10 shadow-lg">
                      <span className="text-white text-xs font-bold">{group.entries.length}</span>
                    </div>
                    <h2 className="text-white font-bold text-lg">{group.label}</h2>
                  </div>

                  {/* Entries in this month */}
                  <div className="ml-10 space-y-3">
                    {group.entries.map(entry => {
                      const post = cornerPosts.find(p => p.id === entry.cornerPostId);
                      const es = getCritiqueSummary(entry);
                      return (
                        <button
                          key={entry.id}
                          onClick={() => {
                            setPortfolioSelectedEntry(entry);
                            setCurrentScreen('portfolioDetail');
                          }}
                          className="w-full glass rounded-2xl p-3 text-left transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/15 active:scale-[0.98]"
                        >
                          <div className="flex gap-3">
                            {/* Thumbnail */}
                            <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 relative">
                              <img src={entry.imageThumb} alt="Work" className="w-full h-full object-cover" />
                              {entry.mediaType === 'video' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-6 h-6 rounded-full bg-black/50 flex items-center justify-center">
                                    <Play size={10} className="text-white ml-0.5" />
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 mb-1">
                                {post && <span className="text-sm">{post.icon}</span>}
                                <span className="text-white/40 text-[10px]">{formatDate(entry.createdAt)}</span>
                              </div>
                              {es.hasText ? (
                                <p className="text-white/80 text-xs leading-relaxed line-clamp-2">
                                  {es.bestQuote}
                                </p>
                              ) : (
                                <p className="text-white/30 text-xs italic">No reflection yet</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Encouragement at the bottom */}
          {portfolioEntries.length >= 5 && (
            <div className="glass rounded-2xl p-5 mt-8 text-center animate-fade-in-delay-2">
              <p className="text-2xl mb-2">{portfolioEntries.length >= 10 ? '\u{1F31F}' : '\u{1F331}'}</p>
              <p className="text-white/80 text-sm font-medium">
                {portfolioEntries.length >= 10
                  ? "Wow — look at everything you've done! You should be really proud."
                  : "Your collection is growing! Keep adding to your story."}
              </p>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LESSON LIBRARY (Browse & import shared lessons)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'lessonLibrary') {
    const allLibLessons = [...lessonLibrary, ...customLessons.filter(l => l._published)];
    const query = librarySearch.toLowerCase().trim();
    const filtered = allLibLessons.filter(l => {
      if (libraryFilter !== 'all' && l.suggestedCornerPost !== libraryFilter) return false;
      if (libraryGradeFilter !== 'all' && l.gradeLevel !== libraryGradeFilter) return false;
      if (libraryKernFilter !== 'all' && l.kernStage !== libraryKernFilter) return false;
      if (libraryStrategyFilter !== 'all' && l.artStrategy !== libraryStrategyFilter) return false;
      if (query && !l.title.toLowerCase().includes(query) && !(l.author || '').toLowerCase().includes(query)) return false;
      return true;
    });

    return (
      <div className="mesh-gradient flex flex-col items-center justify-start p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          <button
            onClick={() => { setLibrarySearch(''); setLibraryFilter('all'); setLibraryGradeFilter('all'); setLibraryKernFilter('all'); setLibraryStrategyFilter('all'); setCurrentScreen('teacherDashboard'); }}
            className="flex items-center gap-2 text-white/80 mb-6 transition-all duration-300 hover:text-white active:scale-95"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <h1 className="text-3xl font-black text-white mb-1 animate-fade-in">📚 Lesson Library</h1>
          <p className="text-white/60 text-sm mb-4 animate-fade-in-delay">
            {allLibLessons.length} lesson{allLibLessons.length !== 1 ? 's' : ''} shared by teachers
          </p>

          {/* Search */}
          <div className="relative mb-4 animate-fade-in-delay">
            <input
              type="text"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="Search lessons..."
              className="w-full px-4 py-3 pl-10 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400/50 transition-all"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">🔍</span>
          </div>

          {/* Filters */}
          <div className="mb-4 space-y-2 animate-fade-in-delay">
            {/* Corner Post filter */}
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              <button
                onClick={() => setLibraryFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  libraryFilter === 'all' ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >All Posts</button>
              {cornerPosts.map(p => (
                <button
                  key={p.id}
                  onClick={() => setLibraryFilter(libraryFilter === p.id ? 'all' : p.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                    libraryFilter === p.id ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                  }`}
                >{p.icon} {p.name}</button>
              ))}
            </div>
            {/* Grade filter */}
            <div className="flex gap-1.5">
              <button
                onClick={() => setLibraryGradeFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  libraryGradeFilter === 'all' ? 'bg-cyan-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >All Grades</button>
              {['K-2', '3-5', '6-8', '9-12'].map(g => (
                <button
                  key={g}
                  onClick={() => setLibraryGradeFilter(libraryGradeFilter === g ? 'all' : g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    libraryGradeFilter === g ? 'bg-cyan-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                  }`}
                >{g}</button>
              ))}
            </div>
            {/* Kern Stage filter */}
            <div className="flex gap-1.5">
              <button
                onClick={() => setLibraryKernFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  libraryKernFilter === 'all' ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >All Stages</button>
              {[
                { value: 'dependent', label: 'Dependent' },
                { value: 'independent', label: 'Independent' },
                { value: 'interdependent', label: 'Interdependent' },
              ].map(k => (
                <button
                  key={k.value}
                  onClick={() => setLibraryKernFilter(libraryKernFilter === k.value ? 'all' : k.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    libraryKernFilter === k.value ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                  }`}
                >{k.label}</button>
              ))}
            </div>
            {/* Art Strategy filter */}
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              <button
                onClick={() => setLibraryStrategyFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  libraryStrategyFilter === 'all' ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >All Strategies</button>
              {['Observation', 'Experimentation', 'Expression', 'Storytelling', 'Critique', 'Construction'].map(s => (
                <button
                  key={s}
                  onClick={() => setLibraryStrategyFilter(libraryStrategyFilter === s ? 'all' : s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                    libraryStrategyFilter === s ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/50 hover:bg-white/20'
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Import by code */}
          <div className="mb-4">
            <button
              onClick={() => setShowImportInput(!showImportInput)}
              className="w-full text-center text-white/40 text-xs hover:text-white/60 transition-all"
            >
              {showImportInput ? 'Cancel' : 'Have a lesson code? Tap to import'}
            </button>
            {showImportInput && (
              <div className="glass rounded-2xl p-4 mt-2 animate-scale-in">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={importCode}
                    onChange={(e) => setImportCode(e.target.value)}
                    placeholder="4TZ-L:..."
                    className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-400/50"
                  />
                  <button
                    onClick={() => {
                      const lesson = importLessonCode(importCode.trim());
                      if (lesson) {
                        setCustomLessons(prev => [...prev, lesson]);
                        setImportCode('');
                        setShowImportInput(false);
                        alert(`"${lesson.title}" imported to your lessons!`);
                      } else {
                        alert('Invalid lesson code. Ask the teacher to share their code again.');
                      }
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold text-sm rounded-xl transition-all active:scale-95"
                  >
                    Import
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Lesson cards */}
          {filtered.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center animate-fade-in">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-white/70 font-medium">No lessons found</p>
              <p className="text-white/40 text-sm mt-1">
                {allLibLessons.length === 0
                  ? 'Publish your first lesson from the Lesson Builder to start the library!'
                  : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 animate-fade-in">
              {filtered.map(lesson => {
                const post = cornerPosts.find(p => p.id === lesson.suggestedCornerPost);
                const alreadyHave = customLessons.some(l => l.title === lesson.title && l.author === lesson.author);
                return (
                  <div key={lesson.id} className={`rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br ${lesson.coverColor || 'from-blue-400 to-indigo-500'}`}>
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-3xl">{lesson.coverEmoji || '📖'}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-bold text-sm truncate">{lesson.title}</h3>
                          {lesson.author && <p className="text-white/70 text-xs">by {lesson.author}</p>}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {post && <span className="bg-white/20 px-2 py-0.5 rounded-full text-white text-[10px] font-bold">{post.icon} {post.name}</span>}
                            {lesson.gradeLevel && <span className="bg-white/20 px-2 py-0.5 rounded-full text-white text-[10px] font-bold">{lesson.gradeLevel}</span>}
                            {lesson.artMedium && <span className="bg-white/20 px-2 py-0.5 rounded-full text-white text-[10px] font-bold">{lesson.artMedium}</span>}
                            {lesson.kernStage && <span className="bg-amber-300/20 px-2 py-0.5 rounded-full text-white text-[10px] font-bold">{lesson.kernStage}</span>}
                            {lesson.artStrategy && <span className="bg-emerald-300/20 px-2 py-0.5 rounded-full text-white text-[10px] font-bold">{lesson.artStrategy}</span>}
                          </div>
                        </div>
                      </div>
                      {lesson.hook && <p className="text-white/80 text-xs mt-2 italic">"{lesson.hook}"</p>}
                      <div className="flex gap-2 mt-3">
                        {alreadyHave ? (
                          <span className="flex-1 py-2 bg-white/10 text-white/50 text-xs font-bold rounded-xl text-center">Already in your lessons</span>
                        ) : (
                          <button
                            onClick={() => {
                              if (importFromLibrary(lesson)) {
                                alert(`"${lesson.title}" added to your lessons!`);
                              }
                            }}
                            className="flex-1 py-2 bg-white/30 text-white text-xs font-bold rounded-xl transition-all active:scale-95 hover:bg-white/40"
                          >
                            + Add to My Lessons
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            const code = exportLessonCode(lesson);
                            try {
                              await navigator.clipboard.writeText(code);
                              alert('Lesson code copied!');
                            } catch {
                              prompt('Copy this code:', code);
                            }
                          }}
                          className="py-2 px-3 bg-white/20 text-white rounded-xl transition-all active:scale-95 hover:bg-white/30"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // AQUARIUM (Student's underwater world + sandcastle progress)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'aquarium') {
    const unplacedItems = aquariumItems.filter(i => !i.placed);
    const placedItems = aquariumItems.filter(i => i.placed);
    const completedPosts = cornerPosts.filter(post =>
      post.missions.some(m => completedMissions.includes(m.id))
    ).length;
    const sandcastleLevel = completedPosts; // 0-6

    return (
      <div className="relative overflow-hidden" style={{
        background: 'linear-gradient(180deg, #0c4a6e 0%, #0e7490 30%, #0891b2 60%, #164e63 85%, #1e3a5f 100%)',
        minHeight: '100vh',
      }}>
        {/* Back button */}
        <button
          onClick={() => setCurrentScreen('home')}
          className="fixed top-6 left-6 z-30 w-10 h-10 rounded-full glass flex items-center justify-center transition-all duration-300 active:scale-95 hover:bg-white/20"
          aria-label="Back"
        >
          <ArrowLeft size={18} className="text-white" />
        </button>

        {/* Title */}
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-30 text-center">
          <h1 className="text-xl font-black text-white drop-shadow-lg animate-fade-in">
            {avatarName ? `${avatarName}'s Aquarium` : 'My Aquarium'}
          </h1>
          <p className="text-cyan-300/60 text-xs mt-0.5">{aquariumItems.length} creature{aquariumItems.length !== 1 ? 's' : ''} discovered</p>
        </div>

        {/* Light rays */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="light-ray" style={{ top: '-10%', left: '15%', width: '100px', height: '70vh' }} />
          <div className="light-ray" style={{ top: '-10%', right: '20%', width: '60px', height: '50vh' }} />
        </div>

        {/* Ambient bubbles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[8, 25, 45, 65, 80].map((left, i) => (
            <div key={i} className={`bubble w-2 h-2 animate-bubble-${(i % 4) + 1} opacity-10`} style={{ left: `${left}%`, bottom: '-5%' }} />
          ))}
        </div>

        {/* Sandy bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(194,178,128,0.3) 40%, rgba(194,178,128,0.5) 100%)',
          borderTop: '2px solid rgba(194,178,128,0.2)',
        }} />

        {/* Sandcastle progress — grows with corner posts */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center z-10">
          <div className="relative" style={{ fontSize: sandcastleLevel === 0 ? 40 : 30 + sandcastleLevel * 12 }}>
            {sandcastleLevel === 0 && '🏖️'}
            {sandcastleLevel >= 1 && sandcastleLevel <= 2 && '🏗️'}
            {sandcastleLevel >= 3 && sandcastleLevel <= 4 && '🏰'}
            {sandcastleLevel >= 5 && sandcastleLevel < 6 && '🏰'}
            {sandcastleLevel === 6 && (
              <span className="animate-pulse-soft">👑🏰👑</span>
            )}
          </div>
          <div className="flex gap-1 justify-center mt-1">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className={`w-3 h-3 rounded-sm transition-all duration-500 ${
                  i < sandcastleLevel
                    ? 'bg-amber-400 shadow-sm shadow-amber-400/50'
                    : 'bg-white/10'
                }`}
              />
            ))}
          </div>
          <p className="text-white/40 text-[10px] mt-1 font-medium">
            {sandcastleLevel === 0 ? 'Start exploring to build your fort!' :
             sandcastleLevel < 6 ? `Fort: ${sandcastleLevel}/6 corner posts` :
             'Fort complete! You did it!'}
          </p>
        </div>

        {/* Placed items in the aquarium */}
        {placedItems.map(item => (
          <div
            key={item.id}
            className="absolute transition-all duration-500"
            style={{
              left: `${item.x}%`,
              top: `${item.y}%`,
              fontSize: item.size === 'lg' ? 48 : item.size === 'md' ? 36 : 28,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
              animation: `float ${3 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          >
            {item.emoji}
          </div>
        ))}

        {/* Unplaced items tray */}
        {unplacedItems.length > 0 && (
          <div className="fixed bottom-28 left-4 right-4 z-20 animate-slide-up">
            <div className="glass rounded-2xl p-3">
              <p className="text-cyan-300 text-xs font-bold uppercase tracking-widest mb-2 text-center">
                New Creatures! Tap to place ({unplacedItems.length})
              </p>
              <div className="flex gap-2 justify-center flex-wrap">
                {unplacedItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setAquariumItems(prev => prev.map(i =>
                        i.id === item.id
                          ? { ...i, placed: true, x: Math.random() * 60 + 20, y: Math.random() * 35 + 30 }
                          : i
                      ));
                    }}
                    className="flex flex-col items-center gap-1 p-2 bg-white/10 rounded-xl transition-all duration-300 active:scale-90 hover:bg-white/20 hover:-translate-y-1 animate-pulse-soft"
                  >
                    <span className="text-2xl">{item.emoji}</span>
                    <span className="text-white/60 text-[9px] font-medium">{item.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {aquariumItems.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="glass rounded-3xl p-8 text-center max-w-xs animate-fade-in">
              <div className="text-5xl mb-3">🐚</div>
              <h2 className="text-white font-bold text-lg mb-2">Your Aquarium Awaits!</h2>
              <p className="text-white/60 text-sm">Complete lessons and missions to discover sea creatures for your aquarium. Each adventure earns you a new friend!</p>
              <button
                onClick={() => setCurrentScreen('lessonPicker')}
                className="mt-4 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-2xl transition-all active:scale-95"
              >
                Start an Adventure!
              </button>
            </div>
          </div>
        )}

        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // VOCABULARY LIST (Diver's Dictionary / Master Vocabulary)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'vocabularyList') {
    // Aggregate all vocabulary from built-in + custom lessons
    const allVocab = [];
    const seen = new Set();
    [...lessonPlans, ...customLessons].forEach(lesson => {
      if (lesson.vocabulary) {
        lesson.vocabulary.forEach(v => {
          const key = v.term.toLowerCase().trim();
          if (!seen.has(key)) {
            seen.add(key);
            allVocab.push({ ...v, lessonTitle: lesson.title, lessonId: lesson.id });
          }
        });
      }
    });
    // Sort alphabetically
    allVocab.sort((a, b) => a.term.toLowerCase().localeCompare(b.term.toLowerCase()));

    const query = vocabSearch.toLowerCase().trim();
    const filtered = query
      ? allVocab.filter(v => v.term.toLowerCase().includes(query) || v.def.toLowerCase().includes(query))
      : allVocab;

    // Group by first letter
    const groups = {};
    filtered.forEach(v => {
      const letter = v.term[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(v);
    });
    const letters = Object.keys(groups).sort();

    return (
      <div className="mesh-gradient flex flex-col items-center justify-start p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => { setVocabSearch(''); setCurrentScreen('home'); }}
            className="flex items-center gap-2 text-white/80 mb-6 transition-all duration-300 hover:text-white active:scale-95"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <h1 className="text-3xl font-black text-white mb-1 animate-fade-in">
            {teacherMode ? '\u{1F4DA} Master Vocabulary' : '\u{1F4D6} Diver\'s Dictionary'}
          </h1>
          <p className="text-white/60 text-sm mb-4 animate-fade-in-delay">
            {allVocab.length} terms from {[...lessonPlans, ...customLessons].filter(l => l.vocabulary && l.vocabulary.length > 0).length} lessons
          </p>

          {/* Search bar */}
          <div className="relative mb-6 animate-fade-in-delay">
            <input
              type="text"
              value={vocabSearch}
              onChange={(e) => setVocabSearch(e.target.value)}
              placeholder="Search terms..."
              className="w-full px-4 py-3 pl-10 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 transition-all"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
              {'\u{1F50D}'}
            </span>
            {vocabSearch && (
              <button
                onClick={() => setVocabSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Results */}
          {filtered.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center animate-fade-in">
              <div className="text-4xl mb-3">{'\u{1F9D0}'}</div>
              <p className="text-white/70">No terms match "{vocabSearch}"</p>
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in">
              {letters.map(letter => (
                <div key={letter}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-black text-sm">
                      {letter}
                    </span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <div className="space-y-2">
                    {groups[letter].map((v, i) => (
                      <div key={`${v.term}-${i}`} className="glass rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-white font-bold text-sm">{v.term}</p>
                            <p className="text-white/70 text-sm mt-1">{v.def}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const lesson = [...lessonPlans, ...customLessons].find(l => l.id === v.lessonId);
                            if (lesson) {
                              setSelectedLesson(lesson);
                              setCurrentScreen('lessonRead');
                            }
                          }}
                          className="mt-2 text-cyan-300/60 text-xs hover:text-cyan-300 transition-colors"
                        >
                          from: {v.lessonTitle}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // SHARE MY PROGRESS (Student QR export)
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'shareProgress') {
    const qrData = encodeProgressQR();
    const missionCount = (completedMissions || []).length;
    const entryCount = portfolioEntries.length;
    const ratedCount = portfolioEntries.filter(e => e.rubricRatings && Object.values(e.rubricRatings).some(v => v > 0)).length;
    const canShare = typeof navigator.share === 'function';

    return (
      <div className="ocean-gradient flex flex-col items-center justify-start p-6 overflow-y-auto relative">
        {/* Ambient bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[12, 35, 58, 78, 90].map((left, i) => (
            <div key={i} className={`bubble w-2 h-2 animate-bubble-${(i % 4) + 1} opacity-15`} style={{ left: `${left}%`, bottom: '-5%' }} />
          ))}
        </div>

        <div className="w-full max-w-sm relative z-10">
          {/* Back button */}
          <button
            onClick={() => setCurrentScreen('home')}
            className="flex items-center gap-2 text-white/80 mb-6 transition-all duration-300 hover:text-white active:scale-95"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <div className="text-center mb-2 animate-fade-in">
            <span className="text-4xl">📡</span>
            <h1 className="text-3xl font-black text-white mt-2">Transmit Expedition Log</h1>
            <p className="text-cyan-300/70 text-sm mt-1 animate-fade-in-delay">Show this to your captain!</p>
          </div>

          {/* QR Code with sonar pulse */}
          <div className="relative flex justify-center my-8 animate-scale-in">
            {/* Sonar rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="sonar-ring w-64 h-64 animate-sonar-1" />
              <div className="sonar-ring absolute w-64 h-64 animate-sonar-2" />
              <div className="sonar-ring absolute w-64 h-64 animate-sonar-3" />
            </div>

            {/* QR in diving helmet frame */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-4 shadow-2xl shadow-cyan-500/20 animate-glow-pulse" style={{ boxShadow: '0 0 30px rgba(34, 211, 238, 0.3)' }}>
                <QRCodeSVG value={qrData} size={180} level="L" fgColor="#0c4a6e" />
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full shadow-lg">
                <p className="text-white font-bold text-xs whitespace-nowrap">{avatarName || 'Explorer'}</p>
              </div>
            </div>
          </div>

          {/* Expedition log summary */}
          <div className="glass rounded-2xl p-5 mb-4 animate-fade-in-delay">
            <p className="text-cyan-300/60 text-[10px] font-bold uppercase tracking-widest mb-3">Expedition Log Contents</p>
            <div className="space-y-2.5 font-mono text-sm">
              <div className="flex justify-between items-center opacity-start animate-stagger-1">
                <span className="text-white/60">Explorer</span>
                <span className="text-white font-medium">{avatarName || 'Anonymous'}</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between items-center opacity-start animate-stagger-2">
                <span className="text-white/60">Missions</span>
                <span className="text-cyan-300 font-medium">{missionCount}</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between items-center opacity-start animate-stagger-3">
                <span className="text-white/60">Portfolio</span>
                <span className="text-cyan-300 font-medium">{entryCount} pieces ({ratedCount} rated)</span>
              </div>
              {sensoryProfile && (
                <>
                  <div className="h-px bg-white/5" />
                  <div className="flex justify-between items-center opacity-start animate-stagger-4">
                    <span className="text-white/60">Sensory Profile</span>
                    <span className="text-emerald-300 font-medium">Included</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Share actions */}
          <div className="flex gap-2 mb-4 animate-fade-in-delay-2">
            <button
              onClick={copyProgressCode}
              className={`flex-1 py-3 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm transition-all duration-300 active:scale-95 ${
                codeCopied
                  ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-300'
                  : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
              }`}
            >
              {codeCopied ? <Check size={16} /> : <Copy size={16} />}
              {codeCopied ? 'Copied!' : 'Copy Code'}
            </button>
            {canShare && (
              <button
                onClick={shareProgressViaAPI}
                className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white flex items-center justify-center gap-2 font-bold text-sm transition-all duration-300 active:scale-95 hover:shadow-lg"
              >
                <Share2 size={16} /> Share
              </button>
            )}
          </div>

          <div className="glass rounded-2xl p-3 animate-fade-in-delay-2">
            <p className="text-white/40 text-[11px] text-center">
              🔒 Photos, videos, and writing stay private — only ratings and progress are shared.
            </p>
          </div>
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
        {renderScanFeedbackToast()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // TEACHER DASHBOARD
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'teacherDashboard') {
    return (
      <div className="mesh-gradient flex flex-col items-center justify-start p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => { setTeacherMode(false); setCurrentScreen('home'); }}
            className="flex items-center gap-2 text-white/80 mb-6 transition-all duration-300 hover:text-white active:scale-95"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <h1 className="text-3xl font-black text-white mb-2 animate-fade-in">{'\u{1F393}'} Teacher Dashboard</h1>
          <p className="text-white/70 text-sm mb-6 animate-fade-in-delay">Scan student QR codes to view class progress.</p>

          {/* Scan & Import */}
          <div className="flex gap-2 mb-4 animate-scale-in">
            <button
              onClick={() => navigator.mediaDevices ? startLiveScan('teacher') : teacherScanRef.current?.click()}
              className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-bold rounded-2xl shadow-lg transition-all duration-300 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5"
            >
              📡 Scan QR
            </button>
            <button
              onClick={() => { setShowPasteInput(!showPasteInput); setPasteCodeInput(''); }}
              className="py-4 px-5 bg-white/10 border border-white/20 text-white font-bold rounded-2xl transition-all duration-300 active:scale-95 hover:bg-white/20"
              title="Paste student code"
            >
              📋
            </button>
          </div>
          {showPasteInput && (
            <div className="glass rounded-2xl p-4 mb-4 animate-scale-in">
              <p className="text-white/60 text-xs mb-2">Paste a student's explorer code:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pasteCodeInput}
                  onChange={(e) => setPasteCodeInput(e.target.value)}
                  placeholder="4TZ:..."
                  className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400/50"
                />
                <button
                  onClick={() => {
                    if (handlePasteCode(pasteCodeInput.trim())) {
                      setPasteCodeInput('');
                      setShowPasteInput(false);
                    } else {
                      showScanToast('error', 'Invalid code. Ask the student to copy their code again.');
                    }
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-sm rounded-xl transition-all active:scale-95"
                >
                  Import
                </button>
              </div>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={teacherScanRef}
            className="hidden"
            onChange={handleTeacherScan}
          />

          {/* Shopping List for upcoming lessons */}
          {(() => {
            const dotL = lessonPlans.find(l => l.id === 'the-dot');
            const otherB = lessonPlans.filter(l => l.id !== 'the-dot');
            const ordered = [dotL, ...otherB, ...customLessons].filter(Boolean);
            const upcoming = ordered
              .filter(l => !portfolioEntries.some(e => e.lessonId === l.id))
              .slice(0, 3);
            const materialsMap = {};
            upcoming.forEach(l => {
              (l.materials || []).forEach(m => {
                const key = m.toLowerCase().trim();
                if (!materialsMap[key]) materialsMap[key] = { label: m, lessons: [] };
                materialsMap[key].lessons.push(l.coverEmoji);
              });
            });
            const items = Object.values(materialsMap);
            if (items.length === 0) return null;
            return (
              <div className="glass rounded-2xl p-5 mb-6 animate-fade-in">
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">
                  {'\u{1F6D2}'} Shopping List — Next {upcoming.length} Lesson{upcoming.length !== 1 ? 's' : ''}
                </p>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="text-white/30 text-sm">{'\u{25CB}'}</span>
                      <span className="text-white text-sm flex-1">{item.label}</span>
                      <span className="text-xs text-white/40">{item.lessons.join(' ')}</span>
                    </div>
                  ))}
                </div>
                <p className="text-white/30 text-[10px] mt-2">Gather these materials before class!</p>
              </div>
            );
          })()}

          {/* Lesson Plans */}
          <div className="glass rounded-2xl p-5 mb-6 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-bold text-sm">{'\u{1F4DA}'} Lesson Plans</p>
              <span className="text-white/40 text-xs">{lessonPlans.length + customLessons.length} total</span>
            </div>
            <div className="space-y-2 mb-3">
              {customLessons.length > 0 ? (
                customLessons.slice(0, 3).map(lesson => (
                  <div key={lesson.id} className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
                    <span className="text-lg">{lesson.coverEmoji}</span>
                    <span className="text-white/80 text-sm flex-1 truncate">{lesson.title}</span>
                    <button
                      onClick={() => {
                        setEditingLessonId(lesson.id);
                        setLessonBuilderData({ ...lesson });
                        setLessonBuilderStep(0);
                        setCurrentScreen('lessonBuilder');
                      }}
                      className="text-white/40 text-xs hover:text-white/70 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-sm">No custom lessons yet.</p>
              )}
              {customLessons.length > 3 && (
                <p className="text-white/30 text-xs text-center">+{customLessons.length - 3} more</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditingLessonId(null);
                  setLessonBuilderData({
                    title: '', author: '', coverEmoji: '\u{1F4D6}', coverColor: 'from-blue-400 to-indigo-500',
                    hook: '', audioUrl: '', artActivity: '',
                    essentialQuestions: ['', '', ''],
                    vocabulary: [{ term: '', def: '' }],
                    techniqueVideos: [],
                    materials: [],
                    procedures: '',
                    suggestedCornerPost: 'environmental',
                    gradeLevel: '',
                    artMedium: '',
                    kernStage: '',
                    artStrategy: '',
                  });
                  setLessonBuilderStep(0);
                  setCurrentScreen('lessonBuilder');
                }}
                className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-blue-500 text-white text-sm font-bold rounded-xl transition-all duration-300 active:scale-95"
              >
                + Create Lesson
              </button>
              <button
                onClick={() => setCurrentScreen('lessonPicker')}
                className="flex-1 py-2.5 bg-white/10 border border-white/20 text-white/70 text-sm font-medium rounded-xl transition-all duration-300 active:scale-95 hover:bg-white/20"
              >
                View All
              </button>
            </div>
            <button
              onClick={() => setCurrentScreen('lessonLibrary')}
              className="w-full mt-2 py-2.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 text-amber-300 text-sm font-bold rounded-xl transition-all duration-300 active:scale-95 hover:bg-amber-500/30"
            >
              📚 Browse Lesson Library ({lessonLibrary.length})
            </button>
          </div>

          {/* Class overview chart */}
          {(() => {
            const studentsWithRatings = teacherStudents
              .map(s => {
                const allR = s.r ? Object.values(s.r).flat().filter(v => v > 0) : [];
                if (allR.length === 0) return null;
                return { value: allR.reduce((a, b) => a + b, 0) / allR.length, name: s.n, ts: s.ts || 0 };
              })
              .filter(Boolean)
              .sort((a, b) => a.ts - b.ts);

            if (studentsWithRatings.length < 2) return null;

            const xLabels = studentsWithRatings.map(s => {
              const name = s.name || '?';
              return studentsWithRatings.length > 6 ? name.split(' ').map(w => w[0]).join('') : name.split(' ')[0];
            });

            return (
              <div className="glass rounded-2xl p-5 mb-6 animate-fade-in">
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Class Overview</p>
                {renderGrowthChart(studentsWithRatings, {
                  gradientId: 'teacherClass',
                  colorFrom: '#f59e0b',
                  colorTo: '#f97316',
                  xLabels,
                  showAvgLine: true
                })}
              </div>
            );
          })()}

          {/* Student roster */}
          {teacherStudents.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center animate-fade-in-delay">
              <div className="text-5xl mb-4">{'\u{1F4CB}'}</div>
              <p className="text-white/80 font-medium">No students scanned yet</p>
              <p className="text-white/50 text-sm mt-2">Scan a student's "Share My Progress" QR code to get started.</p>
              <div className="h-px bg-white/10 my-5" />
              <button
                onClick={loadDemoStudents}
                className="w-full py-3 bg-white/10 border border-white/20 text-white/70 font-medium rounded-xl transition-all duration-300 active:scale-95 hover:bg-white/20 hover:text-white text-sm"
              >
                {'\u{1F3AD}'} Load Sample Class (Demo)
              </button>
            </div>
          ) : (
            <div className="space-y-3 animate-fade-in-delay">
              {teacherStudents.map((student, idx) => {
                const totalPieces = student.pc ? Object.values(student.pc).reduce((a, b) => a + b, 0) : 0;
                const missionCount = (student.m || []).length;
                // Overall average rating
                const allRatings = student.r ? Object.values(student.r).flat().filter(v => v > 0) : [];
                const overallAvg = allRatings.length > 0
                  ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(1)
                  : '—';

                return (
                  <button
                    key={idx}
                    onClick={() => { setTeacherSelectedStudent(student); setCurrentScreen('teacherStudentDetail'); }}
                    className="w-full glass rounded-2xl p-4 flex items-center gap-4 text-left transition-all duration-300 active:scale-95 hover:bg-white/10"
                  >
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-b from-cyan-800/50 to-blue-900/50 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {student.a && student.a.t === 'c' ? (
                        <DiverAvatar avatarData={{ type: 'custom', base: student.a.b, hair: student.a.h, accessory: student.a.x }} size={48} />
                      ) : (
                        <span className="text-2xl">{'\u{1F9D1}'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold truncate">{student.n}</p>
                      <p className="text-white/60 text-xs">
                        {missionCount} mission{missionCount !== 1 ? 's' : ''} · {totalPieces} piece{totalPieces !== 1 ? 's' : ''} · Avg: {overallAvg}
                      </p>
                    </div>
                    <ArrowLeft size={16} className="text-white/40 rotate-180 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Clear all */}
          {teacherStudents.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm('Remove all scanned student data? This cannot be undone.')) {
                  setTeacherStudents([]);
                }
              }}
              className="mt-6 w-full py-3 bg-red-500/20 border border-red-400/30 text-red-300 font-semibold rounded-xl transition-all duration-300 active:scale-95 hover:bg-red-500/30"
            >
              Clear All Students
            </button>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
        {renderLiveScanner()}
        {renderScanFeedbackToast()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // TEACHER STUDENT DETAIL
  // ═══════════════════════════════════════════════════════════════
  if (currentScreen === 'teacherStudentDetail' && teacherSelectedStudent) {
    const student = teacherSelectedStudent;
    const totalPieces = student.pc ? Object.values(student.pc).reduce((a, b) => a + b, 0) : 0;
    const missionCount = (student.m || []).length;
    const scanDate = student.ts ? new Date(student.ts * 1000).toLocaleDateString() : 'Unknown';

    // Decode sensory labels
    const sensoryLabels = {
      mv: { L: 'Lots of movement', M: 'Some movement', N: 'Not much movement' },
      sn: { L: 'Loud is okay', M: 'Medium sounds', Q: 'Quiet please' },
      tc: { S: 'Squishy/soft', E: 'Everything is fine', A: 'Avoids some textures' },
    };

    const rubricLabels = ['Planning', 'Technique', 'Creativity', 'Corner Post', 'Principle', 'Self-Critique'];

    return (
      <div className="mesh-gradient flex flex-col items-center justify-start p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Back button */}
          <button
            onClick={() => { setTeacherSelectedStudent(null); setCurrentScreen('teacherDashboard'); setObservingCornerPost(null); }}
            className="flex items-center gap-2 text-white/80 mb-6 transition-all duration-300 hover:text-white active:scale-95"
          >
            <ArrowLeft size={20} /> Back to Dashboard
          </button>

          {/* Student header */}
          <div className="glass rounded-3xl p-6 mb-6 animate-scale-in">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-b from-cyan-800/50 to-blue-900/50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {student.a && student.a.t === 'c' ? (
                  <DiverAvatar avatarData={{ type: 'custom', base: student.a.b, hair: student.a.h, accessory: student.a.x }} size={64} />
                ) : (
                  <span className="text-3xl">{'\u{1F9D1}'}</span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black text-white">{student.n}</h2>
                <p className="text-white/50 text-xs">Scanned {scanDate}</p>
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-white">{missionCount}</p>
                <p className="text-white/60 text-xs">Missions</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-white">{totalPieces}</p>
                <p className="text-white/60 text-xs">Portfolio Pieces</p>
              </div>
            </div>
          </div>

          {/* Corner Post x Skill Matrix */}
          <h3 className="text-white font-bold text-lg mb-3 animate-fade-in-delay">Corner Post Skills</h3>
          <div className="space-y-3 mb-6 animate-fade-in-delay">
            {Object.entries(CORNER_POST_SKILLS).map(([shortId, info]) => {
              const ratings = (student.r && student.r[shortId]) || null;
              const count = (student.pc && student.pc[shortId]) || 0;
              const tObs = teacherObservations[student.n]?.[shortId];
              const barColor = info.color === 'green' ? '#22c55e' : info.color === 'blue' ? '#3b82f6' : info.color === 'red' ? '#ef4444' : info.color === 'purple' ? '#a855f7' : info.color === 'yellow' ? '#eab308' : '#06b6d4';

              return (
                <div key={shortId} className="glass rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{info.icon}</span>
                      <div>
                        <p className="text-white font-bold text-sm">{info.name}</p>
                        <p className="text-white/50 text-xs">{info.skill}</p>
                      </div>
                    </div>
                    <span className="text-white/40 text-xs">{count} piece{count !== 1 ? 's' : ''}</span>
                  </div>

                  {ratings ? (
                    <div className="space-y-1.5">
                      {ratings.map((val, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-white/60 text-[10px] w-16 text-right flex-shrink-0">{rubricLabels[i] || `Cat ${i+1}`}</span>
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${(val / 5) * 100}%`, backgroundColor: barColor }}
                            />
                          </div>
                          <span className="text-white/80 text-[10px] w-6 flex-shrink-0">{val > 0 ? val : '—'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/30 text-xs italic">No portfolio entries yet</p>
                  )}

                  {/* Teacher observation bars */}
                  {tObs && tObs.ratings && (
                    <div className="mt-3 pt-2 border-t border-white/10">
                      <p className="text-teal-300/70 text-[10px] font-medium uppercase tracking-wider mb-1.5">Teacher Observation</p>
                      <div className="space-y-1.5">
                        {tObs.ratings.map((val, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-teal-300/60 text-[10px] w-16 text-right flex-shrink-0">{rubricLabels[i] || `Cat ${i+1}`}</span>
                            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(val / 5) * 100}%`, backgroundColor: '#14b8a6' }} />
                            </div>
                            <span className="text-teal-300/80 text-[10px] w-6 flex-shrink-0">{val > 0 ? val : '—'}</span>
                          </div>
                        ))}
                      </div>
                      {tObs.note && <p className="text-teal-200/50 text-xs italic mt-2">"{tObs.note}"</p>}
                    </div>
                  )}

                  {/* Observe button / inline form */}
                  {observingCornerPost === shortId ? (
                    <div className="mt-3 pt-3 border-t border-white/10 animate-slide-up">
                      <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">Your Observation</p>
                      <div className="space-y-2 mb-3">
                        {rubricCategories.map((cat, i) => (
                          <div key={cat.key} className="flex items-center gap-2">
                            <span className="text-sm">{cat.emoji}</span>
                            <span className="text-white/60 text-[10px] flex-1 min-w-0 truncate">{cat.label}</span>
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map(star => (
                                <button
                                  key={star}
                                  onClick={() => setObservationRatings(prev => { const next = [...prev]; next[i] = star; return next; })}
                                  className={`w-7 h-7 text-base transition-all duration-200 rounded-full ${
                                    star <= observationRatings[i] ? 'text-teal-400 scale-110' : 'text-white/20 hover:text-teal-300'
                                  }`}
                                >
                                  {star <= observationRatings[i] ? '\u{2B50}' : '\u{2606}'}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <textarea
                        value={observationNote}
                        onChange={e => setObservationNote(e.target.value.slice(0, 300))}
                        className="w-full p-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm h-16 resize-none focus:border-teal-400 focus:outline-none mb-2"
                        placeholder="Optional note..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setTeacherObservations(prev => ({
                              ...prev,
                              [student.n]: {
                                ...(prev[student.n] || {}),
                                [shortId]: { ratings: [...observationRatings], note: observationNote, ts: Date.now() },
                              },
                            }));
                            setObservingCornerPost(null);
                            setObservationRatings([0, 0, 0, 0, 0, 0]);
                            setObservationNote('');
                          }}
                          className="flex-1 py-2 bg-teal-500 text-white text-sm font-bold rounded-xl transition-all active:scale-95"
                        >
                          Save Observation
                        </button>
                        <button
                          onClick={() => { setObservingCornerPost(null); setObservationRatings([0, 0, 0, 0, 0, 0]); setObservationNote(''); }}
                          className="px-4 py-2 bg-white/10 text-white/60 text-sm rounded-xl transition-all active:scale-95"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        const existing = teacherObservations[student.n]?.[shortId];
                        setObservationRatings(existing?.ratings ? [...existing.ratings] : [0, 0, 0, 0, 0, 0]);
                        setObservationNote(existing?.note || '');
                        setObservingCornerPost(shortId);
                      }}
                      className="mt-2 w-full py-1.5 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-medium rounded-xl transition-all active:scale-95 hover:bg-teal-500/30"
                    >
                      {tObs ? 'Edit Observation' : 'Observe'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sensory Profile */}
          {student.s && Object.keys(student.s).length > 0 && (
            <div className="mb-6 animate-fade-in-delay-2">
              <h3 className="text-white font-bold text-lg mb-3">Sensory Profile</h3>
              <div className="glass rounded-2xl p-4 space-y-2">
                {Object.entries(student.s).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-white/60 text-sm">
                      {key === 'mv' ? 'Movement' : key === 'sn' ? 'Sound' : 'Touch'}
                    </span>
                    <span className="text-white text-sm font-medium">
                      {(sensoryLabels[key] && sensoryLabels[key][val]) || val}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed Missions */}
          {student.m && student.m.length > 0 && (
            <div className="animate-fade-in-delay-2">
              <h3 className="text-white font-bold text-lg mb-3">Completed Missions</h3>
              <div className="glass rounded-2xl p-4">
                <div className="flex flex-wrap gap-2">
                  {student.m.map((missionId, i) => {
                    // Find mission name from corner posts
                    let missionName = missionId;
                    for (const post of cornerPosts) {
                      const found = post.missions.find(m => m.id === missionId);
                      if (found) { missionName = found.name; break; }
                    }
                    return (
                      <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-white/80 text-xs font-medium">
                        {missionName}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        {renderActionButtons()}
        {showSettings && renderSettings()}
        {showHelp && renderHelpPanel()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // FALLBACK
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="mesh-gradient flex flex-col items-center justify-center p-6">
      <div className="glass rounded-3xl p-8 text-center max-w-sm w-full animate-scale-in">
        <div className="text-5xl mb-4">😅</div>
        <h1 className="text-2xl font-bold text-white mb-2">Oops! Something went wrong</h1>
        <p className="text-white/60 text-sm mb-6">Let's get you back on track.</p>
        <button
          onClick={() => setCurrentScreen('home')}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl transition-all duration-300 active:scale-95"
        >
          Return Home
        </button>
      </div>
      {renderActionButtons()}
      {showSettings && renderSettings()}
      {showHelp && renderHelpPanel()}
    </div>
  );
};

export default App;
